import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { brand } from '@/lib/brand';

/**
 * English and Arabic, per section 7 of the specification.
 *
 * Resources are inline while the surface is small. They move to separate
 * per-namespace files once there is enough of the UI to warrant lazy loading -
 * splitting a hundred keys across files buys nothing but indirection.
 */
const en = {
  // Named after the domain the page was opened at: Aider ERP, Inspire ERP.
  app: { name: brand.appName },
  signIn: {
    title: 'Sign in',
    subtitle: 'Sign in to continue',
    company: 'Company code',
    companyHint: 'Leave blank if this installation has a single company',
    userName: 'User name or email',
    password: 'Password',
    submit: 'Sign in',
    working: 'Signing in…',
    passwordChanged: 'Password changed. Sign in again.',
  },
  nav: {
    productHistory: 'Product history',
    stockRegister: 'Stock register',
    stockAging: 'Stock aging',
    stockSummary: 'Stock summary',
    salesReport: 'Sales report',
    purchaseReport: 'Purchase report',
    profitReport: 'Profit report',
    receivables: 'Receivables',
    payables: 'Payables',
    dashboard: 'Dashboard',
    accounting: 'Accounting',
    voucherEntry: 'Voucher entry',
    ledgers: 'Chart of accounts',
    sales: 'Sales invoices',
    customers: 'Customers',
    purchase: 'Purchases',
    purchaseReturns: 'Purchase returns',
    purchaseOrders: 'Purchase orders',
    salesOrders: 'Sales orders',
    salesQuotations: 'Sales quotations',
    suppliers: 'Suppliers',
    payments: 'Payments',
    receipts: 'Receipts',
    settings: 'Settings',
    openingStock: 'Opening stock',
    taxReturns: 'VAT / GST returns',
    products: 'Products',
    stockOperations: 'Stock operations',
    stockTransfers: 'Stock transfers',
    stockAdjustment: 'Stock adjustment',
    damagedStock: 'Damaged stock',
    deliveryNotes: 'Delivery notes',
    boms: 'Bills of materials',
    production: 'Production',
    service: 'Service',
    jobCards: 'Job cards',
    rmas: 'Returns (RMA)',
    auditLog: 'Audit log',
    approvals: 'Approvals',
    receiptNotes: 'Receipt notes',
    stockValuation: 'Stock valuation',
    stockLedger: 'Stock ledger',
    itemMovement: 'Item movement',
    batchStock: 'Batch-wise stock',
    batchProfit: 'Batch-wise profit',
    expiryReport: 'Expiry report',
    serialsRegister: 'Serial numbers',
    units: 'Units of measure',
    categories: 'Categories',
    brands: 'Brands',
    warehouses: 'Warehouses',
    trialBalance: 'Trial balance',
    accountGroupSummary: 'Group summary',
    profitAndLoss: 'Profit and loss',
    balanceSheet: 'Balance sheet',
    dayBook: 'Day book',
    voucherReport: 'Voucher report',
    transactionSummary: 'Transaction summary',
    menuUnavailable: 'Menu unavailable.',
    menuAdministration: 'Menu settings',
    cashBook: 'Cash book',
    bankBook: 'Bank book',
    cashFlow: 'Cash flow',
    postDatedCheques: 'Post-dated cheques',
    chequeCalendar: 'Cheque calendar',
    chequeRegister: 'Cheque register',
    signOut: 'Sign out',
    menu: 'Menu',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    collapseSidebar: 'Collapse',
    expandSidebar: 'Expand',
    skipToContent: 'Skip to content',
  },
  invoice: {
    customerDetails: 'Customer details',
    total: 'Total',
    roundOff: 'Round off',
    grandTotal: 'Grand total',
    hold: 'Hold',
    menuHolds: 'Menu — the bill goes on hold',
    supplierDetails: 'Supplier details',
    orderDetails: 'Order details',
    quotationDetails: 'Quotation details',
    items: 'Items',
    itemCount_one: '{{count}} item',
    itemCount_other: '{{count}} items',
    addItem: 'Add item',
    itemsTotal: 'Items total',
    itemsQty: '{{count}} · Qty {{qty}}',
    linesTotal: 'Total',
    ratesAndCharges: 'Rates & charges',
    creditTerms: '{{limit}} · {{days}} days',
    noCustomer: 'No customer chosen yet (F2).',
    noSupplier: 'No supplier chosen yet.',
    chooseCustomer: 'Choose customer',
    chooseSupplier: 'Choose supplier',
    emptyLinesTitle: 'Scan a barcode or choose a product to start.',
    emptyLinesScan: 'Scan',
    emptyLinesAdd: 'Add item',
    emptyLinesEnter: 'Next box',
    lineNumber: 'Line',
    discardTitle: 'Discard this invoice?',
    discardBody: 'What you have entered will be lost.',
    keepEditing: 'Keep editing',
    discard: 'Discard',
    scanLabel: 'Scan barcode',
    removeItem: 'Remove item',
    narration: 'Narration / remarks',
    bill: 'Bill',
    billTotal: 'Bill total',
    returnTotal: 'Return total',
    orderTotal: 'Order total',
    quotationTotal: 'Quotation total',
    paymentDetails: 'Payment details',
    summary: 'Summary',
    customerAccount: 'Customer account',
    supplierAccount: 'Supplier account',
    newBalance: 'New balance',
    newSales: 'Create a new sales invoice',
    newSalesReturn: 'Take back goods a customer returned',
    newPurchase: 'Record a supplier’s invoice',
    newPurchaseReturn: 'Send goods back to a supplier',
    newPurchaseOrder: 'Order goods from a supplier',
    newSalesOrder: 'Take an order from a customer',
    newQuotation: 'Quote prices to a customer',
    editing: 'Changing {{number}}',
  },
  palette: {
    title: 'Go to a screen',
    open: 'Search the menu',
    shortcut: 'Ctrl K',
    searchModule: 'Search module…',
    placeholder: 'Type a screen name…',
    nothing: 'No matches.',
    more: '{{count}} more',
    hint: '↑↓ move · ↵ open · Esc close',
  },
  reports: {
    from: 'From',
    to: 'To',
    asAt: 'As at',
    asAtDate: 'As at {{date}}',
    dr: 'Dr',
    cr: 'Cr',
    filters: 'Filters',
    run: 'Run',
    print: 'Print',
    running: 'Running…',
    ledger: 'Ledger',
    group: 'Group',
    openingDebit: 'Opening Dr',
    openingCredit: 'Opening Cr',
    periodDebit: 'Period Dr',
    periodCredit: 'Period Cr',
    closingDebit: 'Closing Dr',
    closingCredit: 'Closing Cr',
    totals: 'Totals',
    balanced: 'Debits equal credits',
    notBalanced: 'OUT OF BALANCE',
    noData: 'No postings.',
    income: 'Income',
    expenses: 'Expenses',
    totalIncome: 'Total income',
    totalExpenses: 'Total expenses',
    netProfit: 'Net profit',
    netLoss: 'Net loss',
    assets: 'Assets',
    liabilities: 'Liabilities',
    equity: 'Equity',
    retainedEarnings: 'Retained earnings',
    totalAssets: 'Total assets',
    totalLiabilities: 'Total liabilities',
    totalEquity: 'Total equity',
    totalLiabilitiesAndEquity: 'Liabilities + equity',
    date: 'Date',
    voucherNo: 'Voucher no.',
    particulars: 'Particulars',
    debit: 'Debit',
    credit: 'Credit',
    voucherType: 'Voucher type',
    allTypes: 'All types',
    status: 'Status',
    allStatuses: 'All statuses',
    reference: 'Reference',
    amount: 'Amount',
    count: 'Count',
    byType: 'By voucher type',
    byMonth: 'By month',
    cashIn: 'Cash in',
    cashOut: 'Cash out',
    netChange: 'Net change in cash',
    noMovement: 'No movement',
    cashReconciled: 'Cash reconciled',
    cashNotReconciled: 'DOES NOT RECONCILE',
    voucherCount_one: '{{count}} voucher',
    voucherCount_other: '{{count}} vouchers',
    receipts: 'Receipts',
    payments: 'Payments',
    balance: 'Balance',
    openingBalance: 'Opening balance',
    closingBalance: 'Closing balance',
    allAccounts: 'All accounts',
    includeLedgers: 'Show ledgers',
    includeZeroBalances: 'Include zero balances',
    hierarchicalTree: 'Hierarchical Tree',
    hierarchicalView: 'Hierarchical Tree',
    facingColumns: 'Facing Columns',
    type: 'Type',
    classicView: 'Classic View',
    flatList: 'Flat Grid',
    accountsView: 'Account Cards',
    hierarchyLevels: 'Levels',
    heads: 'Heads',
    groups: 'Groups',
    ledgers: 'Ledgers',
    transactions: 'Details',
    accounts: 'Accounts',
    searchAccounts: 'Search accounts…',
    accountHierarchy: 'Account tree',
    transactionDetails: 'Postings',
    entries: 'entries',
    noTransactionsPeriod: 'No postings in this period.',
    subtotal: 'Subtotal',
    totalHead: 'Total',
    grandTotal: 'Grand Total',
    balancedBooks: 'Books balanced',
    chronologicalView: 'Chronological Register',
  },
  voucherTypes: {
    CashReceipt: 'Cash receipt',
    BankReceipt: 'Bank receipt',
    CashPayment: 'Cash payment',
    BankPayment: 'Bank payment',
    Journal: 'Journal',
    Contra: 'Contra',
    OpeningBalance: 'Opening balance',
  },
  vouchers: {
    noPendingBills: 'No pending bills.',
    pendingBills: '{{count}} bills · {{total}} due',
    billNumber: 'Bill',
    billDate: 'Date',
    billDue: 'Due',
    billOutstanding: 'Outstanding',
    billAllocated: 'Allocate',
    settleBill: 'Settle bill {{number}}',
    entryTitle: 'Voucher entry',
    entryHint:
      'Every debit has a corresponding credit. The voucher number is issued by the branch’s numbering series when it posts.',
    type: 'Voucher type',
    date: 'Date',
    party: 'Customer / supplier',
    partyHint: 'Fills the line this voucher is against.',
    noParty: 'None',
    reference: 'Ref / Inv no.',
    narration: 'Narration',
    lineNarration: 'Line narration',
    ledger: 'Ledger',
    side: 'Dr / Cr',
    debitAmount: 'Debit amount',
    creditAmount: 'Credit amount',
    removeLine: 'Remove line',
    addRow: '+ Add row',
    total: 'Total',
    balanced: 'Balanced',
    nothingEntered: 'Nothing entered yet',
    differenceDebit: 'Diff {{amount}} (Dr)',
    differenceCredit: 'Diff {{amount}} (Cr)',
    post: 'Post voucher',
    posting: 'Posting…',
    postedNotice: 'Posted {{number}} for {{total}}.',
    postedAndListed: '{{number}} posted.',
    enterAnother: 'Enter another',
    paymentTitle: 'Payment',
    paymentHint:
      'Money going out: a supplier paid, an expense settled, an advance given.',
    receiptTitle: 'Receipt',
    receiptHint: 'Money coming in: a customer paying, a refund, an advance received.',
    through: 'Through',
    throughCash: 'Cash',
    throughBank: 'Bank',
    amount: 'Amount',
    paidTo: 'Paid to',
    receivedFrom: 'Received from',
    cashAccount: 'Cash account',
    bankAccount: 'Bank account',
    paymentMode: 'Payment mode',
    paymentModeNone: 'Not stated',
    postPayment: 'Post payment',
    postReceipt: 'Post receipt',
    paymentEffect:
      'This debits each ledger above and credits the cash or bank account: the firm owes them less, or has spent it, and holds less money.',
    receiptEffect:
      'This debits the cash or bank account and credits each ledger above: the firm holds more money and they owe it less.',
    dateRequired: 'Date required.',
    partyRequired: 'Party required.',
    cashRequired: 'Name the cash account.',
    bankRequired: 'Name the bank account.',
    amountRequired: 'An amount is required.',
    amountInvalid: 'Amount must be above 0.',
    transactionNo: 'Transaction No.',
    referenceNo: 'Reference No.',
    referenceNoHint: 'Defaults to Transaction No if blank',
    debitAccount: 'Debit Account',
    debitAccountHint: 'The supplier, party, or expense account being debited',
    creditAccount: 'Credit Account',
    creditAccountHint: 'The customer, party, or income account being credited',
    multiplePayments: 'Payment Entries',
    multiplePaymentsHint: 'Add one or more payment entries for this transaction',
    addPaymentEntry: 'Add Payment Entry',
    paymentType: 'Payment Type',
    account: 'Account',
    chequeOrRef: 'Cheque / Ref No',
    totalPaymentAmount: 'Total Amount',
    salesman: 'Salesman',
    primarySalesman: 'Primary Salesman',
    cash: 'Cash',
    credit: 'Credit',
    bank: 'Bank',
    card: 'Card',
    cheque: 'Cheque',
    autoGenerated: 'Auto-generated',
    missingAccount: 'Select an account.',
    actions: 'Actions',
    edit: 'Edit',
    editTitle: 'Edit {{number}}',
    editPostedHint: 'Posted: saving re-posts it under the same number.',
    editReason: 'Reason',
    saveChanges: 'Save changes',
    journalTitle: 'Journal',
    postJournal: 'Post journal',
    updatedNotice: '{{number}} updated.',
    cancelVoucher: 'Cancel voucher',
    cancelTitle: 'Cancel {{number}}',
    cancelHint: 'Cancels it and reverses its entries.',
    cancelReason: 'Reason',
    cancelledNotice: '{{number}} cancelled.',
    journalDetails: 'Journal details',
    journalDetailsHint: 'One line per ledger. Enter each amount under Debit or Credit.',
    addLedger: 'Add ledger',
    ledgerRequired: 'Choose a ledger on each line.',
    paidFrom: 'Paid from',
    receivedInto: 'Received into',
    groupCash: 'Cash',
    groupBank: 'Bank',
    moneyAccountRequired: 'Choose cash or bank.',
    chequeDetails: 'Cheque details',
    chequeNumber: 'Cheque number',
    chequeAccountNumber: 'Account number',
    chequeDate: 'Cheque date',
    inFavourOf: 'In favour of',
    chequeNumberRequired: 'Cheque no. required.',
    chequeNeedsBank: 'Cheques need a bank account.',
    cardDetails: 'Card details',
    cardType: 'Card type',
    cardLastDigits: 'Last four digits',
    cardApproval: 'Approval code',
    paymentDetails: 'Payment details',
    paymentDetailsHint: 'Each ledger this payment covers, with what it pays.',
    receiptDetails: 'Receipt details',
    receiptDetailsHint: 'Each ledger this receipt covers, with what it receives.',
  },
  voucherStatus: {
    Draft: 'Draft',
    Posted: 'Posted',
    Cancelled: 'Cancelled',
  },
  /*
    The product's own figures, shown beside a document line.

    One set of captions rather than one per screen: a purchase, an order and an
    invoice all show the same four things off the same master, and three copies would
    be three chances for them to disagree.
  */
  lineDetails: {
    code: 'Code',
    unit: 'Unit',
    retail: 'Retail',
    wholesale: 'Wholesale',
    mrp: 'MRP',
    fromMaster: 'From the product master. Change it on the product.',
  },
  serials: {
    title: 'Serials — {{product}}',
    summary: 'Serials: {{chosen}}/{{most}}',
    choose: 'Choose serials',
    change: 'Change',
    hintOut:
      'Tick the units going out, or scan a number to tick it. Optional: name all, some or none.',
    hintIn:
      'Scan or type each number and press Enter. Optional: name all, some or none of the units arriving.',
    scanPlaceholder: 'Scan serial / IMEI',
    add: 'Add',
    remove: 'Remove {{number}}',
    alreadyChosen: '{{number}} is already chosen.',
    notOnShelf: '{{number}} not in stock here.',
    notSold: '{{number}} was not sold.',
    onShelf: 'On the shelf',
    soldUnits: 'Units sold',
    noneOnShelf: 'No serials in stock.',
    noneSold: 'No serials sold.',
    filter: 'Filter',
    warrantyTo: 'warranty to {{date}}',
    pickedOf: '{{chosen}}/{{quantity}} chosen',
    done: 'Done',
    scanFound: '{{number}} — {{product}}',
  },
  numbering: {
    title: 'Document numbering',
    hint: 'How each document is numbered, as the server issues the numbers. A change here is what the next document carries; numbers already issued never change, and the counter can move forward but not back.',
    document: 'Document',
    prefix: 'Prefix',
    separator: 'Separator',
    year: 'Year',
    suffix: 'Suffix',
    nextNumber: 'Next no.',
    preview: 'Next document',
    default: 'default',
    issuedOnSave: 'Issued on save',
    types: {
      'sales.invoice': 'Sales invoice',
      'sales.return': 'Sales return',
      'sales.order': 'Sales order',
      'sales.quotation': 'Sales quotation',
      'purchase.invoice': 'Purchase',
      'purchase.return': 'Purchase return',
      'purchase.order': 'Purchase order',
      'accounting.cash-receipt': 'Cash receipt',
      'accounting.bank-receipt': 'Bank receipt',
      'accounting.cash-payment': 'Cash payment',
      'accounting.bank-payment': 'Bank payment',
      'accounting.journal': 'Journal voucher',
    },
  },
  paymentModes: {
    cash: 'Cash',
    cheque: 'Cheque',
    bankTransfer: 'Bank transfer',
    card: 'Card',
    online: 'Online',
    credit: 'Credit',
  },
  cashFlow: {
    Operating: 'Operating activities',
    Investing: 'Investing activities',
    Financing: 'Financing activities',
  },
  menuAdmin: {
    entry: 'Entry',
    route: 'Route',
    permission: 'Requires',
    actions: 'Actions',
    heading: 'heading',
    everyone: 'everyone',
    system: 'system',
    systemCannotDelete: 'System entry: hide instead.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    show: 'Show',
    hide: 'Hide',
    rename: 'Rename',
    renamePrompt: 'New label',
    delete: 'Delete',
    code: 'Code',
    label: 'Label',
    routeOptional: 'Route (optional)',
    add: 'Add entry',
  },
  cheques: {
    direction: {
      label: 'Direction',
      Received: 'Received',
      Issued: 'Issued',
    },
    status: {
      label: 'Status',
      Pending: 'In hand',
      Deposited: 'Banked',
      Cleared: 'Cleared',
      Bounced: 'Bounced',
      Stopped: 'Stopped',
      Cancelled: 'Cancelled',
    },
    allDirections: 'Both',
    allStatuses: 'All statuses',
    chequeNo: 'Cheque no.',
    party: 'Party',
    dueDate: 'Due date',
    instrumentDate: 'Cheque date',
    daysToRun: 'Days',
    dueNow: 'Due',
    bank: 'Bank',
    amount: 'Amount',
    recordedOn: 'Recorded',
    receivable: 'Receivable',
    payable: 'Payable',
    net: 'Net',
    totalReceivable: 'Total receivable',
    totalPayable: 'Total payable',
    totalReceived: 'Total received',
    totalIssued: 'Total issued',
    countByStatus: 'By status',
    multiCurrency: 'Totals mix currencies.',
    noPostDated: 'No post-dated cheques.',
    noDue: 'No cheques due.',
    noCheques: 'No cheques.',
  },
  ledgers: {
    name: 'Name',
    kind: 'Kind',
    groupCode: 'Group code',
    nature: 'Nature',
    currency: 'Currency',
    billWise: 'Bill-wise',
  },
  ledgerKinds: {
    General: 'General',
    Cash: 'Cash',
    Bank: 'Bank',
    Customer: 'Customer',
    Supplier: 'Supplier',
    Employee: 'Employee',
    Tax: 'Tax head',
    AdditionalCharge: 'Charge',
  },
  accountNatures: {
    Asset: 'Asset',
    Liability: 'Liability',
    Equity: 'Equity',
    Income: 'Income',
    Expense: 'Expense',
  },
  attention: {
    title: 'Waiting for you',
    hint: 'Counted from the same lists these open. A row appears only when it has something on it.',
    purchaseDrafts_one: '{{count}} purchase draft',
    purchaseDrafts_other: '{{count}} purchase drafts',
    purchaseOrders_one: '{{count}} open purchase order',
    purchaseOrders_other: '{{count}} open purchase orders',
    salesOrders_one: '{{count}} open sales order',
    salesOrders_other: '{{count}} open sales orders',
    belowReorder_one: '{{count}} product to reorder',
    belowReorder_other: '{{count}} products to reorder',
    chequesDue_one: '{{count}} post-dated cheque',
    chequesDue_other: '{{count}} post-dated cheques',
  },
  dashboard: {
    none: 'No dashboard assigned.',
    customize: 'Customize',
    role: 'Role',
    shown: 'On the dashboard',
    available: 'Add widgets',
    noWidgets: 'No widgets. Add some.',
    add: 'Add',
    remove: 'Remove',
    moveUp: 'Move up',
    moveDown: 'Move down',
    width: 'Width',
    noneWidgets: 'No widgets yet.',
    withheld: 'Not available',
    noData: 'Nothing recorded yet.',
    itemCount_one: '{{count}} item',
    itemCount_other: '{{count}} items',
  },
  charts: {
    heading: 'Trends',
    showTable: 'Table',
    showChart: 'Chart',
    loadFailed: 'The charts could not be loaded.',
    dailySales: 'Sales, last 30 days',
    withTax: 'With tax, net of returns',
    bills_one: '{{n}} bill',
    bills_other: '{{n}} bills',
    billsHeading: 'Bills',
    date: 'Date',
    month: 'Month',
    sales: 'Sales',
    purchases: 'Purchases',
    salesAndPurchases: 'Sales and purchases',
    lastYearBeforeTax: 'Last 12 months, before tax',
    lastYear: 'Last 12 months',
    twelveMonths: 'over 12 months',
    profit: 'Profit',
    loss: 'Loss',
    paymentModes: 'How customers paid',
    thisMonth: 'This month',
    mode: 'Mode',
    amount: 'Amount',
    share: 'Share',
    topProducts: 'Top products',
    topCategories: 'Top categories',
    thisMonthWithTax: 'This month, with tax',
    product: 'Product',
    category: 'Category',
    code: 'Code',
    quantity: 'Qty {{quantity}}',
    quantityHeading: 'Qty',
    receivables: 'What customers owe',
    byDaysOverdue: 'By days overdue',
    daysOverdue: 'Days overdue',
    notDue: 'Not yet due',
    dayRange: '{{from}}–{{to}} days',
    overDays: 'Over {{days}} days',
    overdue: '{{amount}} overdue',
    noSales: 'No sales in these 30 days.',
    noSalesMonth: 'No sales this month yet.',
    nothingYet: 'Nothing recorded yet.',
    nothingOwed: 'Nothing owed.',
  },
  masters: {
    codeAuto: 'Automatic',
    code: 'Code',
    name: 'Name',
    nameArabic: 'Name (Arabic)',
    status: 'Status',
    active: 'Active',
    withdrawn: 'Withdrawn',
    withdraw: 'Withdraw',
    restore: 'Restore',
    add: 'Add',
    includeWithdrawn: 'Include withdrawn',
    // The record a master creates, named on the button that opens its form. A
    // shared "Add" would leave every one of these screens saying the same word
    // about a different thing.
    newUnit: 'New unit',
    newCategory: 'New category',
    editCategory: 'Edit category',
    editBrand: 'Edit brand',
    editWarehouse: 'Edit warehouse',
    newBrand: 'New brand',
    newWarehouse: 'New warehouse',
    newCustomer: 'New customer',
    newSupplier: 'New supplier',
  },
  units: {
    symbol: 'Symbol',
    base: 'Base unit',
    isBase: '— base —',
    newGroup: '— new group —',
    factor: 'Factor',
    decimals: 'Decimals',
    edit: 'Edit unit',
    codeFixed: 'Documents are already stated in this code.',
    factorFixed: 'Changing a factor would restate quantities recorded months ago.',
    codeRequired: 'A unit needs a code.',
    codeTooLong: 'Max 20 characters.',
    nameRequired: 'A unit needs a name.',
    nameTooLong: 'Max 100 characters.',
    factorRequired: 'Factor required.',
    factorInvalid: 'Factor must be above 0.',
    decimalsInvalid: 'Decimals 0–6.',
    factorLockedHint: 'Fixed once a product or document uses this unit.',
  },
  categories: {
    parent: 'Parent',
    topLevel: '— top level —',
    codeRequired: 'A code is required.',
    codeTooLong: 'Max 20 characters.',
    nameRequired: 'A name is required.',
    nameTooLong: 'Max 100 characters.',
  },
  products: {
    hideInPos: 'Hide in POS',
    stock: 'Stock',
    search: 'Search',
    searchHint: 'Matches the product code, the description and any barcode.',
    searchPlaceholder: 'Product code or name…',
    find: 'Search',
    clear: 'Clear',
    descriptionRequired: 'Description required.',
    descriptionTooLong: 'Max 200 characters.',
    categoryRequired: 'Category required.',
    unitRequired: 'Unit required.',
    codeTooLong: 'Max 30 characters.',
    allCategories: '— all categories —',
    new: 'New product',
    cancel: 'Cancel',
    choose: '— choose —',
    noneFound: 'No products match.',
    notFound: 'Product not found.',
    codeAuto: 'Blank = auto',
    save: 'Save',
    saved: 'Saved',
    backToList: 'Back',
    tabDescription: 'Description',
    tabDetails: 'Details',
    tabBarcodes: 'Barcodes',
    tabOpeningStock: 'Opening Stock',
    detail: 'Detail',
    identity: 'Identity',
    classification: 'Classification',
    taxRate: 'Tax rate',
    details: 'Details',
    description: 'Description',
    descriptionArabic: 'Description (Arabic)',
    shortDescription: 'Short description',
    itemName: 'Item name',
    manufacturer: 'Manufacturer',
    label: 'Label',
    size: 'Size',
    origin: 'Origin',
    rack: 'Rack',
    bin: 'Bin',
    category: 'Category',
    brand: 'Brand',
    noBrand: '— no brand —',
    itemType: 'Item type',
    itemStock: 'Stocked',
    itemService: 'Service',
    itemNonStock: 'Non-stock',
    rates: 'Rates',
    costingMethod: 'Costing method',
    costingLastPurchase: 'Last purchase rate',
    costingAverage: 'Average rate',
    cost: 'Cost',
    profitPercentage: 'Profit %',
    corPercentage: 'COR %',
    retailRate: 'Retail rate',
    wholesaleRate: 'Wholesale rate',
    otherRate: 'Other rate',
    retailAboveMrp: 'Retail rate above MRP.',
    maximumRetailPrice: 'MRP',
    mrpHint: 'Zero if none is printed. Retail may not exceed it.',
    deviceAttributes: 'Device attributes',
    device: 'Device',
    colour: 'Colour',
    battery: 'Battery',
    ram: 'RAM',
    storage: 'Storage',
    units: 'Units',
    stockUnit: 'Stock unit',
    purchaseUnit: 'Purchase unit',
    salesUnit: 'Sales unit',
    orderLevels: 'Order levels',
    minimumLevel: 'Minimum',
    reorderLevel: 'Reorder',
    maximumLevel: 'Maximum',
    maximumLevelHint: 'Zero for no ceiling.',
    movement: 'Movement',
    movementUnclassified: '— unclassified —',
    movementFast: 'Fast',
    movementNormal: 'Normal',
    movementSlow: 'Slow',
    movementDead: 'Dead stock',
    tracking: 'Tracking',
    tracksBatches: 'Held in batches',
    tracksSerialNumbers: 'Serial / IMEI',
    requiresSerialImei: 'Serial / IMEI',
    isPacking: 'Packing material',
    shelfLifeDays: 'Shelf life (days)',
    shelfLifeHint: 'Counted from the batch date.',
    batchShort: 'Batch',
    serialShort: 'Serial / IMEI',
    barcodes: 'Barcodes',
    barcode: 'Barcode',
    noBarcodes: 'No barcodes.',
    removeBarcode: 'Remove',
    discontinued: 'Discontinued',
    discontinue: 'Stop buying',
    resume: 'Resume buying',
  },
  stock: {
    new: 'New stock entry',
    newOf: 'New {{type}}',
    save: 'Save',
    post: 'Post',
    cancel: 'Cancel document',
    confirmCancel: 'Cancel and reverse',
    cancelReason: 'Cancel reason',
    cancelledFor: 'Cancelled: {{reason}}',
    postNow: 'Post on save',
    choose: '— choose —',
    noneFound: 'No documents.',
    notFound: 'Document not found.',
    number: 'Number',
    date: 'Date',
    type: 'Type',
    allTypes: '— all types —',
    warehouse: 'Warehouse',
    fromWarehouse: 'From warehouse',
    toWarehouse: 'To warehouse',
    allWarehouses: '— all warehouses —',
    reference: 'Reference',
    narration: 'Narration',
    reason: 'Reason',
    reasonPlaceholder: 'Reason',
    remarks: 'Remarks',
    status: 'Status',
    statusDraft: 'Draft',
    statusPosted: 'Posted',
    statusCancelled: 'Cancelled',
    lines: 'Lines',
    addLine: 'Add line',
    removeLine: 'Remove',
    product: 'Product',
    quantity: 'Quantity',
    counted: 'Counted',
    unit: 'Unit',
    inStockUnits: 'In stock units',
    rate: 'Rate',
    ratePlaceholder: 'Per stock unit',
    value: 'Value',
    unitCost: 'Unit cost',
    movements: 'Movements',
    noMovements: 'No movements.',
    noMovementsInRange: 'No movements.',
    balanceAfter: 'Balance after',
    averageAfter: 'Average after',
    typeOpening: 'Opening stock',
    typeReceipt: 'Material receipt',
    typeIssue: 'Material issue',
    typeTransfer: 'Stock transfer',
    typeAdjustment: 'Stock adjustment',
    typeDamaged: 'Damaged stock',
    typeVerification: 'Physical verification',
    typeDeliveryNote: 'Delivery note',
    typeReceiptNote: 'Receipt note',
    typeProduction: 'Production',
    typeSalesIssue: 'Sales issue',
    typeSalesReturn: 'Sales return receipt',
    typePurchaseReceipt: 'Purchase receipt',
    typePurchaseReturn: 'Purchase return issue',
    countHint:
      'Enter what was found on the shelf. Only the difference against the system figure is posted.',
    adjustmentHint:
      'A positive quantity is stock found, a negative one is stock lost. Leave the rate blank to value it at what the position already says it cost.',
    postedNotice: '{{number}} posted.',
    postedNotice_other: '{{number}} posted.',
    draftNotice: '{{number}} saved as draft.',
    cancelledNotice: 'Cancelled.',
    onHand: 'On hand',
    averageCost: 'Average cost',
    includeZero: 'Show empty',
    totalValue: 'Total: {{value}} {{currency}}',
    nothingOnHand: 'Nothing on hand.',
    nothingMoved: 'No movements.',
    chooseProduct: 'Choose a product.',
    opening: 'Opening',
    closing: 'Closing',
    in: 'In',
    out: 'Out',
    totalIn: 'Total in',
    totalOut: 'Total out',
    valueIn: 'Value in',
    valueOut: 'Value out',
    lastMoved: 'Last moved',
    batch: 'Batch',
    batchAuto: 'Auto',
    batchNone: '—',
    chooseBatch: '— choose a batch —',
    noBatchesInStock: 'None in stock',
    batchAvailable: '{{quantity}} at {{rate}}',
    expiresOn: 'Expires',
    manufacturedOn: 'Made',
    purchaseRate: 'Bought at',
    sellingRate: 'Sells at',
    soldQuantity: 'Sold',
    salesValue: 'Sales',
    costOfSales: 'Cost',
    profit: 'Profit',
    margin: 'Margin',
    allProducts: 'All products',
    noSales: 'No sales.',
    daysToExpiry: 'Days left',
    expired: 'Expired',
    expiringWithin: 'Expiring within',
    days: 'days',
    onlyExpired: '— already expired —',
    asOn: 'As on',
    nothingExpiring: 'Nothing expiring.',
    noBatchStock: 'No batch stock.',
    batchHint:
      'Leave the batch blank on a receipt and the next number is generated for the product. An issue or a transfer has to name a batch that is already in stock.',
    units: 'Units',
    unitsPlaceholder: 'One per line',
    unitsChoose: 'Choose units',
    unitsNone: 'No units in stock',
    unitsCounted: '{{count}} of {{needed}}',
    warrantyUntil: 'Warranty to',
    serialHint:
      'A serialised product needs one number for every unit the line moves. A receipt writes down the numbers on the cases; an issue or a transfer picks from the units already in stock.',
    barcodeScanner: 'Barcode Scanner',
    barcodeScanPlaceholder: 'Scan barcode / code',
    barcodeAdded: 'Added {{code}} (Qty: {{qty}})',
    barcodeNotFound: '"{{barcode}}" not found',
    scan: 'Scan',
    serialsRegister: 'Serials register',
    serialNumber: 'Serial Number',
    underWarranty: 'Under Warranty',
    warrantyExpired: 'Warranty Expired',
    statusInStock: 'In Stock',
    statusIssued: 'Issued',
    statusReturned: 'Returned',
    batchAutoPrompt: 'Blank = auto',
  },
  adjustment: {
    subtitle:
      'Correct the books to what is on the shelf: enter the count or upload it, review the differences, then confirm.',
    countTab: 'Count',
    historyTab: 'History',
    historyTitle: 'Adjustment history',
    reasonPlaceholder: 'Reason',
    search: 'Find a product',
    show: 'Show',
    showAll: 'Everything here',
    showCounted: 'Counted only',
    showDifferences: 'Differences only',
    addProduct: 'Add a product',
    notCountable: '{{code}} is not counted here: batch-tracked or not a stock item',
    downloadSheet: 'Download count sheet',
    uploadSheet: 'Upload count',
    hint: 'Only what you count is adjusted; leave a product blank to leave it alone. The sheet needs a product code column and a counted quantity column. Batch-tracked products are counted batch by batch on Stock operations.',
    unreadable: 'Use .xlsx or .csv.',
    uploaded_one: '{{count}} counted from sheet',
    uploaded_other: '{{count}} counted from sheet',
    unmatched_one: '{{count}} unknown code:',
    unmatched_other: '{{count}} unknown codes:',
    colCode: 'Code',
    colDescription: 'Description',
    colUnit: 'Unit',
    colSystem: 'System stock',
    colCounted: 'Counted',
    nothingToCount: 'No products.',
    chooseWarehouse: 'Choose a warehouse.',
    showingFirst: 'First {{shown}} of {{total}}',
    summary: '{{counted}} counted · {{changed}} differ',
    gain: 'Found:',
    loss: 'Missing:',
    review: 'Review differences',
    reviewTitle: 'Review',
    reviewIntro: '{{warehouse}}: {{counted}} counted, {{changed}} to adjust.',
    noDifferences: 'No differences.',
    back: 'Back',
    confirm: 'Confirm adjustment',
  },
  boms: {
    product: 'Finished good',
    makes: 'Makes',
    components: 'Components',
    estimatedCost: 'Estimated cost',
    status: 'Status',
    active: 'In use',
    withdrawn: 'Withdrawn',
    showWithdrawn: 'Show withdrawn',
    none: 'No bills of materials yet.',
    new: 'New BOM',
    edit: 'BOM',
    finishedGood: 'Finished good',
    makesQuantity: 'Makes (quantity)',
    component: 'Component',
    cost: 'Cost',
    hint: 'Quantities are in each product’s stock unit: how much of each component makes the quantity above. Cost is at today’s average stock cost.',
    incomplete: 'Add product, quantity and components.',
    withdraw: 'Withdraw',
    restore: 'Bring back into use',
    savedNotice: 'Bill of materials saved.',
    withdrawnNotice: 'Bill of materials withdrawn.',
    restoredNotice: 'Bill of materials back in use.',
  },
  production: {
    made: 'Finished goods made',
    madeHint:
      'What this run produced. Each comes in at its share of what the materials cost.',
    used: 'Materials used',
    usedHint:
      'What was used up to make them. A quantity above what is on hand is marked.',
    fillFromBom: 'Fill from BOM',
    filledFrom: 'Filled from {{products}} BOM.',
    noBom: 'No BOM found.',
    batchNumber: 'Batch (blank = auto)',
    incomplete: 'Add an output and a material.',
    post: 'Post production',
  },
  service: {
    number: 'Job no.',
    date: 'Date',
    customer: 'Customer',
    mobile: 'Mobile',
    mobileHint: 'Type three digits to find a returning customer.',
    findMobile: 'Find by mobile',
    noPreviousEntry: 'No previous entry.',
    device: 'Device',
    serialNumber: 'Serial / IMEI',
    fault: 'Fault (as told)',
    technician: 'Technician',
    estimatedOn: 'Estimated date',
    amount: 'Amount',
    statusLabel: 'Status',
    status: {
      received: 'Received',
      processing: 'Processing',
      ready: 'Ready',
      notReady: 'Not repairable',
      informed: 'Informed',
      delivered: 'Delivered',
      dead: 'Dead',
      cancelled: 'Cancelled',
    },
    invoice: 'Invoice',
    allStatuses: 'All statuses',
    search: 'Search',
    searchHint: 'Search…',
    none: 'No job cards yet.',
    new: 'New job card',
    card: 'Job card',
    savedNotice: 'Job card saved.',
    cancelledNotice: 'Job cancelled.',
    incomplete: 'Name and device required.',
    chooseGodown: 'Choose a godown.',
    billedBy: 'Billed: {{number}}',
    cancelledBecause: 'Cancelled: {{reason}}',
    godown: 'Godown',
    reference: 'Reference no.',
    paymentMode: 'Payment',
    cash: 'Cash',
    credit: 'Credit',
    online: 'Online',
    salesman: 'Salesman',
    customerDetails: 'Customer',
    customerName: 'Customer name',
    alternateMobile: 'Alternate mobile',
    email: 'E-mail',
    address1: 'Address line 1',
    address2: 'Address line 2',
    account: 'Customer account',
    walkIn: 'Walk-in (no account)',
    deviceDetails: 'Device',
    itemName: 'Item name',
    brand: 'Brand',
    model: 'Model no.',
    colour: 'Colour',
    accessories: 'Accessories received',
    workDetails: 'Work',
    serviceType: 'Service type',
    problem: 'Problem found',
    estimatedTime: 'Estimated time',
    estimatedCharge: 'Estimated charge',
    remarks: 'Remarks',
    notes: 'Workshop notes',
    stages: 'Stages',
    stage: {
      processing: 'Processing',
      ready: 'Ready',
      notReady: 'Not repairable',
      informed: 'Customer informed',
      delivered: 'Delivered',
      dead: 'Dead',
    },
    stagesAfterSave: 'Save to track stages.',
    partsAndLabour: 'Parts and labour',
    linesLocked: 'Billed; lines locked.',
    part: 'Part or service',
    rate: 'Rate',
    total: 'Total',
    estimateWas: 'Estimate: {{amount}}',
    cancelReason: 'Cancel reason',
    confirmCancel: 'Cancel the job',
    keepJob: 'Keep the job',
    save: 'Save',
    takeIn: 'Take in',
    billTo: 'Bill to account',
    chooseBillTo: 'Choose a customer.',
    raiseInvoice: 'Raise sales invoice',
    cancelJob: 'Cancel job',
  },
  txReports: {
    view: 'View',
    filters: 'Filters',
    clearFilters: 'Clear filters',
    all: 'All',
    bills: 'Bills',
    quantity: 'Qty',
    sale: 'Sale',
    purchase: 'Purchase',
    tax: 'Tax',
    others: 'Others',
    total: 'Total',
    cost: 'Cost',
    profit: 'Profit',
    margin: 'Margin',
    dateParty: 'Date · party',
    product: 'Product',
    mode: 'Mode',
    area: 'Area',
    route: 'Route',
    taxPercent: 'Tax %',
    serialNo: 'Sl no.',
    none: 'Nothing in this period.',
    customer: 'Customer',
    supplier: 'Supplier',
    salesman: 'Salesman',
    brand: 'Brand',
    category: 'Category',
    group: {
      Month: 'Month-wise',
      Day: 'Day-wise',
      Bill: 'Bill-wise',
      Item: 'Item-wise',
      Brand: 'Brand-wise',
      Category: 'Category-wise',
      Salesman: 'Salesman-wise',
      Party: 'Party-wise',
      Customer: 'Customer-wise',
      Supplier: 'Supplier-wise',
    },
  },
  stockPosition: {
    asOn: 'As on',
    code: 'Code',
    name: 'Name',
    batch: 'Batch',
    expiry: 'Expiry date',
    category: 'Category',
    brand: 'Brand',
    godown: 'Godown',
    branch: 'Branch',
    quantity: 'Quantity',
    rate: 'Rate',
    value: 'Stock value',
    received: 'Purchase date',
    age: 'Stock age (days)',
    ageBand: 'Age',
    anyAge: 'Any age',
    bandsIn: 'Bands in',
    expiryFrom: 'Expiry from',
    expiryTo: 'Expiry to',
    allBranches: '— all branches —',
    allGodowns: '— all godowns —',
    allBrands: '— all brands —',
    totalQuantity: 'Quantity',
    totalValue: 'Stock value',
    nothing: 'No stock.',
  },
  stockRegister: {
    purchase: 'Purchase',
    sales: 'Sales',
    otherIn: 'Other in',
    otherOut: 'Other out',
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    newEntries: 'New entry',
    inEntry: 'In an entry',
    general: 'General',
    sale: 'Sale',
    salesReturn: 'Sales return',
    purchase: 'Purchase',
    purchaseReturn: 'Purchase return',
    quotation: 'Quotation',
    salesOrder: 'Sales order',
    purchaseOrder: 'Purchase order',
    receipt: 'Receipt',
    payment: 'Payment',
    journal: 'Journal',
    serviceSale: 'Service sale',
    customer: 'Customer',
    supplier: 'Supplier',
    product: 'Product',
    ledger: 'Ledger',
    newHere: 'New on this screen',
    save: 'Save',
    draft: 'Save draft',
    print: 'Save & print',
    bill: 'Save & bill',
    addLine: 'Add line',
    hold: 'Hold the bill',
    goCustomer: 'Go to customer',
    goScan: 'Go to barcode scan',
    goPayment: 'Go to amount received',
    goCharges: 'Go to additional ledgers',
    nextBox: 'Next box in a line, then the next line',
    hint: {
      customer: 'Customer',
      scan: 'Scan',
      'add-line': 'Add line',
      payment: 'Received',
      charges: 'Ledgers',
      save: 'Save',
      print: 'Save & print',
      all: 'All shortcuts',
    },
    close: 'Close',
    search: 'Search menus',
    list: 'This list',
    finishFirst: 'Save or close the open entry first.',
  },
  references: {
    loadFrom: 'Load from',
    typeNumber: 'Type or pick a no.',
    loadFailed: 'Could not load it.',
    quotation: 'Quotation',
    salesOrder: 'Sales order',
    salesInvoice: 'Sales invoice',
    purchase: 'Purchase',
    purchaseOrder: 'Purchase order',
    purchaseInvoice: 'Purchase invoice',
  },
  serviceSales: {
    title: 'Service sales',
    new: 'New',
    none: 'No service sales yet.',
    newTitle: 'New service sale',
    editTitle: 'Service sale {{number}}',
    jobNo: 'Job no.',
    invoiceNo: 'Invoice no.',
    mobile1: 'Mobile 1',
    mobile2: 'Mobile 2',
    mailId: 'Mail ID',
    item: 'Item',
    serialNo: 'Serial no.',
    eDate: 'E. date',
    eRate: 'E. rate',
    eTime: 'E. time',
    accessories: 'Accessories',
    fault: 'Fault',
    notes: 'Notes',
    problem: 'Problem',
    isDead: 'Is dead',
    address1: 'Address 1',
    address2: 'Address 2',
    cashCredit: 'Cash / credit',
    mode: 'Mode',
    tax: 'Tax',
    nonTax: 'NT',
    barcode: 'Barcode',
    product: 'Product',
    code: 'Code',
    qty: 'Qty',
    uom: 'UOM',
    addLine: 'Add line',
    gTotal: 'G. total',
    saveAndBill: 'Save & bill',
    billed: 'Billed: {{number}}',
    postFailed: 'Billed {{number}}; not posted: {{reason}}',
    notFound: 'No product with that code.',
    billNeedsCustomer: 'Choose a customer account to bill.',
  },
  rma: {
    number: 'RMA no.',
    date: 'Date',
    direction: 'Direction',
    fromCustomer: 'From customer',
    toSupplier: 'To supplier',
    bothDirections: 'Both directions',
    party: 'Customer / supplier',
    customer: 'Customer',
    supplier: 'Supplier',
    invoice: 'Invoice',
    reason: 'Reason',
    quantity: 'Qty',
    statusLabel: 'Status',
    status: {
      requested: 'Requested',
      approved: 'Approved',
      received: 'Received',
      underInspection: 'Under inspection',
      repaired: 'Repaired',
      replaced: 'Replaced',
      refunded: 'Refunded',
      closed: 'Closed',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
    },
    condition: 'Condition',
    conditions: {
      good: 'Good',
      repairable: 'Faulty, repairable',
      defective: 'Defective',
      damaged: 'Damaged',
    },
    outcome: 'Settled by',
    search: 'Search',
    searchHint: 'Search…',
    allStatuses: 'All statuses',
    none: 'No returns yet.',
    new: 'New return (RMA)',
    card: 'Return',
    godown: 'Returns godown',
    godownHint: 'Where the returned goods are kept.',
    sale: 'Sales invoice',
    purchase: 'Purchase invoice',
    pickInvoice: 'Invoice (optional)',
    pickPartyFirst: 'Choose party first',
    invoiceReference: 'Invoice ref.',
    narration: 'Notes',
    product: 'Product',
    serial: 'Serial / IMEI',
    lineReason: 'What is wrong',
    stepNote: 'Note',
    stepNoteHint: 'Required when rejecting or cancelling.',
    save: 'Save',
    request: 'Request return',
    approve: 'Approve',
    reject: 'Reject',
    cancel: 'Cancel return',
    savedNotice: 'Return saved.',
    notices: {
      approve: 'Return approved.',
      reject: 'Return rejected.',
      cancel: 'Return cancelled.',
      receive: 'Received.',
      repair: 'Recorded as repaired.',
      close: 'Return closed.',
      replaced: 'Recorded as replaced.',
      replacedWith: 'Replaced: {{number}}',
      inspected: 'Inspection saved.',
    },
    incomplete: 'Party, godown, reason and a product required.',
    reasonNeeded: 'Reason required.',
    settledBy: 'Settled: {{number}}',
    details: 'Return',
    inspection: 'Inspection',
    inspectionNotes: 'Inspection notes',
    inspectedOn: 'Inspected on',
    saveInspection: 'Save inspection',
    photos: 'Photos',
    noPhotos: 'No photos yet.',
    photoCaption: 'Photo caption',
    addPhoto: 'Add photo',
    photoFailed: 'Photo not added.',
    removePhoto: 'Remove',
    goods: 'Goods',
    outcomeTitle: 'Outcome',
    outcomeHintCustomer:
      'Repaired: the customer gets the same unit back. Replace: a good unit leaves the stock godown and the returned one stays in the returns godown. Credit note: a draft sales return at what the customer paid, to check and post.',
    outcomeHintSupplier:
      'Repaired: the supplier sent the unit back repaired. Replace: the supplier’s replacement moves from the returns godown into the stock godown. Debit note: a draft purchase return, to check and post.',
    repaired: 'Repaired',
    stockGodown: 'Stock godown',
    replace: 'Replace',
    creditNote: 'Raise credit note',
    debitNote: 'Raise debit note',
    receive: 'Goods received',
    handOver: 'Handed to supplier',
    close: 'Close',
    history: 'History',
  },
  auditLog: {
    when: 'When',
    user: 'User',
    module: 'Module',
    action: 'Action',
    actions: { Create: 'Created', Update: 'Changed', Delete: 'Deleted' },
    record: 'Record',
    recordId: 'Record ID',
    fields: 'Fields',
    field: 'Field',
    before: 'Before',
    after: 'After',
    search: 'Search',
    searchHint: 'Search…',
    allModules: 'All modules',
    allUsers: 'All users',
    allActions: 'All actions',
    none: 'No entries.',
    sharedDatabase: 'Audit DB missing; using main DB.',
  },
  approvals: {
    heldNotice: '{{number}} sent for approval.',
    requestedAt: 'Requested',
    document: 'Document',
    action: 'Change',
    editAction: 'Correction',
    cancelAction: 'Cancellation',
    requestedBy: 'Requested by',
    reason: 'Reason',
    statusLabel: 'Status',
    status: {
      pending: 'Waiting',
      approved: 'Approved',
      rejected: 'Refused',
      withdrawn: 'Withdrawn',
    },
    kinds: { sales: 'Sale', purchase: 'Purchase', voucher: 'Voucher' },
    decidedBy: 'Decided by',
    allStatuses: 'All',
    none: 'No pending approvals.',
    request: 'Change request',
    proposed: 'Proposed',
    date: 'Date',
    product: 'Product',
    rate: 'Rate',
    amount: 'Amount',
    ledger: 'Ledger',
    debit: 'Debit',
    credit: 'Credit',
    openDocument: 'Open document',
    note: 'Note',
    noteHint: 'Required when refusing',
    approve: 'Approve',
    reject: 'Refuse',
    withdraw: 'Withdraw my request',
    reasonNeeded: 'Reason required.',
    approvedNotice: '{{number}} approved.',
    rejectedNotice: '{{number}} rejected.',
    withdrawnNotice: 'Withdrawn.',
  },
  quotations: {
    number: 'Quotation no.',
    date: 'Quotation date',
    expectedOn: 'Valid until',
    linesTitle: 'What is offered',
    none: 'No quotations.',
    new: 'New quotation',
    newSales: 'New sales quotation',
    document: 'Quotation',
    confirm: 'Mark accepted',
    confirmed: 'Accepted',
    cancelled: 'Declined',
    close: 'Decline quotation',
    closedFor: 'Declined: {{reason}}',
    searchHint: 'Search…',
    savedNotice: 'Saved {{number}}.',
    confirmedNotice: '{{number}} accepted.',
    closedNotice: 'Declined.',
  },
  prefs: {
    tabs: {
      general: 'General',
      defaults: 'Default values',
      sales: 'Sales',
      purchase: 'Purchase',
      inventory: 'Inventory',
      accounts: 'Accounts',
      charges: 'Additional ledgers',
      printing: 'Printing',
      numbering: 'Numbering',
      data: 'Data',
    },
    decimalPlaces_one: '{{count}} decimal place',
    decimalPlaces_other: '{{count}} decimal places',
    regionTitle: 'Region and appearance',
    regionHint: 'Where the firm trades, and how the screens look for everyone.',
    gulfMode: 'Gulf mode',
    gulfModeHint:
      'Arabic names beside the English ones on products, customers and other masters. Off, the Arabic fields are hidden unless one already holds a name.',
    theme: 'Theme',
    themeHint: 'The look every user starts with. Each person can still switch their own.',
    light: 'Light',
    dark: 'Dark',
    partiesTitle: 'Parties and places',
    partiesHint:
      'Who and where a new document starts with. Each can still be changed on the document.',
    accountsTitle: 'Default accounts',
    accountsHint:
      'The cash and bank accounts money is received into and paid from unless another is chosen.',
    defaultCash: 'Cash account',
    firstCash: 'First cash account',
    defaultBank: 'Bank account',
    firstBank: 'First bank account',
    approvalsTitle: 'Approvals',
    billWiseReceiptsTitle: 'Receipts: bill-wise',
    billWisePaymentsTitle: 'Payments: bill-wise',
    billWiseHint: 'For parties kept bill-wise, which bills money is set against.',
    billWise: 'Bill-wise management',
    billWiseReceiptsHint: 'Show a credit customer’s pending bills on a receipt.',
    billWisePaymentsHint: 'Show a supplier’s pending bills on a payment.',
    fillBills: 'Fill selected bills',
    fillBillsHint:
      'Tick the bills being settled and enter what goes to each; the amount is filled from them.',
    salesRulesTitle: 'Sales rules',
    salesEntryTitle: 'Sales entry',
    salesEntryHint: 'How a new sale is priced and which columns its lines offer.',
    taxModeNone: 'Non-tax',
    taxModeVat: 'Tax (VAT)',
    taxModeGst: 'GST',
    rateRetail: 'Retail rate',
    rateWholesale: 'Wholesale rate',
    rateMrp: 'MRP',
    rateLastQuotation: 'Last quotation',
    rateCustomer: 'Customer rate',
    rateBatch: 'Batch rate',
    purchaseRulesTitle: 'Purchase rules',
    stockRulesTitle: 'Stock rules',
    trackingTitle: 'Tracking',
    trackingHint: 'What the firm keeps track of on its products.',
    savingTitle: 'Saving transactions',
    salesScreenTitle: 'On the sales screen',
    showSalesScan: 'Barcode scan box',
    showSalesReference: 'Reference number',
    pickSalesmen: 'Choose salesman and billing man',
    showBillingName: 'Billing name',
    showSalesNarration: 'Narration / remarks',
    allowRateChange: 'Change rates on the bill',
    itemDiscountColumn: 'Item discount column',
    freeQuantityColumn: 'Free quantity column',
    inclusiveRates: 'Rates include tax (reverse calculation)',
    allowLoadFrom: 'Load from a quotation, order or purchase',
    allowHold: 'Hold bills',
    namedCustomerForNonCash: 'Card, online and credit sales need a named customer',
    walkInCustomer: 'Walk-in customer',
    primaryCustomer: 'The Primary customer',
    salesPaymentTitle: 'Payment and rounding',
    defaultSalesPaymentMode: 'Payment mode of a new sale',
    salesRoundTo: 'Round the bill total to',
    noRounding: 'No rounding',
    roundToNearest: 'Nearest {{unit}}',
    onlineTypesTitle: 'Online payment types',
    noOnlineTypes:
      'None yet. Add Google Pay, PhonePe, Talabat or any other way customers pay online.',
    onlineTypeName: 'Name',
    onlineTypeBank: 'Settles into',
    onlineTypeActive: 'Active',
    defaultBankAccount: 'Default bank account',
    onlineTypePlaceholder: 'Google Pay, PhonePe, Talabat…',
    addOnlineType: 'Add type',
    onlineTypeTaken: 'That type is already on the list.',
    salesDefaultsTitle: 'A new sale starts with',
    purchaseDefaultsTitle: 'A new purchase starts with',
    voucherPaymentMode: 'Payment mode of receipts and payments',
    paymentDebitAccount: 'Debit account of a payment',
    receiptCreditAccount: 'Credit account of a receipt',
    showChargesOn: 'Show additional ledgers on {{document}}',
    documents: {
      Sales: 'Sales invoice',
      SalesReturn: 'Sales return',
      SalesOrder: 'Sales order',
      Purchase: 'Purchase invoice',
      PurchaseReturn: 'Purchase return',
      PurchaseOrder: 'Purchase order',
    },
    noCharges: 'No charges.',
    chargeActive: 'Apply',
    dataTitle: 'Bring in and clear out',
    dataHint: 'Import from another system, or clear the firm’s data for a fresh start.',
    importData: 'Import data',
    clearData: 'Clear data',
    sampleTitle: 'Sample data',
    sampleHint:
      'Demonstration invoices, purchases, orders and vouchers to try the system on.',
    sampleRun: 'Generate samples',
    sampleRunning: 'Generating…',
    sampleDone: 'Sample data created.',
    sampleFailed: 'Some samples failed.',
  },
  years: {
    title: 'Financial years',
    hint: 'The periods documents are numbered and posted in. A closed year takes no postings until it is opened again.',
    code: 'Year',
    start: 'Starts',
    end: 'Ends',
    status: 'Status',
    open: 'Open',
    closed: 'Closed',
    locked: 'Locked',
    edit: 'Edit',
    close: 'Close',
    reopen: 'Reopen',
    add: 'Add a year',
    save: 'Save',
  },
  notes: {
    customer: 'Customer',
    supplier: 'Supplier',
    invoice: 'Invoice',
    notBilled: 'Not yet invoiced',
    billedBy: 'Invoiced by {{number}}',
    raiseInvoice: 'Raise sales invoice',
    raisePurchase: 'Raise purchase invoice',
    chooseCustomer: 'Choose a customer.',
    chooseSupplier: 'Choose a supplier.',
    deliveryHint:
      'The goods leave stock when the note is saved. Raise the invoice from the note later; it bills the goods without taking them out again.',
    receiptHint:
      'The goods come into stock at the rate entered when the note is saved. Raise the purchase from the note when the supplier invoices; it pays for the goods without receiving them again.',
  },
  history: {
    code: 'Code',
    previous: 'Previous stock',
    actual: 'Actual stock',
    adjusted: 'Adjusted',
    reason: 'Reason',
    none: 'No corrections.',
  },
  damaged: {
    entriesTab: 'Write-offs',
    historyTab: 'History',
    historyTitle: 'Damaged stock history',
  },
  policies: {
    title: 'Business rules',
    hint: 'Rules the server enforces on every document, whichever screen or device enters it.',
    readOnly: 'Read only.',
    negativeStock: 'Negative stock',
    negativeStockHint:
      'Whether goods may be issued that the system does not show on hand. Batches and serial numbers are never allowed below zero.',
    creditLimit: 'Credit limit',
    creditLimitHint:
      'What happens when a sale on credit would take a customer past their credit limit.',
    block: 'Block',
    warn: 'Warn',
    ignore: 'Ignore',
    transfersRequireApproval: 'Transfers need approval',
    transfersRequireApprovalHint:
      'On: stock moves between warehouses only by request, approval, dispatch and receipt. Off: direct transfers are allowed as well.',
    editsRequireApproval: 'Edits need approval',
    editsRequireApprovalHint:
      'A correction or cancellation by someone who may not approve it is held until an approver accepts it on the Approvals screen.',
    purchasesUpdateProductRates: 'Purchase updates rates',
    purchasesUpdateProductRatesHint:
      "Posting a purchase sets every product's cost to what was paid, and rebuilds the retail rate from its profit percentage (never above the MRP).",
  },
  transfers: {
    number: 'Request no.',
    from: 'From',
    to: 'To',
    requested: 'Requested',
    approved: 'Approved',
    dispatched: 'Dispatched',
    availableAtSource: 'At source',
    dispatchNumber: 'Dispatch no.',
    receiptNumber: 'Receipt no.',
    allStatuses: 'All statuses',
    noneFound: 'No requests.',
    notFound: 'Request not found.',
    new: 'New request',
    direct: 'Direct transfer',
    directHint:
      'Moves the goods at once, from one warehouse straight into the other, without a request or an approval.',
    requestsRequired: 'Direct transfers are off.',
    edit: 'Edit request',
    approve: 'Approve',
    reject: 'Reject',
    cancelRequest: 'Cancel request',
    dispatch: 'Dispatch',
    receive: 'Receive',
    confirm: 'Confirm',
    reason: 'Reason',
    askFrom: 'From warehouse',
    chooseWarehouses: 'Choose both warehouses.',
    addAProduct: 'Add a product.',
    deliverTo: 'To warehouse',
    quantityWanted: 'Quantity wanted',
    send: 'Send request',
    saveChanges: 'Save changes',
    approveHint:
      'Agree to as much of each product as the source can spare. Nought turns a line down.',
    dispatchHint:
      'Pick the batches and serial numbers that are leaving. The goods go into Goods in transit until {{to}} receives them.',
    receiveHint:
      'Takes everything that was dispatched into the receiving warehouse, at the cost it left at.',
    receivedOn: 'Received on',
    raisedNotice: 'Request {{number}} sent.',
    savedNotice: 'Request saved.',
    approvedNotice: 'Request {{number}} approved.',
    rejectedNotice: 'Request {{number}} rejected.',
    cancelledNotice: 'Request {{number}} cancelled.',
    dispatchedNotice: '{{number}} dispatched ({{document}}).',
    receivedNotice: '{{number}} received ({{document}}).',
    statusRequested: 'Requested',
    statusApproved: 'Approved',
    statusDispatched: 'Dispatched',
    statusReceived: 'Received',
    statusRejected: 'Rejected',
    statusCancelled: 'Cancelled',
  },
  warehouses: {
    branch: 'Branch',
    central: '— central —',
    address: 'Address',
    default: 'Default',
    makeDefault: 'Make default',
  },
  sales: {
    number: 'Number',
    billingName: 'Billing name',
    billingNamePlaceholder: 'Walk-in customer’s name',
    onlineType: 'Paid with',
    chooseOnlineType: 'Choose…',
    onlineTypeRequired: 'Choose how it was paid online.',
    namedCustomerRequired: 'Choose the customer: {{mode}} sales need a named customer.',
    heldBills_one: '{{count}} held bill',
    heldBills_other: '{{count}} held bills',
    heldTitle: 'Held bills',
    heldSubtitle: 'Bills put on hold today, newest first.',
    noHeldBills: 'No bills on hold.',
    openHeld: 'Open',
    discardHeld: 'Discard',
    heldNotice: 'Bill put on hold. Open it again from Held bills.',
    resumedFrom: 'Held at {{time}}',
    dateRequired: 'Date required.',
    customerRequired: 'Customer required.',
    warehouseRequired: 'Warehouse required.',
    linesRequired: 'Add a product.',
    quantityInvalid: 'Qty must be above 0.',
    rateInvalid: 'Invalid rate.',
    taxInvalid: 'Tax 0–100%.',
    kind: 'Kind',
    invoice: 'Invoice',
    return: 'Return',
    date: 'Date',
    customer: 'Customer',
    warehouse: 'Warehouse',
    reference: 'Reference',
    lines: 'Lines',
    taxable: 'Taxable',
    tax: 'Tax',
    total: 'Total',
    status: 'Status',
    draft: 'Draft',
    posted: 'Posted',
    cancelled: 'Cancelled',
    allKinds: 'All kinds',
    allStatuses: 'All statuses',
    from: 'From',
    to: 'To',
    search: 'Search',
    searchHint: 'Number or reference',
    none: 'No documents.',
    new: 'New invoice',
    newInvoice: 'New invoice',
    newReturn: 'New return',
    open: 'Open',
    document: 'Document',
    close: 'Close',
    saveDraft: 'Save draft',
    post: 'Post',
    cancel: 'Cancel document',
    cancelReason: 'Reason',
    product: 'Product',
    quantity: 'Quantity',
    rate: 'Rate',
    discount: 'Discount',
    taxPercent: 'Tax %',
    net: 'Net',
    lineTotal: 'Total',
    addLine: 'Add line',
    removeLine: 'Remove',
    chooseCustomer: '— choose a customer —',
    chooseWarehouse: '— choose a warehouse —',
    chooseProduct: '— choose a product —',
    againstInvoice: 'Against invoice',
    noInvoice: '— none —',
    currency: 'Currency',
    batch: 'Batch',
    chooseBatch: '— choose a batch —',
    serialsChosen: '{{chosen}}/{{wanted}} chosen',
    noSerials: 'No serials in stock.',
    savedNotice: 'Saved {{number}} (draft).',
    postedNotice: '{{number}} posted: {{total}}.',
    cancelledNotice: 'Cancelled.',
    edit: 'Edit',
    editDocument: 'Edit {{number}}',
    editPostedHint: 'Posted: saving re-posts it under the same number.',
    editReason: 'Reason',
    updatedNotice: '{{number}} updated.',
    postedProduced:
      'Posting produced a stock document, a journal, and — for a sale — a bill.',
    returnHint:
      'Naming the invoice lets the goods come back at the cost they left at, and the credit settle that sale’s bill. Without one they come back at the current average and the credit sits on the account.',
    totalsHint:
      'An estimate. The server rounds the total to the currency when it is saved.',
    salesInvoices: 'Sales invoices',
    salesReturns: 'Sales returns',
    newSalesReturn: 'New return',
    returnNo: 'Return No',
    noReturns: 'No returns.',
    invoicesTab: 'Sales Invoices',
    returnsTab: 'Sales Returns',
    invoiceNo: 'Invoice No',
    referenceNo: 'Ref. No',
    salesman: 'Salesman',
    billingman: 'Billingman',
    paymentMode: 'Payment Mode',
    taxMode: 'Tax Mode',
    barcode: 'Barcode',
    barcodePlaceholder: 'Scan barcode…',
    freeQuantity: 'Free Qty',
    itemDiscount: 'Discount',
    salesRateType: 'Sales Rate',
    reverseCalculation: 'Reverse Calculation',
    autoBatch: 'Auto Batch',
    additionalLedgers: 'Additional Ledgers',
    addLedger: 'Add Ledger',
    addition: 'Add (+)',
    addCharge: '+ Add charge',
    deduction: 'Deduct (-)',
    chooseLedger: 'Choose Ledger',
    additionalLedgersTotal: 'Charges total',
    batchExpiresOn: 'Expires',
    batchExpired: 'Expired',
    inclusiveRate: 'Incl. tax',
    scanProductSuccess: 'Added',
    scanProductNotFound: 'Barcode not found',
    itemsTitle: 'Items',
    itemCount_one: '{{count}} item',
    itemCount_other: '{{count}} items',
    generalTab: 'General',
    shippingTab: 'Shipping',
    narration: 'Narration / Remarks',
    grandTotal: 'Grand Total',
    cashGiven: 'Cash received',
    changeBack: 'Balance to return',
    billDue: 'Due on this bill',
    itemsTotal: 'Items total',
    prevBalance: 'Previous balance',
    billSection: 'Bill',
    billTotal: 'Bill total',
    returnTotal: 'Return total',
    paymentSection: 'Payment',
    receivedNow: 'Amount paid',
    receivedInto: 'Received into',
    noReceivingAccount: 'No cash/bank account',
    transactionId: 'Transaction ID',
    transactionIdPlaceholder: 'e.g. 4471 2290 1183',
    cardApproval: 'Approval code',
    cardApprovalPlaceholder: 'From the card slip',
    accountSection: 'Customer account',
    thisBill: 'This bill',
    thisReturn: 'This return',
    balanceAfter: 'New balance',
    overCreditLimit: 'Over limit {{limit}} by {{by}}.',
    creditLimitBlocks: 'Collect the excess or raise the limit.',
    amountReceived: 'Received',
    paymentReference: 'Transaction ID',
    taxTreatment: 'Mode',
    print: 'Print',
    printInvoice: 'Print Invoice',
    printPreview: 'Print',
    documentDetails: 'Document Details',
    time: 'Time',
    noEmployees: 'No employees yet',
    savedPostedNotice: 'Posted {{number}}.',
    save: 'Save',
    saveAndPrint: 'Save & print',
    taxAmount: 'Tax',
    setUpCharge: 'New additional ledger',
    setUpChargeHint:
      'Any expense or income ledger can be carried on a sale. Choose it and whether it adds to the bill or comes off it; it is then offered on every sale.',
    setUp: 'Set up',
    noAdditionalLedgers: 'No charges.',
  },
  customers: {
    mobile: 'Mobile',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    address2: 'Address line 2',
    creditDays: 'Credit days',
    creditLimit: 'Credit limit',
    noLimit: 'none',
    stateCode: 'State',
    state: 'State',
    noState: 'Not stated',
    stateHint: 'Decides which tax heads an invoice to them charges.',
    stateNoRegime: 'Set the tax regime in Settings to choose a state.',
    stateRequired: 'State required for GST.',
    registrationNumber: 'Tax reg. / GSTIN',
    openingBalance: 'Opening balance',
    openingHint: 'What they already owed when the books were taken on.',
    openingInvalid: 'Opening must be positive.',
    codeAuto: 'Leave blank for the next code in the sequence.',
    codeFixed: 'A code cannot be changed once history points at it.',
    codeTooLong: 'Max 30 characters.',
    nameRequired: 'Name required.',
    nameTooLong: 'Max 200 characters.',
    emailInvalid: 'Invalid email.',
    creditDaysInvalid: 'Invalid credit days.',
    creditLimitInvalid: 'Invalid credit limit.',
    edit: 'Edit customer',
    contactPerson: 'Contact person',
    website: 'Website',
    fax: 'Fax',
    city: 'City',
    pinCode: 'PIN code',
    area: 'Area',
    route: 'Route',
    noAreas: 'No areas yet',
    noRoutes: 'No routes yet',
    chooseCountryFirst: 'Choose country first',
    gstNumber: 'GST ID',
    tinNumber: 'TIN',
    cstNumber: 'CST registration no.',
    sms: 'SMS',
    sendSms: 'Send SMS',
    openingAmount: 'Opening amount',
    sectionGeneral: 'General',
    sectionAddress: 'Address',
    sectionTax: 'Tax registration',
    sectionTerms: 'Credit & opening',
    sectionPrint: 'Print settings',
    printCopies: 'Copies',
    printPreview: 'Preview',
    previewFirst: 'Show before printing',
    printStraightAway: 'Print straight away',
    copiesInvalid: 'Copies: 1 or more.',
    photo: 'Photo',
    noPhoto: 'No photo',
    addPhoto: 'Add photo',
    changePhoto: 'Change photo',
    removePhoto: 'Remove photo',
    photoInvalid: 'Invalid image.',
  },
  places: {
    countries: 'Countries',
    states: 'States',
    countriesTitle: 'Countries',
    statesTitle: 'States',
    country: 'Country',
    allCountries: 'All countries',
    currency: 'Currency',
    currencyName: 'Currency name',
    newCountry: 'New country',
    editCountry: 'Edit country',
    newState: 'New state',
    editState: 'Edit state',
    areas: 'Areas',
    routes: 'Routes',
    newArea: 'New area',
    newRoute: 'New route',
    editArea: 'Edit area',
    editRoute: 'Edit route',
    codeRequired: 'A code is required.',
    codeTooLong: 'Max 3 letters.',
    stateCodeTooLong: 'Max 10 characters.',
    nameRequired: 'A name is required.',
    nameTooLong: 'Max 100 characters.',
    countryRequired: 'Choose the country.',
    currencyInvalid: '3 letters, e.g. QAR.',
  },
  suppliers: {
    registration: 'Tax registration',
    registrationHint: 'What an input tax reclaim is made against.',
    stateHint: 'Decides which tax heads a purchase from them carries.',
    stateRequired: 'State required for GST.',
    openingHint: 'What the firm already owed them when the books were taken on.',
    nameRequired: 'Name required.',
    edit: 'Edit supplier',
  },
  orders: {
    number: 'Order no.',
    numberAuto: 'Issued by the branch’s numbering series.',
    numberOnSave: 'Auto on save',
    date: 'Order date',
    expectedOn: 'Expected',
    expectedHint: 'When the goods were promised. Left blank if nobody said.',
    supplier: 'Supplier',
    customer: 'Customer',
    warehouse: 'Warehouse',
    reference: 'Reference',
    referenceSupplier: 'Their quotation number, where there is one.',
    referenceCustomer: 'Their purchase order number, where there is one.',
    narration: 'Narration',
    lines: 'Lines',
    linesTitle: 'What was ordered',
    outstandingLines: 'Still owed',
    outstandingOnly: 'Outstanding only',
    taxable: 'Taxable',
    tax: 'Tax',
    total: 'Total',
    currency: 'Currency',
    status: 'Status',
    draft: 'Draft',
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Closed',
    allStatuses: 'All statuses',
    from: 'From',
    to: 'To',
    search: 'Search',
    searchHint: 'Search…',
    none: 'No orders.',
    new: 'New order',
    newPurchase: 'New purchase order',
    newSales: 'New sales order',
    open: 'Open',
    document: 'Order',
    saveDraft: 'Save draft',
    confirm: 'Confirm order',
    convertPurchase: 'Make purchase',
    convertSales: 'Make invoice',
    close: 'Close order',
    closeReason: 'Reason',
    closeHint: 'Kept on the order, and shown wherever it is read back.',
    closedFor: 'Closed: {{reason}}',
    booksHint:
      'An order is a commitment rather than a transaction: it posts no voucher, so it moves no account and appears in no trial balance. The books follow when the purchase or invoice raised from it is posted.',
    product: 'Product',
    quantity: 'Quantity',
    ordered: 'Ordered',
    invoiced: 'Invoiced',
    outstanding: 'Outstanding',
    rate: 'Rate',
    discount: 'Discount',
    taxPercent: 'Tax %',
    net: 'Net',
    savedNotice: 'Saved {{number}}.',
    confirmedNotice: '{{number}} confirmed.',
    convertedPurchase: 'Draft purchase {{number}} created.',
    convertedSales: 'Draft invoice {{number}} created.',
    closedNotice: 'Order closed.',
    dateRequired: 'Date required.',
    supplierRequired: 'Supplier required.',
    customerRequired: 'Customer required.',
    warehouseRequired: 'Warehouse required.',
    expectedBeforeDate: 'Before order date.',
    linesRequired: 'Add a product.',
    quantityInvalid: 'Qty must be above 0.',
    rateInvalid: 'Invalid rate.',
    taxInvalid: 'Tax 0–100%.',
  },
  documents: {
    columns: 'Line columns',
    charges: 'Charges',
    chargesShort: 'Charges',
    chargesHint:
      'Freight, packing, delivery, a discount off the whole document — whatever the supplier billed beside the goods.',
    chargeAccount: 'Charge account',
    chargeAmount: 'Amount',
    chooseCharge: '— choose an account —',
    addCharge: 'Add ledger',
    removeCharge: 'Remove',
    chargeAdds: 'Adds',
    chargeDeducts: 'Deducts',
    noChargeAccounts: 'No charge accounts.',
    noChargesSetUp: 'No additional ledgers set up.',
    setUpCharges: 'Set up',
    gross: 'Gross',
    discount: 'Discount',
    net: 'Net',
    rounding: 'Round off',
    taxable: 'Taxable',
    tax: 'Tax',
    total: 'Total',
  },
  purchase: {
    number: 'Number',
    documentNumber: 'Document number',
    numberAuto: 'Issued by the branch’s numbering series.',
    numberOnSave: 'Auto on save',
    openDraft: 'Open draft',
    noReturns: 'No returns.',
    linesTitle: 'What was bought',
    narration: 'Narration',
    taxAmount: 'Tax',
    chargeTotal: 'Charges',
    taxMode: 'Tax treatment',
    modeTax: 'Taxable',
    modeNonTax: 'No tax',
    modeCst: 'CST',
    warehouseDefaulted:
      'Defaulted from the warehouse master; change it if the goods went elsewhere.',
    againstHint: 'Lets the debit settle that document’s bill.',
    cancelHint: 'Kept on the document, and shown wherever it is read back.',
    dateRequired: 'Date required.',
    supplierRequired: 'Supplier required.',
    warehouseRequired: 'Warehouse required.',
    supplierInvoiceMissing: 'Add the supplier invoice no.',
    saveAndPost: 'Save & post',
    savedNotPosted: 'Saved {{number}} as a draft; not posted: {{reason}}',
    invoiceDateNeedsNumber: 'Add the invoice no.',
    linesRequired: 'Add a product.',
    quantityInvalid: 'Qty must be above 0.',
    rateInvalid: 'Invalid rate.',
    discountInvalid: 'Invalid discount.',
    taxInvalid: 'Tax 0–100%.',
    kind: 'Kind',
    invoice: 'Purchase',
    return: 'Return',
    date: 'Date',
    supplier: 'Supplier',
    warehouse: 'Warehouse',
    supplierInvoice: 'Supplier invoice',
    supplierInvoiceDate: 'Their date',
    lines: 'Lines',
    taxable: 'Taxable',
    tax: 'Input tax',
    total: 'Total',
    status: 'Status',
    draft: 'Draft',
    posted: 'Posted',
    cancelled: 'Cancelled',
    allKinds: 'All kinds',
    allStatuses: 'All statuses',
    from: 'From',
    to: 'To',
    search: 'Search',
    searchHint: 'Either number',
    none: 'No documents.',
    new: 'New document',
    newInvoice: 'New purchase',
    newReturn: 'New return',
    open: 'Open',
    document: 'Document',
    close: 'Close',
    saveDraft: 'Save draft',
    post: 'Post',
    cancelDocument: 'Cancel document',
    cancelReason: 'Reason',
    product: 'Product',
    quantity: 'Quantity',
    rate: 'Cost',
    discount: 'Discount',
    taxPercent: 'Tax %',
    net: 'Net',
    addLine: 'Add line',
    removeLine: 'Remove',
    chooseSupplier: '— choose a supplier —',
    chooseWarehouse: '— choose a warehouse —',
    chooseProduct: '— choose a product —',
    againstInvoice: 'Against purchase',
    noInvoice: '— none —',
    currency: 'Currency',
    batch: 'Batch',
    batchHint: 'From the carton',
    batchExisting: 'Batch',
    batchSellingRate: 'Batch selling rate',
    batchSellingRateHint: 'Blank = product rate',
    expiresOn: 'Expires',
    serialsNamed: '{{named}}/{{wanted}} named',
    serialsHint: 'One number per line',
    savedNotice: 'Saved {{number}} (draft).',
    postedNotice: '{{number}} posted: {{total}}.',
    cancelledNotice: 'Cancelled.',
    edit: 'Edit',
    editDocument: 'Edit {{number}}',
    editPostedHint: 'Posted: saving re-posts it under the same number.',
    editReason: 'Reason',
    updatedNotice: '{{number}} updated.',
    postedProduced:
      'Posting produced a receipt, a journal, and — for a purchase — a bill the supplier is owed.',
    supplierInvoiceHint:
      'Input tax is reclaimable against the supplier’s own tax invoice, so their number and date are what a return reports. The same number twice for one supplier is refused.',
    returnHint:
      'Naming the purchase lets the debit settle that document’s bill. Without one it sits on the supplier’s account. Goods go back at what the shelf says they cost, which is not always what was agreed.',
    totalsHint:
      'An estimate. The server rounds the total to the currency when it is saved.',
  },
  tax: {
    byDate: 'Date-wise',
    byRate: 'Rate-wise',
    salesTaxable: 'Sales taxable',
    purchaseTaxable: 'Purchase taxable',
    total: 'Total',
    summary: 'Summary',
    outputTax: 'Output tax',
    inputTax: 'Input tax',
    head: 'Head',
    rate: 'Rate',
    tax: 'Tax',
    taxableValue: 'Taxable value',
    taxableSupplies: 'Taxable supplies',
    zeroRated: 'Zero-rated supplies',
    taxablePurchases: 'Taxable purchases',
    zeroRatedPurchases: 'Zero-rated purchases',
    netPayable: 'Net payable',
    onLedger: 'On the ledger',
    difference: 'Difference',
    inputOnLedger: 'Ledger input',
    inputDifference: 'Input difference',
    byJournal: 'Posted by journal',
    document: 'Document',
    voucher: 'Voucher',
    account: 'Account',
    narration: 'Narration',
    registration: 'Registration',
    regimeVat: 'GCC VAT',
    regimeGst: 'India GST',
    regimeNone: 'No tax regime',
    reconciled: 'Reconciled',
    notReconciled: 'Not reconciled — check',
    nothingCharged: 'No tax this period.',
    noSupplies: 'No supplies.',
    noInput: 'No input tax.',
    differenceHint:
      'The ledger column is what each tax account actually moved by. A difference means tax reached the books by some route other than a sales document — a journal written by hand — and only a person can say which figure is right.',
    inputHint: 'Journal rows have no taxable value.',
  },
  grid: {
    search: 'Search…',
    columns: 'Columns',
    freeze: 'Freeze first',
    unfreeze: 'Unfreeze',
    exportCsv: 'Export CSV',
    saveLayout: 'Save layout',
    editLayout: 'Edit layout',
    resetLayout: 'Reset',
    layoutSaved: 'Layout saved',
    layoutReset: 'Layout reset',
    sortBy: 'Sort by',
    sortNone: 'Unsorted',
    sortDirection: 'Reverse',
    moveLeft: 'Move earlier',
    moveRight: 'Move later',
    position: 'Position',
    width: 'Width',
    auto: 'Auto',
    actions: 'Actions',
    noRows: 'Nothing to show.',
    rowCount: '{{shown}} of {{total}}',
    pageOf: 'Page {{page}}/{{pages}} · {{total}}',
    previousPage: 'Previous',
    nextPage: 'Next',
    columnsHint: 'Columns',
    freezeHint: 'Freeze first column',
    exportHint: 'Export CSV',
    saveLayoutHint: 'Save layout',
    columnsLockedHint:
      'The arrangement is kept. Choose Edit layout from the list options to change it.',
    editLayoutHint: 'Edit layout',
    options: 'List options',
    resetLayoutHint: 'Reset layout',
    showingRange: '{{first}}–{{last}} of {{total}}',
    totalRows_one: '{{count}} record',
    totalRows_other: '{{count}} records',
    ofUnfiltered: '(of {{total}})',
    pagination: 'Pagination',
    goToPage: 'Go to page {{page}}',
  },
  settings: {
    title: 'Settings',
    subtitle: 'How this installation behaves',
    taxTitle: 'Tax',
    taxEnabled: 'Charge tax',
    allowSaveDraft: 'Offer Save draft on sales and purchases',
    taxHint:
      'The regime decides which heads a document charges and which states it can name.',
    regime: 'Tax regime',
    regimeVat: 'GCC VAT',
    regimeGst: 'India GST',
    regimeNone: 'No tax',
    firmState: 'Firm state',
    firmStateHint:
      'Compared with the customer\u2019s to decide IGST against CGST plus SGST.',
    noState: 'Not set',
    taxRates: 'Tax rates',
    taxRatesHint: 'Separated by commas. These are what a rate picker offers.',
    defaultTaxRate: 'Default tax rate',
    translationTitle: 'Arabic translation',
    translationHint:
      'Fills an Arabic name in from the English one. What comes back stays editable.',
    autoTranslate: 'Auto-translate Arabic',
    autoTranslateHint:
      'Sends the English text to Google Translate as it is typed. Off until you turn it on.',
    apiKey: 'Translate API key',
    apiKeyHint:
      'Optional. Without one the public endpoint is used, which Google rate-limits as it sees fit.',
    apiKeyOptional: 'Optional',
    translateNow: 'Translate',
    translating: 'Translating…',
    translateFailed:
      'The translation service could not be reached. Type the name instead.',
    documentsTitle: 'Documents',
    documentsHint: 'What a new document starts with.',
    preferredWarehouse: 'Default godown',
    preferredWarehouseHint:
      'Leave unset to use whichever warehouse the master marks as the default.',
    warehouseFromMaster: 'Master default',
    masterDefault: 'default',
    defaultPaymentMode: 'Default payment mode',
    defaultPaymentModeHint:
      'Leave unset to ask each time. A counter that only takes cash need not say so on every voucher.',
    productsTitle: 'A new product starts with',
    productsHint:
      'What a new product starts with, so a mandatory field is not asked twice.',
    defaultCategory: 'Default category',
    defaultCategoryHint: 'Still mandatory — this only fills it in.',
    defaultStockUnit: 'Default unit',
    defaultStockUnitHint: 'Still mandatory — this only fills it in.',
    defaultBrand: 'Default brand',
    chargesTitle: 'Additional ledgers',
    chargeEffect: 'Effect',
    chargeDefault: 'Default amount',
    chargeIsDefault: 'Default',
    chargeTabs: {
      sales: 'Sales & returns',
      salesOrder: 'Quotations & orders',
      purchase: 'Purchases & returns',
      purchaseOrder: 'Purchase orders',
    },
    chargesHint:
      'Freight, packing, delivery, a standing discount — set once per kind of document and applied to every new one, so the row is not added by hand a hundred times a week.',
    chargesDocument: 'Document',
    chargeAmount: 'Amount',
    chargeAmountBlank: 'Ask',
    chargesNoneSet: 'No document kind starts with a charge yet.',
    chargesSetOn: 'Set on: {{kinds}}.',
    chargeDocuments: {
      sales: 'Sales invoice',
      salesReturn: 'Sales return',
      salesOrder: 'Sales order',
      purchase: 'Purchase invoice',
      purchaseReturn: 'Purchase return',
      purchaseOrder: 'Purchase order',
    },
    askEachTime: 'Ask each time',
    decimalsTitle: 'Decimal places',
    decimalsHintFirm:
      'How many places money is shown to. Set for the firm, because this sign-in is not scoped to a branch.',
    decimalsHintBranch:
      'How many places money is shown to in this branch. Branches keep their own, and a branch that has not chosen follows the firm.',
    decimals: 'Places',
    decimalsFieldHint:
      'Presentation only: the server keeps and posts the full precision it was given.',
    decimalsFollowFirm: 'Firm default ({{count}})',
    listsTitle: 'Lists',
    listsHint: 'How much of a list is shown at once.',
    pageSize: 'Rows per page',
    masterDefaultsTitle: 'Master defaults',
    masterDefaultsHint:
      'Configure initial choices for all main dropdowns and sales features.',
    defaultCustomer: 'Customer',
    defaultSupplier: 'Supplier',
    defaultSalesman: 'Salesman',
    defaultBillingman: 'Billing man',
    defaultSalesRateType: 'Sales rate',
    defaultTaxMode: 'Tax mode',
    quantityDecimals: 'Qty decimals',
    quantityDecimalsHint: 'Number of decimal places for product quantities.',
    enableDeviceAttributes: 'Device attributes on products',
    enableDeviceAttributesHint:
      'Show device attributes (Device, Colour, Battery, RAM, Storage) in product details.',
    enableReverseCalculation: 'Reverse calculation',
    enableReverseCalculationHint:
      'Allow entering tax-inclusive rates with automatic reverse computation in sales.',
    enableFreeQuantity: 'Enable Free Quantity',
    enableFreeQuantityHint: 'Show free quantity column on sales invoice lines.',
    enableItemDiscount: 'Enable Item Discount',
    enableItemDiscountHint: 'Show discount column on sales invoice lines.',
    enableAutoBatch: 'Pick batches automatically (FIFO)',
    enableAutoBatchHint: 'Automatically pick FIFO batches for sales line items.',
    enableGodowns: 'More than one godown',
    enableGodownsHint:
      'Choose a godown on documents, stock and reports. When off, everything uses the default godown and the field is hidden.',
    directSave: 'Save every transaction final in one step',
    directSaveHint:
      'Save every transaction final in one step: orders are confirmed and stock documents posted as they are saved, with no draft.',
    enableSerialNumbers: 'Serial / IMEI numbers',
    enableSerialNumbersHint:
      'Offer serial numbers on products tracked by them. Always optional: a line may name all, some or none of its units.',
    additionalLedgerDefaults: 'Default charges',
    additionalLedgerDefaultsHint:
      'Configure standing default additional ledgers for each transaction type.',
    addDefaultLedger: 'Add Default Ledger',
    customerInvoiceTemplate: 'Print template',
    formatA4: 'A4',
    formatA5: 'A5',
    formatThermal: 'Thermal 80mm',
    pageSizeHint: 'Between 5 and 500.',
    reset: 'Reset all settings',
    storedLocally: 'Using this browser’s copy.',
    storedForFirm:
      'Shared by every workstation in this firm: a change here reaches every counter. Rows per page stays with this browser.',
    storedReadOnly: 'Admin only. Changes stay in this browser.',
  },
  masterPicker: {
    add: {
      category: 'New category',
      brand: 'New brand',
      unit: 'New unit',
      taxRate: 'New tax rate',
      customer: 'New customer',
      supplier: 'New supplier',
      employee: 'New employee',
      ledger: 'New ledger',
      warehouse: 'New godown',
    },
    code: 'Code',
    codeAutomatic: 'Automatic',
    name: 'Name',
    nameArabic: 'Name (Arabic)',
    mobile: 'Mobile',
    convertsTo: 'Converts to {{unit}}',
    factor: 'How many {{unit}} in one',
  },
  taxRates: {
    title: 'Tax rates',
    new: 'New tax rate',
    edit: 'Edit tax rate',
    percentage: 'Percentage',
    default: 'Default',
    makeDefault: 'Make default',
    isDefault: 'Default rate',
    namePlaceholder: 'e.g. VAT 5%',
    nameRequired: 'Give the rate a name.',
    percentageRequired: 'Enter the percentage.',
    percentageInvalid: '0–100 only.',
    duplicate: 'Already used by {{name}}.',
    none: 'No tax rate',
  },
  photos: {
    title: 'Photos',
    photo: 'Photo {{number}}',
    add: 'Add',
    change: 'Change',
    remove: 'Remove',
    invalid: 'That file is not a picture.',
  },
  employees: {
    title: 'Employees',
    new: 'New employee',
    edit: 'Edit employee',
    none: 'No employees.',
  },
  ledgerList: {
    title: 'Ledgers',
    none: 'No ledgers.',
    allKinds: 'All kinds',
  },
  voucherList: {
    receipts: 'Receipts',
    payments: 'Payments',
    journals: 'Journal',
    newReceipt: 'New receipt',
    newPayment: 'New payment',
    newJournal: 'New journal',
    receivedFrom: 'Received from',
    receivedInto: 'Received into',
    paidTo: 'Paid to',
    paidFrom: 'Paid from',
    debited: 'Debit',
    credited: 'Credit',
    noReceipts: 'No receipts in this period.',
    noPayments: 'No payments in this period.',
    noJournals: 'No journal entries in this period.',
    savedNotice: '{{number}} saved.',
  },
  ledgerMaster: {
    newLedger: 'New ledger',
    editLedger: 'Edit ledger',
    name: 'Ledger name',
    code: 'Code',
    codeHint: 'Leave blank to make one from the name.',
    nameArabic: 'Name in Arabic',
    kind: 'Kind',
    group: 'Group (schedule)',
    openingBalance: 'Opening balance',
    openingHint: 'What stood on it when you started using the system.',
    openingSide: 'Opening side',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    bankDetails: 'Bank details',
    withdraw: 'Deactivate',
    restore: 'Put back in use',
    editedElsewhere: 'Edit “{{name}}” on its own screen.',
    renameGroup: 'Rename group',
    newGroup: 'New group',
    groupName: 'Group name',
    parentGroup: 'Under',
    parentHint: 'The new group takes this group’s nature: asset, liability, income…',
    statement: 'Statement',
    groups: 'Groups',
    view: 'View',
    ledgerCount: 'Ledgers',
    rename: 'Rename',
  },
  statement: {
    title: 'Ledger statement',
    voucher: 'Voucher',
    type: 'Type',
    particulars: 'Particulars',
    reference: 'Reference',
    balance: 'Balance',
    chooseLedger: 'Choose a ledger…',
    pickLedger: 'Choose a ledger.',
    ledger: 'Ledger',
    movement: 'Movement',
    noPostings: 'No postings.',
  },
  outstanding: {
    debtorsTitle: 'Debtors outstanding',
    creditorsTitle: 'Creditors outstanding',
    overdueOnly: 'Overdue only',
    view: 'View',
    views: {
      bills: 'Bill-wise',
      aging: 'Age-wise',
    },
    nothing: {
      debtors: 'Nothing owed.',
      creditors: 'Nothing owed.',
    },
    nothingAged: 'Nothing outstanding.',
    billWiseHint:
      'Built from open bills, so a party appears here when their account is bill-wise. Any other party’s balance is on their ledger statement.',
    parties: 'Parties',
    totalOutstanding: 'Outstanding',
    totalOverdue: 'Overdue',
    mixedCurrencies: 'Totals mix currencies.',
    billCount_one: '{{count}} bill',
    billCount_other: '{{count}} bills',
    overdueAmount: '{{amount}} overdue',
    bill: 'Bill',
    due: 'Due',
    original: 'Amount',
    settled: 'Settled',
    outstanding: 'Outstanding',
    daysOverdue: 'Days overdue',
    range: '{{from}}–{{to}} days',
    over: 'Over {{days}} days',
    party: 'Party',
    notDue: 'Not yet due',
    total: 'Total',
  },
  profile: {
    anonymous: 'User',
    user: 'User',
    title: 'My profile',
    subtitle: 'Your details, sign-in and preferences',
    active: 'Active Session',
    passwordManaged: 'Password managed',
    myProfile: 'My profile',
    tabs: {
      details: 'Details',
      security: 'Security',
      preferences: 'Preferences',
      access: 'Roles & access',
    },
    noRoles: 'No roles',
    lastSignIn: 'Last signed in',
    passwordChanged: 'Password changed',
    never: 'Never',
    signedInOn: 'Signed in on',
    deviceCount_one: '{{count}} device',
    deviceCount_other: '{{count}} devices',
    personalDetails: 'Personal details',
    personalHint: 'Your name is shown on screens and on the documents you enter.',
    detailsSaved: 'Saved.',
    displayName: 'Name',
    displayNameHint: 'Shown in the top bar, on documents and in reports.',
    email: 'Email',
    mobile: 'Mobile',
    signInDetails: 'Sign-in',
    userName: 'User name',
    company: 'Company',
    memberSince: 'Member since',
    userNameFixed:
      'Your user name cannot be changed. An administrator can reset your password from Settings › Users.',
    samePassword: 'Must differ from current.',
    passwordHint:
      'Use at least 12 characters. A long phrase is easier to remember and harder to guess.',
    passwordChangedSignIn: 'Password changed. Sign in again.',
    passwordSignsOut: 'Signs you out everywhere.',
    showPasswords: 'Show passwords',
    strength: {
      1: 'Weak',
      2: 'Fair',
      3: 'Good',
      4: 'Strong',
    },
    whereSignedIn: 'Sessions',
    whereSignedInHint:
      'Sign out of a device you no longer use, or one you don’t recognise.',
    on: ' on ',
    unknownDevice: 'Unknown device',
    thisDevice: 'This device',
    sessionTimes: 'In {{signedIn}} · active {{active}}',
    noSessions: 'No other sign-ins.',
    sessionEnded: 'Signed out.',
    othersEnded_one: 'Signed out {{count}} device.',
    othersEnded_other: 'Signed out {{count}} devices.',
    signOutOthers_one: 'Sign out other device',
    signOutOthers_other: 'Sign out others',
    light: 'Light',
    dark: 'Dark',
    themeHint: 'How the application looks on this device.',
    languageHint: 'Arabic turns the whole screen right-to-left.',
    yourRoles: 'Your roles',
    rolesHint: 'Roles are given by an administrator in Settings › Users.',
    fullAccess: 'Full access',
    whatYouCanDo: 'What you can do',
    everything: 'Full access.',
    nothingGranted: 'No permissions.',
  },
  changePassword: {
    title: 'Change password',
    subtitle: 'Choose a strong, unique password',
    current: 'Current password',
    new: 'New password',
    confirm: 'Confirm new password',
    policy: 'At least 12 characters',
    mismatch: 'Passwords differ.',
    unreachable: 'Server unreachable.',
    submit: 'Update password',
    working: 'Updating…',
    success: 'Password updated.',
  },
  users: {
    title: 'Users',
    hint: 'Who may sign in, and with which roles. A new user’s starting password has to be changed at their first sign-in. What a role may do and which menus it shows are set under Roles and menus.',
    new: 'New user',
    editTitle: 'Edit {{name}}',
    userName: 'User name',
    displayName: 'Name',
    email: 'Email',
    mobile: 'Mobile',
    password: 'Starting password',
    newPassword: 'New starting password',
    passwordHint: 'Min 12 characters',
    roles: 'Roles',
    status: 'Status',
    active: 'May sign in',
    lastLogin: 'Last signed in',
    mustChange: 'must change password',
    lockedOut: 'locked out',
    resetPassword: 'Reset password',
    resetTitle: 'Reset password: {{name}}',
    createdNotice: '{{name}} created.',
    updatedNotice: '{{name}} saved.',
    resetNotice: '{{name}} password reset.',
  },
  roles: {
    title: 'Roles and menus',
    hint: 'A role decides which menus its users see and what they may do. A user with several roles gets everything any of them gives.',
    new: 'New role',
    choose: 'Select or create a role.',
    userCount_one: '{{count}} user',
    userCount_other: '{{count}} users',
    everything: 'everything',
    everythingHint: 'Full access; only the name can change.',
    name: 'Role name',
    description: 'Description',
    menus: 'Menus',
    allMenus: 'All permitted menus',
    menusHint:
      'Tick the screens this role should see in the menu. Ticking a screen also allows opening it.',
    permissions: 'Permissions',
    permissionsHint:
      'View lets them read; create, edit and delete let them enter, correct and cancel; approve, print and export are as named.',
    resource: 'Area',
    delete: 'Delete role',
    savedNotice: 'Role {{name}} saved.',
    deletedNotice: 'Role {{name}} deleted.',
    verbs: {
      view: 'View',
      create: 'Create',
      edit: 'Edit',
      delete: 'Delete',
      approve: 'Approve',
      print: 'Print',
      export: 'Export',
    },
    modules: {
      platform: 'Platform',
      accounting: 'Accounting',
      inventory: 'Inventory',
      sales: 'Sales',
      purchase: 'Purchase',
      manufacturing: 'Manufacturing',
      service: 'Service',
      reporting: 'Reports and dashboards',
      printing: 'Printing',
      workflow: 'Workflow',
    },
  },
  dataReset: {
    title: 'Clear data',
    warning: 'Cannot be undone. Back up first.',
    transactionsTitle: 'Clear all transactions',
    transactionsBody: 'Deletes all transactions and resets numbering.',
    everythingTitle: 'Clear everything',
    everythingBody: 'Also deletes customers, suppliers, products and other masters.',
    clearTransactions: 'Clear transactions',
    clearEverything: 'Clear everything',
    confirmHint: 'Type {{name}} to confirm.',
    confirmLabel: 'Firm name',
    confirm: 'Clear now',
    clearing: 'Clearing…',
    transactionsDone: 'Transactions cleared.',
    everythingDone: 'Everything cleared.',
    nothingRemoved: 'Nothing to remove.',
    kinds: {
      salesInvoices: 'Sales & returns',
      purchaseInvoices: 'Purchases and returns',
      orders: 'Orders',
      stockDocuments: 'Stock documents',
      vouchers: 'Vouchers',
      cheques: 'Cheques',
      products: 'Products',
      parties: 'Parties and ledgers',
      warehouses: 'Warehouses',
      categories: 'Categories',
      units: 'Units',
    },
  },
  importData: {
    title: 'Import data',
    subtitle: 'From EasyRetail',
    intro:
      'Brings a firm across from EasyRetail: account groups and ledgers, customers, suppliers and employees, categories, brands, units and warehouses, products with their barcodes, every ledger’s balance as at a date, and the stock on hand as opening stock. Past invoices and vouchers are not brought across; the balances carry their effect.',
    again:
      'Importing again is safe. Whatever came across before is recognised and left alone, so an import that stopped part way is finished by running it again.',
    sourceLabel: 'Source',
    sourceFile: 'Export file',
    sourceDatabase: 'Database',
    fileStep1: 'Download the exporter.',
    fileStep2: 'Run it on the EasyRetail PC.',
    exporterWarning: 'If Windows warns: More info › Run anyway.',
    fileStep3: 'In its window, connect, pick the database and click Export.',
    fileStep4: 'Choose easyretail-export.json from the Desktop.',
    downloadExporter: 'Download exporter (.exe)',
    downloading: 'Downloading…',
    chooseFile: 'Choose export file',
    reading: 'Reading…',
    notAnExport: 'Not an EasyRetail export.',
    unreadableFile: 'Unreadable file.',
    databaseHint:
      'For when this server can reach the shop’s SQL Server, on the same network or over a VPN. Otherwise use an export file.',
    server: 'SQL Server',
    database: 'Database',
    userName: 'User',
    password: 'Password',
    asAt: 'Balances as at',
    userHint: 'Blank user = Windows sign-in',
    readDatabase: 'Read database',
    asAtLine: 'Balances as at {{date}} · exported {{exported}}',
    changeSource: 'Use another source',
    mastersTitle: 'Masters',
    counts: {
      groups: 'Account groups',
      ledgers: 'Ledgers',
      customers: 'Customers',
      suppliers: 'Suppliers',
      employees: 'Employees',
      categories: 'Categories',
      subClasses: 'Sub-classes',
      brands: 'Brands',
      products: 'Products',
      barcodes: 'Barcodes',
    },
    balancesTitle: 'Balances',
    debit: 'Debit',
    credit: 'Credit',
    difference: 'Difference',
    differenceNote: 'Difference {{amount}} put on the opening difference ledger.',
    stockTitle: 'Stock on hand',
    stockLines: 'Lines',
    stockValue: 'Value at cost',
    negativeStock: 'Zero or below: skipped',
    optionsTitle: 'What to import',
    optionMasters: 'Masters (always)',
    optionProducts: 'Products and barcodes',
    optionBalances: 'Balances at {{date}}',
    optionCarry: 'Carry P&L to equity',
    optionStock: 'Stock at {{date}}',
    optionsHint:
      'Balances replace the opening balance of every imported ledger. Records already in this firm with the same code are matched rather than duplicated.',
    start: 'Import now',
    runAgain: 'Run again',
    stop: 'Stop',
    stopping: 'Stopping…',
    progressTitle: 'Progress',
    finished: 'Import finished.',
    failed: 'Import failed. Fix and run again.',
    stopped: 'Stopped. Run again to resume.',
    openingSet: 'Opening balances set',
    profitCarried: 'Profit to equity',
    differenceCarried: 'Opening difference',
    check: 'Check:',
    links: {
      trialBalance: 'Trial balance',
      ledgers: 'Chart of accounts',
      customers: 'Customers',
      suppliers: 'Suppliers',
      products: 'Products',
      valuation: 'Stock valuation',
    },
    steps: {
      masters: 'Masters',
      products: 'Products',
      balances: 'Balances',
      stock: 'Opening stock',
    },
    status: {
      waiting: 'Waiting',
      running: 'Running',
      done: 'Done',
      failed: 'Failed',
      stopped: 'Stopped',
      off: 'Not selected',
    },
    batch: '{{done}} of {{total}}',
    tally: {
      created: '{{value}} created',
      balancesSet: '{{value}} balances',
      matched: '{{value}} matched',
      alreadyImported: '{{value}} existing',
      skipped: '{{value}} left out',
      nothing: 'Nothing to do',
    },
    notes_one: '{{count}} note',
    notes_other: '{{count}} notes',
  },
  printing: {
    print: 'Print',
    template: 'Template',
    copies: 'Copies',
    alsoPrint: 'Also {{copies}} on {{template}}',
    choosePrinter: 'Printer: {{printer}}',
    settingsTitle: 'Print settings',
    settingsHint:
      'How each document prints, separately for tax and non-tax: preview first or print straight away, the template, the printer it is meant for, how many copies, and an optional second print on another printer and template.',
    printerHint:
      'A browser chooses the actual printer in its own print dialog and cannot pick one by itself. The printer named here is shown when printing, so the right one is chosen; making it the computer’s default printer saves even that step.',
    savedNotice: 'Saved.',
    document: 'Document',
    preview: 'Preview',
    printer: 'Printer',
    printerPlaceholder: 'e.g. Counter thermal',
    secondPrint: 'Second print',
    copyLabels: 'Copy labels',
    bilingual: 'Arabic + English',
    bilingualHint:
      'Each line of a document prints twice: the Arabic on one line and the English on the next - invoices, quotations, purchases, receipts and every other print. Changeable for any one print.',
    bilingualShort: 'Arabic + English',
    movedHint: 'Printing is now set up per document and tax mode, with designed layouts:',
    followSettings: 'As in print settings',
    templatesTitle: 'Print templates',
    templatesHint:
      'Design the layouts documents print on: your company details and logo, which parts appear, the columns, terms and signatures, the paper and the style. The preview is exactly what prints.',
    templatesSaved: 'Templates saved.',
    newTemplate: 'New template',
    duplicate: 'Duplicate',
    shareCompany: 'Apply to all templates',
    companyShared: 'Copied. Save to keep.',
    deleteTemplate: 'Delete',
    copyOf: 'Copy of {{name}}',
    previewAs: 'Preview as',
    general: 'General',
    templateName: 'Template name',
    paper: 'Paper',
    titleOverride: 'Title',
    company: 'Company',
    companyName: 'Company name',
    companyNameArabic: 'Company name in Arabic',
    companyDetails: 'Address & contact',
    taxNumber: 'Tax number',
    logo: 'Logo',
    logoUnreadable: 'Invalid image.',
    removeLogo: 'Remove',
    showSection: 'Show',
    columnsSection: 'Item columns',
    footerSection: 'Footer',
    termsLabel: 'Terms and conditions',
    note: 'Footer note',
    signatureLeft: 'Left signature line',
    signatureRight: 'Right signature line',
    styleSection: 'Style',
    fontSize: 'Type size',
    accent: 'Colour',
    borders: 'Rules and borders',
    number: 'No',
    date: 'Date',
    reference: 'Ref',
    warehouse: 'Warehouse',
    item: 'Item',
    quantity: 'Qty',
    unit: 'Unit',
    rate: 'Rate',
    discount: 'Disc',
    taxPercent: 'Tax %',
    tax: 'Tax',
    amount: 'Amount',
    serials: 'Serial / IMEI',
    account: 'Account',
    debit: 'Debit',
    credit: 'Credit',
    inWords: 'In words',
    narration: 'Note',
    total: 'Total',
    terms: 'Terms and conditions',
    taxable: 'Taxable value',
    taxTotal: 'Tax',
    charges: 'Charges',
    roundOff: 'Round off',
    documents: {
      salesInvoice: 'Sales invoice',
      salesReturn: 'Sales return',
      salesQuotation: 'Sales quotation',
      salesOrder: 'Sales order',
      purchaseInvoice: 'Purchase',
      purchaseReturn: 'Purchase return',
      receipt: 'Receipt',
      payment: 'Payment',
      journal: 'Journal',
    },
    modes: { tax: 'Tax', nonTax: 'Non-tax' },
    papers: { A4: 'A4', A5: 'A5', '80mm': 'Till roll 80 mm', '58mm': 'Till roll 58 mm' },
    fontScales: { small: 'Small', normal: 'Normal', large: 'Large' },
    show: {
      arabicTitle: 'Arabic title',
      partyAddress: 'Party address',
      partyTaxNumber: 'Party tax no.',
      warehouse: 'Warehouse',
      reference: 'Reference',
      narration: 'Narration',
      productCode: 'Product code',
      serials: 'Serial / IMEI numbers',
      amountInWords: 'Amount in words',
      signatures: 'Signature lines',
    },
    columns: {
      unit: 'Unit',
      rate: 'Rate',
      discount: 'Discount',
      taxPercent: 'Tax %',
      tax: 'Tax amount',
    },
    titles: {
      salesInvoice: { tax: 'Tax invoice', nonTax: 'Invoice' },
      salesReturn: { tax: 'Tax credit note', nonTax: 'Credit note' },
      salesQuotation: { tax: 'Quotation', nonTax: 'Quotation' },
      salesOrder: { tax: 'Sales order', nonTax: 'Sales order' },
      purchaseInvoice: { tax: 'Purchase invoice', nonTax: 'Purchase invoice' },
      purchaseReturn: { tax: 'Debit note', nonTax: 'Debit note' },
      receipt: { tax: 'Receipt voucher', nonTax: 'Receipt voucher' },
      payment: { tax: 'Payment voucher', nonTax: 'Payment voucher' },
      journal: { tax: 'Journal voucher', nonTax: 'Journal voucher' },
    },
    titlesArabic: {
      salesInvoice: { tax: 'فاتورة ضريبية', nonTax: 'فاتورة' },
      salesReturn: { tax: 'إشعار دائن ضريبي', nonTax: 'إشعار دائن' },
      salesQuotation: { tax: 'عرض سعر', nonTax: 'عرض سعر' },
      salesOrder: { tax: 'أمر بيع', nonTax: 'أمر بيع' },
      purchaseInvoice: { tax: 'فاتورة مشتريات', nonTax: 'فاتورة مشتريات' },
      purchaseReturn: { tax: 'إشعار مدين', nonTax: 'إشعار مدين' },
      receipt: { tax: 'سند قبض', nonTax: 'سند قبض' },
      payment: { tax: 'سند صرف', nonTax: 'سند صرف' },
      journal: { tax: 'سند قيد', nonTax: 'سند قيد' },
    },
    party: {
      billTo: 'Bill to',
      supplier: 'Supplier',
      receivedFrom: 'Received from',
      paidTo: 'Paid to',
    },
    watermark: { draft: 'Draft', cancelled: 'Cancelled' },
    copy: { '1': 'Original', '2': 'Duplicate', '3': 'Triplicate', '4': 'Copy' },
    sample: { narration: 'Delivered to the customer’s shop.' },
  },
  common: {
    retry: 'Try again',
    dismiss: 'Dismiss',
    yes: 'Yes',
    no: 'No',
    loading: 'Loading…',
    theme: 'Theme',
    language: 'Language',
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    edit: 'Edit',
    choose: 'Search or choose…',
    none: '— none —',
    noMatches: 'Nothing matches that.',
    moreMatches: '{{count}} more',
    required: 'Required.',
    notANumber: 'Enter a number.',
    mustChangePassword: 'Change your password to continue.',
    remove: 'Remove',
    expandAll: 'Expand All',
    collapseAll: 'Collapse All',
  },
} as const;

const ar = {
  app: { name: brand.appNameArabic },
  signIn: {
    title: 'تسجيل الدخول',
    subtitle: 'سجّل الدخول للمتابعة',
    company: 'رمز الشركة',
    companyHint: 'اتركه فارغاً إذا كان النظام يحتوي على شركة واحدة',
    userName: 'اسم المستخدم أو البريد الإلكتروني',
    password: 'كلمة المرور',
    submit: 'تسجيل الدخول',
    working: 'جارٍ تسجيل الدخول…',
    passwordChanged: 'تم تغيير كلمة المرور. سجّل الدخول مجدداً.',
  },
  nav: {
    productHistory: 'سجل المنتج',
    stockRegister: 'سجل المخزون',
    stockAging: 'أعمار المخزون',
    stockSummary: 'ملخص المخزون',
    salesReport: 'تقرير المبيعات',
    purchaseReport: 'تقرير المشتريات',
    profitReport: 'تقرير الأرباح',
    receivables: 'المبالغ المستحقة لنا',
    payables: 'المبالغ المستحقة علينا',
    dashboard: 'لوحة المعلومات',
    accounting: 'المحاسبة',
    voucherEntry: 'إدخال قيد',
    ledgers: 'دليل الحسابات',
    sales: 'فواتير المبيعات',
    customers: 'العملاء',
    purchase: 'المشتريات',
    purchaseReturns: 'مرتجعات المشتريات',
    purchaseOrders: 'أوامر الشراء',
    salesOrders: 'أوامر البيع',
    salesQuotations: 'عروض الأسعار',
    suppliers: 'الموردون',
    payments: 'سندات الصرف',
    receipts: 'سندات القبض',
    settings: 'الإعدادات',
    openingStock: 'المخزون الافتتاحي',
    taxReturns: 'الإقرارات الضريبية',
    products: 'المنتجات',
    stockOperations: 'حركات المخزون',
    stockTransfers: 'تحويلات المخزون',
    stockAdjustment: 'تسوية المخزون',
    damagedStock: 'المخزون التالف',
    deliveryNotes: 'إشعارات التسليم',
    boms: 'قوائم المواد',
    production: 'الإنتاج',
    service: 'الصيانة',
    jobCards: 'بطاقات العمل',
    rmas: 'المرتجعات (RMA)',
    auditLog: 'سجل التدقيق',
    approvals: 'الموافقات',
    receiptNotes: 'إشعارات الاستلام',
    stockValuation: 'تقييم المخزون',
    stockLedger: 'دفتر المخزون',
    itemMovement: 'حركة الأصناف',
    batchStock: 'المخزون حسب التشغيلة',
    batchProfit: 'الربح حسب الدفعة',
    expiryReport: 'تقرير الصلاحية',
    serialsRegister: 'الأرقام التسلسلية',
    units: 'وحدات القياس',
    categories: 'الفئات',
    brands: 'العلامات التجارية',
    warehouses: 'المستودعات',
    trialBalance: 'ميزان المراجعة',
    accountGroupSummary: 'ملخص المجموعات',
    profitAndLoss: 'الأرباح والخسائر',
    balanceSheet: 'الميزانية العمومية',
    dayBook: 'دفتر اليومية',
    voucherReport: 'تقرير القيود',
    transactionSummary: 'ملخص الحركات',
    menuUnavailable: 'القائمة غير متاحة.',
    menuAdministration: 'إعدادات القائمة',
    cashBook: 'دفتر الصندوق',
    bankBook: 'دفتر البنك',
    cashFlow: 'التدفقات النقدية',
    postDatedCheques: 'الشيكات المؤجلة',
    chequeCalendar: 'تقويم الشيكات',
    chequeRegister: 'سجل الشيكات',
    signOut: 'تسجيل الخروج',
    menu: 'القائمة',
    openMenu: 'فتح القائمة',
    closeMenu: 'إغلاق القائمة',
    collapseSidebar: 'طي',
    expandSidebar: 'توسيع',
    skipToContent: 'تخطٍّ إلى المحتوى',
  },
  invoice: {
    customerDetails: 'بيانات العميل',
    total: 'الإجمالي',
    roundOff: 'التقريب',
    grandTotal: 'الإجمالي الكلي',
    hold: 'تعليق',
    menuHolds: 'القائمة — تُعلَّق الفاتورة',
    supplierDetails: 'بيانات المورد',
    orderDetails: 'بيانات الطلب',
    quotationDetails: 'بيانات عرض السعر',
    items: 'الأصناف',
    itemCount_one: 'صنف واحد',
    itemCount_other: '{{count}} أصناف',
    addItem: 'إضافة صنف',
    itemsTotal: 'إجمالي الأصناف',
    itemsQty: '{{count}} · الكمية {{qty}}',
    linesTotal: 'الإجمالي',
    ratesAndCharges: 'الأسعار والرسوم',
    creditTerms: '{{limit}} · {{days}} يوم',
    noCustomer: 'لم يتم اختيار عميل بعد (F2).',
    noSupplier: 'لم يتم اختيار مورد بعد.',
    chooseCustomer: 'اختر العميل',
    chooseSupplier: 'اختر المورد',
    emptyLinesTitle: 'امسح باركود أو اختر صنفًا للبدء.',
    emptyLinesScan: 'مسح',
    emptyLinesAdd: 'إضافة صنف',
    emptyLinesEnter: 'الحقل التالي',
    lineNumber: 'السطر',
    discardTitle: 'تجاهل هذه الفاتورة؟',
    discardBody: 'ستفقد ما أدخلته.',
    keepEditing: 'متابعة التحرير',
    discard: 'تجاهل',
    scanLabel: 'مسح الباركود',
    removeItem: 'حذف الصنف',
    narration: 'البيان / الملاحظات',
    bill: 'الفاتورة',
    billTotal: 'إجمالي الفاتورة',
    returnTotal: 'إجمالي المرتجع',
    orderTotal: 'إجمالي الطلب',
    quotationTotal: 'إجمالي عرض السعر',
    paymentDetails: 'بيانات الدفع',
    summary: 'الملخص',
    customerAccount: 'حساب العميل',
    supplierAccount: 'حساب المورد',
    newBalance: 'الرصيد الجديد',
    newSales: 'إنشاء فاتورة مبيعات جديدة',
    newSalesReturn: 'استلام بضاعة أعادها العميل',
    newPurchase: 'تسجيل فاتورة مورد',
    newPurchaseReturn: 'إرجاع بضاعة إلى المورد',
    newPurchaseOrder: 'طلب بضاعة من مورد',
    newSalesOrder: 'استلام طلب من عميل',
    newQuotation: 'عرض أسعار على عميل',
    editing: 'تعديل {{number}}',
  },
  palette: {
    title: 'الانتقال إلى شاشة',
    open: 'البحث في القائمة',
    shortcut: 'Ctrl K',
    searchModule: 'البحث في النظام…',
    placeholder: 'اكتب اسم الشاشة…',
    nothing: 'لا نتائج.',
    more: 'و{{count}} أخرى',
    hint: '↑↓ تنقل · ↵ فتح · Esc إغلاق',
  },
  reports: {
    from: 'من',
    to: 'إلى',
    asAt: 'كما في',
    asAtDate: 'كما في {{date}}',
    dr: 'مدين',
    cr: 'دائن',
    filters: 'عوامل التصفية',
    run: 'تشغيل',
    print: 'طباعة',
    running: 'جارٍ التشغيل…',
    ledger: 'الحساب',
    group: 'المجموعة',
    openingDebit: 'مدين افتتاحي',
    openingCredit: 'دائن افتتاحي',
    periodDebit: 'مدين الفترة',
    periodCredit: 'دائن الفترة',
    closingDebit: 'مدين ختامي',
    closingCredit: 'دائن ختامي',
    totals: 'المجاميع',
    balanced: 'المدين يساوي الدائن',
    notBalanced: 'غير متوازن',
    noData: 'لا قيود.',
    income: 'الإيرادات',
    expenses: 'المصروفات',
    totalIncome: 'إجمالي الإيرادات',
    totalExpenses: 'إجمالي المصروفات',
    netProfit: 'صافي الربح',
    netLoss: 'صافي الخسارة',
    assets: 'الأصول',
    liabilities: 'الالتزامات',
    equity: 'حقوق الملكية',
    retainedEarnings: 'الأرباح المحتجزة',
    totalAssets: 'إجمالي الأصول',
    totalLiabilities: 'إجمالي الالتزامات',
    totalEquity: 'إجمالي حقوق الملكية',
    totalLiabilitiesAndEquity: 'الالتزامات وحقوق الملكية',
    date: 'التاريخ',
    voucherNo: 'رقم القيد',
    particulars: 'البيان',
    debit: 'مدين',
    credit: 'دائن',
    voucherType: 'نوع القيد',
    allTypes: 'كل الأنواع',
    status: 'الحالة',
    allStatuses: 'كل الحالات',
    reference: 'المرجع',
    amount: 'المبلغ',
    count: 'العدد',
    byType: 'حسب نوع القيد',
    byMonth: 'حسب الشهر',
    cashIn: 'النقد الوارد',
    cashOut: 'النقد الصادر',
    netChange: 'صافي التغير في النقد',
    noMovement: 'لا توجد حركة',
    cashReconciled: 'النقد متطابق',
    cashNotReconciled: 'غير مطابق',
    // Arabic has dual and plural forms beyond one/other, which i18next selects
    // by the same suffix convention. Providing them all keeps the count reading
    // naturally instead of forcing the English two-form shape onto it.
    voucherCount_zero: 'لا توجد قيود',
    voucherCount_one: 'قيد واحد',
    voucherCount_two: 'قيدان',
    voucherCount_few: '{{count}} قيود',
    voucherCount_many: '{{count}} قيدًا',
    voucherCount_other: '{{count}} قيد',
    receipts: 'المقبوضات',
    payments: 'المدفوعات',
    balance: 'الرصيد',
    openingBalance: 'الرصيد الافتتاحي',
    closingBalance: 'الرصيد الختامي',
    allAccounts: 'كل الحسابات',
    includeLedgers: 'إظهار الحسابات',
    includeZeroBalances: 'تضمين الأرصدة الصفرية',
    hierarchicalTree: 'الشجرة الهرمية',
    hierarchicalView: 'الشجرة الهرمية',
    facingColumns: 'الأعمدة المتقابلة',
    type: 'النوع',
    classicView: 'العرض الكلاسيكي',
    flatList: 'جدول مسطح',
    accountsView: 'بطاقات الحسابات',
    hierarchyLevels: 'المستويات',
    heads: 'الأصول والفروع',
    groups: 'المجموعات',
    ledgers: 'الحسابات',
    transactions: 'التفاصيل',
    accounts: 'الحسابات',
    searchAccounts: 'بحث في الحسابات…',
    accountHierarchy: 'شجرة الحسابات',
    transactionDetails: 'القيود',
    entries: 'حركات',
    noTransactionsPeriod: 'لا قيود في هذه الفترة.',
    subtotal: 'المجموع الفرعي',
    totalHead: 'إجمالي',
    grandTotal: 'المجموع الإجمالي العام',
    balancedBooks: 'الدفاتر متوازنة',
    chronologicalView: 'السجل الزمني للقيود',
  },
  voucherTypes: {
    CashReceipt: 'سند قبض نقدي',
    BankReceipt: 'سند قبض بنكي',
    CashPayment: 'سند صرف نقدي',
    BankPayment: 'سند صرف بنكي',
    Journal: 'قيد يومية',
    Contra: 'قيد تحويل',
    OpeningBalance: 'رصيد افتتاحي',
  },
  vouchers: {
    noPendingBills: 'لا توجد فواتير معلقة.',
    pendingBills: '{{count}} فواتير · {{total}} مستحق',
    billNumber: 'الفاتورة',
    billDate: 'التاريخ',
    billDue: 'الاستحقاق',
    billOutstanding: 'المستحق',
    billAllocated: 'المخصص',
    settleBill: 'تسوية الفاتورة {{number}}',
    entryTitle: 'إدخال قيد',
    entryHint: 'لكل مدين دائن مقابل. رقم القيد يصدر من تسلسل الفرع عند الترحيل.',
    type: 'نوع القيد',
    date: 'التاريخ',
    party: 'العميل / المورد',
    partyHint: 'يملأ السطر الذي يخص هذا القيد.',
    noParty: 'بدون',
    reference: 'المرجع / رقم الفاتورة',
    narration: 'البيان',
    lineNarration: 'بيان السطر',
    ledger: 'الحساب',
    side: 'مدين / دائن',
    debitAmount: 'المبلغ المدين',
    creditAmount: 'المبلغ الدائن',
    removeLine: 'حذف السطر',
    addRow: '+ إضافة سطر',
    total: 'الإجمالي',
    balanced: 'متوازن',
    nothingEntered: 'لم يُدخل شيء بعد',
    differenceDebit: 'فرق {{amount}} (مدين)',
    differenceCredit: 'فرق {{amount}} (دائن)',
    post: 'ترحيل القيد',
    posting: 'جارٍ الترحيل…',
    postedNotice: 'تم ترحيل {{number}} بمبلغ {{total}}.',
    postedAndListed: 'تم ترحيل {{number}}.',
    enterAnother: 'إدخال سند آخر',
    paymentTitle: 'سند صرف',
    paymentHint: 'أموال صادرة: سداد مورد، مصروف، أو سلفة.',
    receiptTitle: 'سند قبض',
    receiptHint: 'أموال واردة: تحصيل من عميل، أو مبلغ مستلم.',
    through: 'عن طريق',
    throughCash: 'نقداً',
    throughBank: 'بنك',
    amount: 'المبلغ',
    paidTo: 'مدفوع إلى',
    receivedFrom: 'مستلم من',
    cashAccount: 'حساب الصندوق',
    bankAccount: 'الحساب البنكي',
    paymentMode: 'طريقة الدفع',
    paymentModeNone: 'غير محدد',
    postPayment: 'ترحيل سند الصرف',
    postReceipt: 'ترحيل سند القبض',
    paymentEffect:
      'يجعل الطرف مديناً والصندوق أو البنك دائناً: تقل التزامات المنشأة ويقل ما بحوزتها من نقد.',
    receiptEffect:
      'يجعل الصندوق أو البنك مديناً والطرف دائناً: يزيد نقد المنشأة ويقل ما على الطرف.',
    dateRequired: 'التاريخ مطلوب.',
    partyRequired: 'الطرف مطلوب.',
    cashRequired: 'حدد حساب الصندوق.',
    bankRequired: 'حدد الحساب البنكي.',
    amountRequired: 'المبلغ مطلوب.',
    amountInvalid: 'المبلغ أكبر من صفر.',
    transactionNo: 'رقم المعاملة',
    referenceNo: 'الرقم المرجعي',
    referenceNoHint: 'يُملأ برقم المعاملة تلقائياً إذا تُرك فارغاً',
    debitAccount: 'حساب المدين',
    debitAccountHint: 'حساب المورد أو الطرف أو المصروف المدين',
    creditAccount: 'حساب الدائن',
    creditAccountHint: 'حساب العميل أو الطرف أو الإيراد الدائن',
    multiplePayments: 'طرق الدفع والقيود',
    multiplePaymentsHint: 'إضافة طريقة دفع أو أكثر لهذه المعاملة',
    addPaymentEntry: 'إضافة قيد دفع',
    paymentType: 'نوع الدفع',
    account: 'الحساب',
    chequeOrRef: 'رقم الشيك / المرجع',
    totalPaymentAmount: 'إجمالي المبلغ',
    salesman: 'المندوب',
    primarySalesman: 'المندوب الرئيسي',
    cash: 'نقداً',
    credit: 'آجل',
    bank: 'بنك',
    card: 'بطاقة',
    cheque: 'شيك',
    autoGenerated: 'توليد تلقائي',
    missingAccount: 'اختر حساباً.',
    actions: 'إجراءات',
    edit: 'تعديل',
    editTitle: 'تعديل {{number}}',
    editPostedHint: 'مُرحّل: الحفظ يعيد ترحيله بالرقم نفسه.',
    editReason: 'السبب',
    saveChanges: 'حفظ التعديلات',
    journalTitle: 'قيد يومية',
    postJournal: 'ترحيل القيد',
    updatedNotice: 'تم تحديث {{number}}.',
    cancelVoucher: 'إلغاء السند',
    cancelTitle: 'إلغاء {{number}}',
    cancelHint: 'يلغيه ويعكس قيوده.',
    cancelReason: 'السبب',
    cancelledNotice: 'تم إلغاء {{number}}.',
    journalDetails: 'تفاصيل القيد',
    journalDetailsHint: 'سطر لكل حساب. أدخل المبلغ في المدين أو الدائن.',
    addLedger: 'إضافة حساب',
    ledgerRequired: 'اختر حساباً في كل سطر.',
    paidFrom: 'الدفع من',
    receivedInto: 'القبض إلى',
    groupCash: 'نقد',
    groupBank: 'بنك',
    moneyAccountRequired: 'اختر النقد أو البنك.',
    chequeDetails: 'تفاصيل الشيك',
    chequeNumber: 'رقم الشيك',
    chequeAccountNumber: 'رقم الحساب',
    chequeDate: 'تاريخ الشيك',
    inFavourOf: 'لصالح',
    chequeNumberRequired: 'رقم الشيك مطلوب.',
    chequeNeedsBank: 'الشيك يحتاج حساباً بنكياً.',
    cardDetails: 'بيانات البطاقة',
    cardType: 'نوع البطاقة',
    cardLastDigits: 'آخر أربعة أرقام',
    cardApproval: 'رمز الموافقة',
    paymentDetails: 'تفاصيل الدفع',
    paymentDetailsHint: 'كل حساب يشمله هذا الدفع مع المبلغ المدفوع له.',
    receiptDetails: 'تفاصيل القبض',
    receiptDetailsHint: 'كل حساب يشمله هذا القبض مع المبلغ المقبوض منه.',
  },
  voucherStatus: {
    Draft: 'مسودة',
    Posted: 'مرحّل',
    Cancelled: 'ملغى',
  },
  lineDetails: {
    code: 'الرمز',
    unit: 'الوحدة',
    retail: 'التجزئة',
    wholesale: 'الجملة',
    mrp: 'السعر المطبوع',
    fromMaster: 'من بيانات المنتج. يُعدَّل من بطاقة المنتج.',
  },
  serials: {
    title: 'الأرقام — {{product}}',
    summary: 'الأرقام: {{chosen}}/{{most}}',
    choose: 'اختيار الأرقام',
    change: 'تغيير',
    hintOut:
      'حدّد الوحدات الصادرة، أو امسح رقماً لتحديده. اختياري: كلها أو بعضها أو لا شيء.',
    hintIn:
      'امسح أو اكتب كل رقم ثم اضغط Enter. اختياري: كل الوحدات الواردة أو بعضها أو لا شيء.',
    scanPlaceholder: 'امسح الرقم / IMEI',
    add: 'إضافة',
    remove: 'إزالة {{number}}',
    alreadyChosen: '{{number}} محدد مسبقاً.',
    notOnShelf: '{{number}} غير متوفر هنا.',
    notSold: '{{number}} لم يُبع.',
    onShelf: 'في المخزن',
    soldUnits: 'الوحدات المباعة',
    noneOnShelf: 'لا أرقام في المخزون.',
    noneSold: 'لا أرقام مباعة.',
    filter: 'تصفية',
    warrantyTo: 'الضمان حتى {{date}}',
    pickedOf: 'اختير {{chosen}}/{{quantity}}',
    done: 'تم',
    scanFound: '{{number}} — {{product}}',
  },
  numbering: {
    title: 'ترقيم المستندات',
    hint: 'كيف يُرقَّم كل مستند كما يصدره الخادم. أي تغيير هنا يظهر في المستند التالي؛ الأرقام الصادرة لا تتغير، ويمكن تقديم العدّاد لا إرجاعه.',
    document: 'المستند',
    prefix: 'البادئة',
    separator: 'الفاصل',
    year: 'السنة',
    suffix: 'اللاحقة',
    nextNumber: 'الرقم التالي',
    preview: 'المستند التالي',
    default: 'افتراضي',
    issuedOnSave: 'يصدر عند الحفظ',
    types: {
      'sales.invoice': 'فاتورة مبيعات',
      'sales.return': 'مرتجع مبيعات',
      'sales.order': 'أمر بيع',
      'sales.quotation': 'عرض سعر',
      'purchase.invoice': 'فاتورة مشتريات',
      'purchase.return': 'مرتجع مشتريات',
      'purchase.order': 'أمر شراء',
      'accounting.cash-receipt': 'سند قبض نقدي',
      'accounting.bank-receipt': 'سند قبض بنكي',
      'accounting.cash-payment': 'سند صرف نقدي',
      'accounting.bank-payment': 'سند صرف بنكي',
      'accounting.journal': 'قيد يومية',
    },
  },
  paymentModes: {
    cash: 'نقداً',
    cheque: 'شيك',
    bankTransfer: 'تحويل بنكي',
    card: 'بطاقة',
    online: 'دفع إلكتروني',
    credit: 'آجل',
  },
  cashFlow: {
    Operating: 'الأنشطة التشغيلية',
    Investing: 'الأنشطة الاستثمارية',
    Financing: 'الأنشطة التمويلية',
  },
  menuAdmin: {
    entry: 'العنصر',
    route: 'المسار',
    permission: 'يتطلب',
    actions: 'الإجراءات',
    heading: 'عنوان',
    everyone: 'الجميع',
    system: 'نظامي',
    systemCannotDelete: 'عنصر نظام: أخفِه بدلاً من الحذف.',
    moveUp: 'تحريك لأعلى',
    moveDown: 'تحريك لأسفل',
    show: 'إظهار',
    hide: 'إخفاء',
    rename: 'إعادة تسمية',
    renamePrompt: 'الاسم الجديد',
    delete: 'حذف',
    code: 'الرمز',
    label: 'التسمية',
    routeOptional: 'المسار (اختياري)',
    add: 'إضافة عنصر',
  },
  cheques: {
    direction: {
      label: 'النوع',
      Received: 'وارد',
      Issued: 'صادر',
    },
    status: {
      label: 'الحالة',
      Pending: 'قيد الانتظار',
      Deposited: 'مودعة',
      Cleared: 'محصّلة',
      Bounced: 'مرتجعة',
      Stopped: 'موقوفة',
      Cancelled: 'ملغاة',
    },
    allDirections: 'الكل',
    allStatuses: 'كل الحالات',
    chequeNo: 'رقم الشيك',
    party: 'الطرف',
    dueDate: 'تاريخ الاستحقاق',
    instrumentDate: 'تاريخ الشيك',
    daysToRun: 'الأيام',
    dueNow: 'مستحق',
    bank: 'البنك',
    amount: 'المبلغ',
    recordedOn: 'تاريخ التسجيل',
    receivable: 'مستحق القبض',
    payable: 'مستحق الدفع',
    net: 'الصافي',
    totalReceivable: 'إجمالي مستحق القبض',
    totalPayable: 'إجمالي مستحق الدفع',
    totalReceived: 'إجمالي الوارد',
    totalIssued: 'إجمالي الصادر',
    countByStatus: 'حسب الحالة',
    multiCurrency: 'الإجماليات بعملات متعددة.',
    noPostDated: 'لا شيكات مؤجلة.',
    noDue: 'لا شيكات مستحقة.',
    noCheques: 'لا شيكات.',
  },
  ledgers: {
    name: 'الاسم',
    kind: 'النوع',
    groupCode: 'رمز المجموعة',
    nature: 'الطبيعة',
    currency: 'العملة',
    billWise: 'حسب الفاتورة',
  },
  ledgerKinds: {
    Employee: 'موظف',
    Tax: 'بند ضريبي',
    AdditionalCharge: 'مصروف إضافي',
    General: 'عام',
    Cash: 'نقدي',
    Bank: 'بنكي',
    Customer: 'عميل',
    Supplier: 'مورّد',
  },
  accountNatures: {
    Asset: 'أصول',
    Liability: 'التزامات',
    Equity: 'حقوق ملكية',
    Income: 'إيرادات',
    Expense: 'مصروفات',
  },
  attention: {
    title: 'في انتظارك',
    hint: 'محسوبة من القوائم نفسها التي تفتحها. لا يظهر السطر إلا إذا كان به شيء.',
    purchaseDrafts_zero: 'مسودات مشتريات: {{count}}',
    purchaseDrafts_one: 'مسودات مشتريات: {{count}}',
    purchaseDrafts_two: 'مسودات مشتريات: {{count}}',
    purchaseDrafts_few: 'مسودات مشتريات: {{count}}',
    purchaseDrafts_many: 'مسودات مشتريات: {{count}}',
    purchaseDrafts_other: 'مسودات مشتريات: {{count}}',
    purchaseOrders_zero: 'أوامر شراء مفتوحة: {{count}}',
    purchaseOrders_one: 'أوامر شراء مفتوحة: {{count}}',
    purchaseOrders_two: 'أوامر شراء مفتوحة: {{count}}',
    purchaseOrders_few: 'أوامر شراء مفتوحة: {{count}}',
    purchaseOrders_many: 'أوامر شراء مفتوحة: {{count}}',
    purchaseOrders_other: 'أوامر شراء مفتوحة: {{count}}',
    salesOrders_zero: 'أوامر بيع مفتوحة: {{count}}',
    salesOrders_one: 'أوامر بيع مفتوحة: {{count}}',
    salesOrders_two: 'أوامر بيع مفتوحة: {{count}}',
    salesOrders_few: 'أوامر بيع مفتوحة: {{count}}',
    salesOrders_many: 'أوامر بيع مفتوحة: {{count}}',
    salesOrders_other: 'أوامر بيع مفتوحة: {{count}}',
    belowReorder_zero: 'منتجات للطلب: {{count}}',
    belowReorder_one: 'منتجات للطلب: {{count}}',
    belowReorder_two: 'منتجات للطلب: {{count}}',
    belowReorder_few: 'منتجات للطلب: {{count}}',
    belowReorder_many: 'منتجات للطلب: {{count}}',
    belowReorder_other: 'منتجات للطلب: {{count}}',
    chequesDue_zero: 'شيكات مؤجلة: {{count}}',
    chequesDue_one: 'شيكات مؤجلة: {{count}}',
    chequesDue_two: 'شيكات مؤجلة: {{count}}',
    chequesDue_few: 'شيكات مؤجلة: {{count}}',
    chequesDue_many: 'شيكات مؤجلة: {{count}}',
    chequesDue_other: 'شيكات مؤجلة: {{count}}',
  },
  dashboard: {
    customize: 'تخصيص',
    role: 'الدور',
    shown: 'على اللوحة',
    available: 'إضافة عناصر',
    noWidgets: 'لا توجد عناصر. أضف بعضها.',
    add: 'إضافة',
    remove: 'إزالة',
    moveUp: 'تحريك لأعلى',
    moveDown: 'تحريك لأسفل',
    width: 'العرض',
    noneWidgets: 'لا توجد عناصر بعد.',
    none: 'لا توجد لوحة.',
    withheld: 'غير متاح',
    noData: 'لا توجد بيانات بعد.',
    itemCount_zero: 'لا عناصر',
    itemCount_one: 'عنصر واحد',
    itemCount_two: 'عنصران',
    itemCount_few: '{{count}} عناصر',
    itemCount_many: '{{count}} عنصرًا',
    itemCount_other: '{{count}} عنصر',
  },
  charts: {
    heading: 'الاتجاهات',
    showTable: 'جدول',
    showChart: 'رسم',
    loadFailed: 'تعذر تحميل الرسوم البيانية.',
    dailySales: 'المبيعات، آخر 30 يومًا',
    withTax: 'شاملة الضريبة، بعد المرتجعات',
    bills_zero: 'لا فواتير',
    bills_one: 'فاتورة واحدة',
    bills_two: 'فاتورتان',
    bills_few: '{{n}} فواتير',
    bills_many: '{{n}} فاتورة',
    bills_other: '{{n}} فاتورة',
    billsHeading: 'الفواتير',
    date: 'التاريخ',
    month: 'الشهر',
    sales: 'المبيعات',
    purchases: 'المشتريات',
    salesAndPurchases: 'المبيعات والمشتريات',
    lastYearBeforeTax: 'آخر 12 شهرًا، قبل الضريبة',
    lastYear: 'آخر 12 شهرًا',
    twelveMonths: 'خلال 12 شهرًا',
    profit: 'الربح',
    loss: 'الخسارة',
    paymentModes: 'طرق الدفع',
    thisMonth: 'هذا الشهر',
    mode: 'الطريقة',
    amount: 'المبلغ',
    share: 'النسبة',
    topProducts: 'الأصناف الأكثر مبيعًا',
    topCategories: 'الفئات الأكثر مبيعًا',
    thisMonthWithTax: 'هذا الشهر، شاملة الضريبة',
    product: 'الصنف',
    category: 'الفئة',
    code: 'الرمز',
    quantity: 'الكمية {{quantity}}',
    quantityHeading: 'الكمية',
    receivables: 'المستحق على العملاء',
    byDaysOverdue: 'حسب أيام التأخير',
    daysOverdue: 'أيام التأخير',
    notDue: 'غير مستحق بعد',
    dayRange: '{{from}}–{{to}} يومًا',
    overDays: 'أكثر من {{days}} يومًا',
    overdue: '{{amount}} متأخر',
    noSales: 'لا مبيعات في هذه الأيام الثلاثين.',
    noSalesMonth: 'لا مبيعات هذا الشهر بعد.',
    nothingYet: 'لا شيء مسجل بعد.',
    nothingOwed: 'لا مستحقات.',
  },
  masters: {
    codeAuto: 'تلقائي',
    code: 'الرمز',
    name: 'الاسم',
    nameArabic: 'الاسم بالعربية',
    status: 'الحالة',
    active: 'نشط',
    withdrawn: 'مسحوب',
    withdraw: 'سحب',
    restore: 'استعادة',
    add: 'إضافة',
    includeWithdrawn: 'تضمين المسحوب',
    newUnit: 'وحدة جديدة',
    newCategory: 'فئة جديدة',
    editCategory: 'تعديل الفئة',
    editBrand: 'تعديل العلامة التجارية',
    editWarehouse: 'تعديل المستودع',
    newBrand: 'علامة تجارية جديدة',
    newWarehouse: 'مستودع جديد',
    newCustomer: 'عميل جديد',
    newSupplier: 'مورد جديد',
  },
  units: {
    symbol: 'الرمز المختصر',
    base: 'الوحدة الأساسية',
    isBase: '— أساسية —',
    newGroup: '— مجموعة جديدة —',
    factor: 'المعامل',
    decimals: 'الكسور العشرية',
    edit: 'تعديل الوحدة',
    codeFixed: 'الحركات المسجلة تستخدم هذا الرمز بالفعل.',
    factorFixed: 'تغيير المعامل يعيد صياغة كميات سُجّلت منذ أشهر.',
    codeRequired: 'الوحدة تحتاج إلى رمز.',
    codeTooLong: 'بحد أقصى 20 حرفاً.',
    nameRequired: 'الوحدة تحتاج إلى اسم.',
    nameTooLong: 'بحد أقصى 100 حرف.',
    factorRequired: 'المعامل مطلوب.',
    factorInvalid: 'المعامل أكبر من صفر.',
    decimalsInvalid: 'الكسور 0–6.',
    factorLockedHint: 'يُثبَّت بعد استخدام الوحدة في صنف أو مستند.',
  },
  categories: {
    parent: 'الفئة الأم',
    topLevel: '— المستوى الأعلى —',
    codeRequired: 'الرمز مطلوب.',
    codeTooLong: 'بحد أقصى 20 حرفاً.',
    nameRequired: 'الاسم مطلوب.',
    nameTooLong: 'بحد أقصى 100 حرف.',
  },
  products: {
    hideInPos: 'إخفاء في نقطة البيع',
    stock: 'المخزون',
    search: 'بحث',
    searchHint: 'يطابق رمز الصنف والوصف وأي باركود.',
    searchPlaceholder: 'رمز الصنف أو اسمه…',
    clear: 'مسح',
    descriptionRequired: 'الوصف مطلوب.',
    descriptionTooLong: 'بحد أقصى 200 حرف.',
    categoryRequired: 'الفئة مطلوبة.',
    unitRequired: 'الوحدة مطلوبة.',
    codeTooLong: 'بحد أقصى 30 حرفاً.',
    find: 'بحث',
    allCategories: '— كل الفئات —',
    new: 'منتج جديد',
    cancel: 'إلغاء',
    choose: '— اختر —',
    noneFound: 'لا توجد منتجات مطابقة.',
    notFound: 'المنتج غير موجود.',
    codeAuto: 'فارغ = تلقائي',
    save: 'حفظ',
    saved: 'تم الحفظ',
    backToList: 'رجوع',
    tabDescription: 'الوصف',
    tabDetails: 'التفاصيل',
    tabBarcodes: 'الباركود',
    tabOpeningStock: 'بضاعة أول المدة',
    detail: 'التفاصيل',
    identity: 'التعريف',
    classification: 'التصنيف',
    taxRate: 'نسبة الضريبة',
    details: 'التفاصيل',
    description: 'الوصف',
    descriptionArabic: 'الوصف بالعربية',
    shortDescription: 'الوصف المختصر',
    itemName: 'اسم الصنف',
    manufacturer: 'الشركة المصنعة',
    label: 'الملصق',
    size: 'الحجم',
    origin: 'بلد المنشأ',
    rack: 'الرف',
    bin: 'الصندوق',
    category: 'الفئة',
    brand: 'العلامة التجارية',
    noBrand: '— بدون علامة —',
    itemType: 'نوع الصنف',
    itemStock: 'مخزني',
    itemService: 'خدمة',
    itemNonStock: 'غير مخزني',
    rates: 'الأسعار',
    costingMethod: 'طريقة التكلفة',
    costingLastPurchase: 'سعر آخر شراء',
    costingAverage: 'متوسط السعر',
    cost: 'التكلفة',
    profitPercentage: 'نسبة الربح ٪',
    corPercentage: 'COR ٪',
    retailRate: 'سعر التجزئة',
    wholesaleRate: 'سعر الجملة',
    otherRate: 'سعر آخر',
    retailAboveMrp: 'سعر التجزئة أعلى من السعر الأقصى.',
    maximumRetailPrice: 'أقصى سعر بيع',
    mrpHint: 'صفر إذا لم يكن مطبوعاً. لا يجوز أن يتجاوزه سعر التجزئة.',
    deviceAttributes: 'خصائص الجهاز',
    device: 'الجهاز',
    colour: 'اللون',
    battery: 'البطارية',
    ram: 'الذاكرة',
    storage: 'سعة التخزين',
    units: 'الوحدات',
    stockUnit: 'وحدة المخزون',
    purchaseUnit: 'وحدة الشراء',
    salesUnit: 'وحدة البيع',
    orderLevels: 'مستويات الطلب',
    minimumLevel: 'الحد الأدنى',
    reorderLevel: 'حد إعادة الطلب',
    maximumLevel: 'الحد الأقصى',
    maximumLevelHint: 'صفر يعني بلا حد أقصى.',
    movement: 'الحركة',
    movementUnclassified: '— غير مصنف —',
    movementFast: 'سريع',
    movementNormal: 'عادي',
    movementSlow: 'بطيء',
    movementDead: 'راكد',
    tracking: 'التتبع',
    tracksBatches: 'يُحفظ بدفعات',
    tracksSerialNumbers: 'رقم تسلسلي / IMEI',
    requiresSerialImei: 'رقم تسلسلي / IMEI',
    isPacking: 'مادة تغليف',
    shelfLifeDays: 'مدة الصلاحية (أيام)',
    shelfLifeHint: 'تُحسب من تاريخ الدفعة.',
    batchShort: 'دفعة',
    serialShort: 'تسلسلي / IMEI',
    barcodes: 'الباركود',
    barcode: 'الباركود',
    noBarcodes: 'لا باركود.',
    removeBarcode: 'إزالة',
    discontinued: 'موقوف الشراء',
    discontinue: 'إيقاف الشراء',
    resume: 'استئناف الشراء',
  },
  stock: {
    new: 'حركة مخزون جديدة',
    newOf: '{{type}} جديد',
    save: 'حفظ',
    post: 'ترحيل',
    cancel: 'إلغاء المستند',
    confirmCancel: 'إلغاء وعكس الحركة',
    cancelReason: 'سبب الإلغاء',
    cancelledFor: 'ملغى: {{reason}}',
    postNow: 'الترحيل عند الحفظ',
    choose: '— اختر —',
    noneFound: 'لا مستندات.',
    notFound: 'المستند غير موجود.',
    number: 'الرقم',
    date: 'التاريخ',
    type: 'النوع',
    allTypes: '— كل الأنواع —',
    warehouse: 'المستودع',
    fromWarehouse: 'من مستودع',
    toWarehouse: 'إلى مستودع',
    allWarehouses: '— كل المستودعات —',
    reference: 'المرجع',
    narration: 'البيان',
    reason: 'السبب',
    reasonPlaceholder: 'السبب',
    remarks: 'ملاحظات',
    status: 'الحالة',
    statusDraft: 'مسودة',
    statusPosted: 'مرحّل',
    statusCancelled: 'ملغى',
    lines: 'السطور',
    addLine: 'إضافة سطر',
    removeLine: 'إزالة',
    product: 'المنتج',
    quantity: 'الكمية',
    counted: 'الكمية المعدودة',
    unit: 'الوحدة',
    inStockUnits: 'بوحدة المخزون',
    rate: 'السعر',
    ratePlaceholder: 'لوحدة المخزون',
    value: 'القيمة',
    unitCost: 'تكلفة الوحدة',
    movements: 'الحركات',
    noMovements: 'لا حركات.',
    noMovementsInRange: 'لا حركات.',
    balanceAfter: 'الرصيد بعدها',
    averageAfter: 'المتوسط بعدها',
    typeOpening: 'مخزون افتتاحي',
    typeReceipt: 'إذن استلام',
    typeIssue: 'إذن صرف',
    typeTransfer: 'تحويل مخزون',
    typeAdjustment: 'تسوية مخزون',
    typeDamaged: 'بضاعة تالفة',
    typeVerification: 'جرد فعلي',
    typeDeliveryNote: 'إشعار تسليم',
    typeReceiptNote: 'إشعار استلام',
    typeProduction: 'إنتاج',
    typeSalesIssue: 'صرف مبيعات',
    typeSalesReturn: 'استلام مرتجع مبيعات',
    typePurchaseReceipt: 'استلام مشتريات',
    typePurchaseReturn: 'صرف مرتجع مشتريات',
    countHint: 'أدخل ما وُجد على الرف فعلياً. يُرحَّل الفرق عن رصيد النظام فقط.',
    adjustmentHint:
      'الكمية الموجبة بضاعة وُجدت، والسالبة بضاعة مفقودة. اترك السعر فارغاً لتقييمها بتكلفة الرصيد الحالية.',
    postedNotice: 'تم ترحيل {{number}}.',
    postedNotice_two: 'تم ترحيل {{number}} — حركتان مسجّلتان.',
    postedNotice_few: 'تم ترحيل {{number}} — {{count}} حركات مسجّلة.',
    postedNotice_many: 'تم ترحيل {{number}} — {{count}} حركة مسجّلة.',
    postedNotice_other: 'تم ترحيل {{number}} — {{count}} حركة مسجّلة.',
    draftNotice: 'حُفظ {{number}} كمسودة.',
    cancelledNotice: 'تم الإلغاء.',
    onHand: 'الرصيد',
    averageCost: 'متوسط التكلفة',
    includeZero: 'إظهار الفارغ',
    totalValue: 'الإجمالي: {{value}} {{currency}}',
    nothingOnHand: 'لا يوجد رصيد.',
    nothingMoved: 'لا حركات.',
    chooseProduct: 'اختر منتجاً.',
    opening: 'رصيد أول المدة',
    closing: 'رصيد آخر المدة',
    in: 'وارد',
    out: 'صادر',
    totalIn: 'إجمالي الوارد',
    totalOut: 'إجمالي الصادر',
    valueIn: 'قيمة الوارد',
    valueOut: 'قيمة الصادر',
    lastMoved: 'آخر حركة',
    batch: 'التشغيلة',
    batchAuto: 'تلقائي',
    batchNone: '—',
    chooseBatch: '— اختر تشغيلة —',
    noBatchesInStock: 'لا يوجد رصيد',
    batchAvailable: '{{quantity}} بسعر {{rate}}',
    expiresOn: 'تنتهي في',
    manufacturedOn: 'تاريخ الإنتاج',
    purchaseRate: 'سعر الشراء',
    sellingRate: 'سعر البيع',
    soldQuantity: 'المباع',
    salesValue: 'المبيعات',
    costOfSales: 'التكلفة',
    profit: 'الربح',
    margin: 'الهامش',
    allProducts: 'كل المنتجات',
    noSales: 'لا مبيعات.',
    daysToExpiry: 'الأيام المتبقية',
    expired: 'منتهية',
    expiringWithin: 'تنتهي خلال',
    days: 'يوماً',
    onlyExpired: '— المنتهية فقط —',
    asOn: 'حتى تاريخ',
    nothingExpiring: 'لا شيء ينتهي.',
    noBatchStock: 'لا مخزون دفعات.',
    batchHint:
      'اترك التشغيلة فارغة في الاستلام ليُولَّد الرقم التالي للمنتج. أما الصرف والتحويل فيجب أن يحددا تشغيلة موجودة بالمخزون.',
    units: 'الوحدات',
    unitsPlaceholder: 'رقم في كل سطر',
    unitsChoose: 'اختر الوحدات',
    unitsNone: 'لا توجد وحدات بالمخزون',
    unitsCounted: '{{count}} من {{needed}}',
    warrantyUntil: 'الضمان حتى',
    serialHint:
      'المنتج ذو الأرقام التسلسلية يحتاج رقماً لكل وحدة يحركها السطر. الاستلام يسجّل الأرقام المكتوبة على العلب، أما الصرف والتحويل فيختاران من الوحدات الموجودة بالمخزون.',
    barcodeScanner: 'قارئ الباركود',
    barcodeScanPlaceholder: 'امسح الباركود / الرمز',
    barcodeAdded: 'تمت إضافة {{code}} (الكمية: {{qty}})',
    barcodeNotFound: '"{{barcode}}" غير موجود',
    scan: 'مسح',
    serialsRegister: 'سجل الأرقام',
    serialNumber: 'الرقم التسلسلي',
    underWarranty: 'تحت الضمان',
    warrantyExpired: 'منتهي الضمان',
    statusInStock: 'في المستودع',
    statusIssued: 'مصروف',
    statusReturned: 'مرتجع',
    batchAutoPrompt: 'فارغ = تلقائي',
  },
  adjustment: {
    subtitle: 'طابق الدفاتر مع ما على الرف: أدخل الجرد أو ارفعه، راجع الفروقات، ثم أكد.',
    countTab: 'الجرد',
    historyTab: 'السجل',
    historyTitle: 'سجل التسويات',
    reasonPlaceholder: 'السبب',
    search: 'ابحث عن صنف',
    show: 'عرض',
    showAll: 'الكل',
    showCounted: 'المجرود فقط',
    showDifferences: 'الفروقات فقط',
    addProduct: 'إضافة صنف',
    notCountable: 'لا يُجرد {{code}} هنا: يُتتبَّع بالدفعات أو ليس صنف مخزون',
    downloadSheet: 'تنزيل ورقة الجرد',
    uploadSheet: 'رفع الجرد',
    hint: 'لا يُسوّى إلا ما جردته؛ اترك الصنف فارغاً ليبقى كما هو. تحتاج الورقة عمود رمز الصنف وعمود الكمية المجرودة. الأصناف ذات الدفعات تُجرد دفعة دفعة في حركات المخزون.',
    unreadable: 'استخدم xlsx أو csv.',
    uploaded_zero: 'من الملف: {{count}}',
    uploaded_one: 'من الملف: {{count}}',
    uploaded_two: 'من الملف: {{count}}',
    uploaded_few: 'من الملف: {{count}}',
    uploaded_many: 'من الملف: {{count}}',
    uploaded_other: 'من الملف: {{count}}',
    unmatched_zero: 'رموز غير معروفة ({{count}}):',
    unmatched_one: 'رموز غير معروفة ({{count}}):',
    unmatched_two: 'رموز غير معروفة ({{count}}):',
    unmatched_few: 'رموز غير معروفة ({{count}}):',
    unmatched_many: 'رموز غير معروفة ({{count}}):',
    unmatched_other: 'رموز غير معروفة ({{count}}):',
    colCode: 'الرمز',
    colDescription: 'الوصف',
    colUnit: 'الوحدة',
    colSystem: 'رصيد النظام',
    colCounted: 'المجرود',
    nothingToCount: 'لا منتجات.',
    chooseWarehouse: 'اختر المستودع.',
    showingFirst: 'أول {{shown}} من {{total}}',
    summary: 'عُدّ {{counted}} · مختلف {{changed}}',
    gain: 'زيادة:',
    loss: 'نقص:',
    review: 'مراجعة الفروقات',
    reviewTitle: 'مراجعة',
    reviewIntro: '{{warehouse}}: عُدّ {{counted}}، للتسوية {{changed}}.',
    noDifferences: 'لا فروقات.',
    back: 'رجوع',
    confirm: 'تأكيد التسوية',
  },
  boms: {
    product: 'المنتج التام',
    makes: 'ينتج',
    components: 'المكونات',
    estimatedCost: 'التكلفة التقديرية',
    status: 'الحالة',
    active: 'مستخدمة',
    withdrawn: 'موقوفة',
    showWithdrawn: 'إظهار الموقوفة',
    none: 'لا توجد قوائم مواد بعد.',
    new: 'قائمة مواد جديدة',
    edit: 'قائمة المواد',
    finishedGood: 'المنتج التام',
    makesQuantity: 'ينتج (الكمية)',
    component: 'المكوّن',
    cost: 'التكلفة',
    hint: 'الكميات بوحدة مخزون كل صنف: كم من كل مكوّن ينتج الكمية أعلاه. التكلفة بمتوسط تكلفة المخزون اليوم.',
    incomplete: 'أضف المنتج والكمية والمكونات.',
    withdraw: 'إيقاف',
    restore: 'إعادة الاستخدام',
    savedNotice: 'تم حفظ قائمة المواد.',
    withdrawnNotice: 'تم إيقاف قائمة المواد.',
    restoredNotice: 'عادت قائمة المواد للاستخدام.',
  },
  production: {
    made: 'المنتجات التامة',
    madeHint: 'ما أنتجته هذه العملية. يدخل كل منها بحصته من تكلفة المواد.',
    used: 'المواد المستخدمة',
    usedHint: 'ما استُهلك لإنتاجها. تُعلّم الكمية التي تتجاوز المتوفر.',
    fillFromBom: 'تعبئة من قائمة المواد',
    filledFrom: 'عُبئ من قائمة مواد {{products}}.',
    noBom: 'لا توجد قائمة مواد.',
    batchNumber: 'الدفعة (فارغ = تلقائي)',
    incomplete: 'أضف منتجاً ومادة.',
    post: 'ترحيل الإنتاج',
  },
  service: {
    number: 'رقم العمل',
    date: 'التاريخ',
    customer: 'العميل',
    mobile: 'الجوال',
    mobileHint: 'اكتب ثلاثة أرقام للعثور على عميل سابق.',
    findMobile: 'بحث بالجوال',
    noPreviousEntry: 'لا يوجد إدخال سابق.',
    device: 'الجهاز',
    serialNumber: 'الرقم التسلسلي / IMEI',
    fault: 'العطل (كما ذكره العميل)',
    technician: 'الفني',
    estimatedOn: 'التاريخ المتوقع',
    amount: 'المبلغ',
    statusLabel: 'الحالة',
    status: {
      received: 'مستلم',
      processing: 'قيد الإصلاح',
      ready: 'جاهز',
      notReady: 'غير قابل للإصلاح',
      informed: 'تم إبلاغ العميل',
      delivered: 'تم التسليم',
      dead: 'تالف',
      cancelled: 'ملغى',
    },
    invoice: 'الفاتورة',
    allStatuses: 'كل الحالات',
    search: 'بحث',
    searchHint: 'بحث…',
    none: 'لا توجد بطاقات عمل بعد.',
    new: 'بطاقة عمل جديدة',
    card: 'بطاقة عمل',
    savedNotice: 'تم حفظ بطاقة العمل.',
    cancelledNotice: 'تم إلغاء العمل.',
    incomplete: 'الاسم والجهاز مطلوبان.',
    chooseGodown: 'اختر المستودع.',
    billedBy: 'فوترة: {{number}}',
    cancelledBecause: 'ملغى: {{reason}}',
    godown: 'المستودع',
    reference: 'رقم المرجع',
    paymentMode: 'الدفع',
    cash: 'نقدي',
    credit: 'آجل',
    online: 'إلكتروني',
    salesman: 'المندوب',
    customerDetails: 'العميل',
    customerName: 'اسم العميل',
    alternateMobile: 'جوال بديل',
    email: 'البريد الإلكتروني',
    address1: 'العنوان 1',
    address2: 'العنوان 2',
    account: 'حساب العميل',
    walkIn: 'عميل عابر (بدون حساب)',
    deviceDetails: 'الجهاز',
    itemName: 'اسم الصنف',
    brand: 'العلامة التجارية',
    model: 'رقم الموديل',
    colour: 'اللون',
    accessories: 'الملحقات المستلمة',
    workDetails: 'العمل',
    serviceType: 'نوع الخدمة',
    problem: 'المشكلة المكتشفة',
    estimatedTime: 'الوقت المتوقع',
    estimatedCharge: 'التكلفة المتوقعة',
    remarks: 'ملاحظات',
    notes: 'ملاحظات الورشة',
    stages: 'المراحل',
    stage: {
      processing: 'قيد الإصلاح',
      ready: 'جاهز',
      notReady: 'غير قابل للإصلاح',
      informed: 'تم إبلاغ العميل',
      delivered: 'تم التسليم',
      dead: 'تالف',
    },
    stagesAfterSave: 'احفظ لتتبع المراحل.',
    partsAndLabour: 'القطع والأجور',
    linesLocked: 'تمت الفوترة؛ الأسطر مقفلة.',
    part: 'القطعة أو الخدمة',
    rate: 'السعر',
    total: 'الإجمالي',
    estimateWas: 'التقدير: {{amount}}',
    cancelReason: 'سبب الإلغاء',
    confirmCancel: 'إلغاء العمل',
    keepJob: 'الإبقاء على العمل',
    save: 'حفظ',
    takeIn: 'استلام',
    billTo: 'الفوترة على حساب',
    chooseBillTo: 'اختر العميل.',
    raiseInvoice: 'إنشاء فاتورة مبيعات',
    cancelJob: 'إلغاء العمل',
  },
  txReports: {
    view: 'العرض',
    filters: 'التصفية',
    clearFilters: 'مسح التصفية',
    all: 'الكل',
    bills: 'الفواتير',
    quantity: 'الكمية',
    sale: 'المبيعات',
    purchase: 'المشتريات',
    tax: 'الضريبة',
    others: 'أخرى',
    total: 'الإجمالي',
    cost: 'التكلفة',
    profit: 'الربح',
    margin: 'الهامش',
    dateParty: 'التاريخ · الطرف',
    product: 'المنتج',
    mode: 'النوع',
    area: 'المنطقة',
    route: 'المسار',
    taxPercent: 'نسبة الضريبة',
    serialNo: 'الرقم التسلسلي',
    none: 'لا شيء في هذه الفترة.',
    customer: 'العميل',
    supplier: 'المورد',
    salesman: 'المندوب',
    brand: 'العلامة',
    category: 'الفئة',
    group: {
      Month: 'حسب الشهر',
      Day: 'حسب اليوم',
      Bill: 'حسب الفاتورة',
      Item: 'حسب الصنف',
      Brand: 'حسب العلامة',
      Category: 'حسب الفئة',
      Salesman: 'حسب المندوب',
      Party: 'حسب الطرف',
      Customer: 'حسب العميل',
      Supplier: 'حسب المورد',
    },
  },
  stockPosition: {
    asOn: 'في تاريخ',
    code: 'الرمز',
    name: 'الاسم',
    batch: 'التشغيلة',
    expiry: 'تاريخ الانتهاء',
    category: 'الفئة',
    brand: 'العلامة التجارية',
    godown: 'المستودع',
    branch: 'الفرع',
    quantity: 'الكمية',
    rate: 'السعر',
    value: 'قيمة المخزون',
    received: 'تاريخ الشراء',
    age: 'عمر المخزون (أيام)',
    ageBand: 'العمر',
    anyAge: 'أي عمر',
    bandsIn: 'الفئات بـ',
    expiryFrom: 'الانتهاء من',
    expiryTo: 'الانتهاء إلى',
    allBranches: '— كل الفروع —',
    allGodowns: '— كل المستودعات —',
    allBrands: '— كل العلامات —',
    totalQuantity: 'الكمية',
    totalValue: 'قيمة المخزون',
    nothing: 'لا يوجد مخزون.',
  },
  stockRegister: {
    purchase: 'المشتريات',
    sales: 'المبيعات',
    otherIn: 'وارد آخر',
    otherOut: 'صادر آخر',
  },
  shortcuts: {
    title: 'اختصارات لوحة المفاتيح',
    newEntries: 'إدخال جديد',
    inEntry: 'داخل الإدخال',
    general: 'عام',
    sale: 'بيع',
    salesReturn: 'مرتجع مبيعات',
    purchase: 'شراء',
    purchaseReturn: 'مرتجع مشتريات',
    quotation: 'عرض سعر',
    salesOrder: 'أمر بيع',
    purchaseOrder: 'أمر شراء',
    receipt: 'سند قبض',
    payment: 'سند صرف',
    journal: 'قيد يومية',
    serviceSale: 'بيع صيانة',
    customer: 'عميل',
    supplier: 'مورد',
    product: 'منتج',
    ledger: 'حساب',
    newHere: 'جديد في هذه الشاشة',
    save: 'حفظ',
    draft: 'حفظ كمسودة',
    print: 'حفظ وطباعة',
    bill: 'حفظ وفوترة',
    addLine: 'إضافة سطر',
    hold: 'تعليق الفاتورة',
    goCustomer: 'الانتقال إلى العميل',
    goScan: 'الانتقال إلى مسح الباركود',
    goPayment: 'الانتقال إلى المبلغ المستلم',
    goCharges: 'الانتقال إلى الحسابات الإضافية',
    nextBox: 'الحقل التالي في السطر، ثم السطر التالي',
    hint: {
      customer: 'العميل',
      scan: 'مسح',
      'add-line': 'إضافة سطر',
      payment: 'المستلم',
      charges: 'الحسابات',
      save: 'حفظ',
      print: 'حفظ وطباعة',
      all: 'كل الاختصارات',
    },
    close: 'إغلاق',
    search: 'بحث في القوائم',
    list: 'هذه القائمة',
    finishFirst: 'احفظ الإدخال المفتوح أو أغلقه أولاً.',
  },
  references: {
    loadFrom: 'تحميل من',
    typeNumber: 'اكتب أو اختر الرقم',
    loadFailed: 'تعذر التحميل.',
    quotation: 'عرض سعر',
    salesOrder: 'أمر بيع',
    salesInvoice: 'فاتورة بيع',
    purchase: 'مشتريات',
    purchaseOrder: 'أمر شراء',
    purchaseInvoice: 'فاتورة شراء',
  },
  serviceSales: {
    title: 'مبيعات الصيانة',
    new: 'جديد',
    none: 'لا توجد مبيعات صيانة بعد.',
    newTitle: 'بيع صيانة جديد',
    editTitle: 'بيع صيانة {{number}}',
    jobNo: 'رقم العمل',
    invoiceNo: 'رقم الفاتورة',
    mobile1: 'جوال 1',
    mobile2: 'جوال 2',
    mailId: 'البريد',
    item: 'الصنف',
    serialNo: 'الرقم التسلسلي',
    eDate: 'التاريخ المتوقع',
    eRate: 'السعر المتوقع',
    eTime: 'الوقت المتوقع',
    accessories: 'الملحقات',
    fault: 'العطل',
    notes: 'ملاحظات',
    problem: 'المشكلة',
    isDead: 'تالف',
    address1: 'العنوان 1',
    address2: 'العنوان 2',
    cashCredit: 'نقداً / آجل',
    mode: 'النوع',
    tax: 'ضريبة',
    nonTax: 'بدون ضريبة',
    barcode: 'الباركود',
    product: 'المنتج',
    code: 'الرمز',
    qty: 'الكمية',
    uom: 'الوحدة',
    addLine: 'إضافة سطر',
    gTotal: 'الإجمالي',
    saveAndBill: 'حفظ وفوترة',
    billed: 'فوترة: {{number}}',
    postFailed: 'فوترة {{number}}؛ لم تُرحّل: {{reason}}',
    notFound: 'لا يوجد منتج بهذا الرمز.',
    billNeedsCustomer: 'اختر حساب العميل للفوترة.',
  },
  rma: {
    number: 'رقم المرتجع',
    date: 'التاريخ',
    direction: 'الاتجاه',
    fromCustomer: 'من عميل',
    toSupplier: 'إلى مورد',
    bothDirections: 'الاتجاهين',
    party: 'العميل / المورد',
    customer: 'العميل',
    supplier: 'المورد',
    invoice: 'الفاتورة',
    reason: 'السبب',
    quantity: 'الكمية',
    statusLabel: 'الحالة',
    status: {
      requested: 'مطلوب',
      approved: 'معتمد',
      received: 'مستلم',
      underInspection: 'قيد الفحص',
      repaired: 'تم الإصلاح',
      replaced: 'تم الاستبدال',
      refunded: 'تم الاسترداد',
      closed: 'مغلق',
      rejected: 'مرفوض',
      cancelled: 'ملغى',
    },
    condition: 'الحالة الفنية',
    conditions: {
      good: 'جيد',
      repairable: 'معطل وقابل للإصلاح',
      defective: 'معيب',
      damaged: 'تالف',
    },
    outcome: 'سُوّي بموجب',
    search: 'بحث',
    searchHint: 'بحث…',
    allStatuses: 'كل الحالات',
    none: 'لا توجد مرتجعات بعد.',
    new: 'مرتجع جديد (RMA)',
    card: 'مرتجع',
    godown: 'مستودع المرتجعات',
    godownHint: 'المكان الذي تُحفظ فيه البضاعة المرتجعة.',
    sale: 'فاتورة المبيعات',
    purchase: 'فاتورة المشتريات',
    pickInvoice: 'الفاتورة (اختياري)',
    pickPartyFirst: 'اختر الطرف أولاً',
    invoiceReference: 'مرجع الفاتورة',
    narration: 'ملاحظات',
    product: 'المنتج',
    serial: 'الرقم التسلسلي / IMEI',
    lineReason: 'العطل',
    stepNote: 'ملاحظة',
    stepNoteHint: 'مطلوبة عند الرفض أو الإلغاء.',
    save: 'حفظ',
    request: 'طلب إرجاع',
    approve: 'اعتماد',
    reject: 'رفض',
    cancel: 'إلغاء المرتجع',
    savedNotice: 'تم حفظ المرتجع.',
    notices: {
      approve: 'تم اعتماد المرتجع.',
      reject: 'تم رفض المرتجع.',
      cancel: 'تم إلغاء المرتجع.',
      receive: 'تم الاستلام.',
      repair: 'تم تسجيل الإصلاح.',
      close: 'تم إغلاق المرتجع.',
      replaced: 'تم تسجيل الاستبدال.',
      replacedWith: 'استُبدل: {{number}}',
      inspected: 'تم حفظ الفحص.',
    },
    incomplete: 'الطرف والمستودع والسبب ومنتج مطلوبة.',
    reasonNeeded: 'السبب مطلوب.',
    settledBy: 'سُوّي: {{number}}',
    details: 'المرتجع',
    inspection: 'الفحص',
    inspectionNotes: 'ملاحظات الفحص',
    inspectedOn: 'تاريخ الفحص',
    saveInspection: 'حفظ الفحص',
    photos: 'الصور',
    noPhotos: 'لا توجد صور بعد.',
    photoCaption: 'وصف الصورة',
    addPhoto: 'إضافة صورة',
    photoFailed: 'لم تُضف الصورة.',
    removePhoto: 'حذف',
    goods: 'البضاعة',
    outcomeTitle: 'النتيجة',
    outcomeHintCustomer:
      'تم الإصلاح: يستلم العميل نفس الوحدة. استبدال: تخرج وحدة سليمة من مستودع المخزون وتبقى المرتجعة في مستودع المرتجعات. إشعار دائن: مرتجع مبيعات مسودة بسعر البيع، للمراجعة والترحيل.',
    outcomeHintSupplier:
      'تم الإصلاح: أعاد المورد الوحدة بعد إصلاحها. استبدال: تنتقل وحدة المورد البديلة من مستودع المرتجعات إلى مستودع المخزون. إشعار مدين: مرتجع مشتريات مسودة، للمراجعة والترحيل.',
    repaired: 'تم الإصلاح',
    stockGodown: 'مستودع المخزون',
    replace: 'استبدال',
    creditNote: 'إنشاء إشعار دائن',
    debitNote: 'إنشاء إشعار مدين',
    receive: 'تم استلام البضاعة',
    handOver: 'سُلّمت للمورد',
    close: 'إغلاق',
    history: 'السجل',
  },
  auditLog: {
    when: 'الوقت',
    user: 'المستخدم',
    module: 'الوحدة',
    action: 'الإجراء',
    actions: { Create: 'إنشاء', Update: 'تعديل', Delete: 'حذف' },
    record: 'السجل',
    recordId: 'معرّف السجل',
    fields: 'الحقول',
    field: 'الحقل',
    before: 'قبل',
    after: 'بعد',
    search: 'بحث',
    searchHint: 'بحث…',
    allModules: 'كل الوحدات',
    allUsers: 'كل المستخدمين',
    allActions: 'كل الإجراءات',
    none: 'لا سجلات.',
    sharedDatabase: 'قاعدة التدقيق غير موجودة؛ تُستخدم الرئيسية.',
  },
  approvals: {
    heldNotice: 'أُرسل {{number}} للموافقة.',
    requestedAt: 'تاريخ الطلب',
    document: 'المستند',
    action: 'التغيير',
    editAction: 'تصحيح',
    cancelAction: 'إلغاء',
    requestedBy: 'طلبه',
    reason: 'السبب',
    statusLabel: 'الحالة',
    status: {
      pending: 'بانتظار الموافقة',
      approved: 'تمت الموافقة',
      rejected: 'مرفوض',
      withdrawn: 'مسحوب',
    },
    kinds: { sales: 'مبيعات', purchase: 'مشتريات', voucher: 'سند' },
    decidedBy: 'قرّره',
    allStatuses: 'الكل',
    none: 'لا طلبات معلقة.',
    request: 'طلب تغيير',
    proposed: 'المقترح',
    date: 'التاريخ',
    product: 'المنتج',
    rate: 'السعر',
    amount: 'المبلغ',
    ledger: 'الحساب',
    debit: 'مدين',
    credit: 'دائن',
    openDocument: 'فتح المستند',
    note: 'ملاحظة',
    noteHint: 'مطلوبة عند الرفض',
    approve: 'موافقة',
    reject: 'رفض',
    withdraw: 'سحب طلبي',
    reasonNeeded: 'السبب مطلوب.',
    approvedNotice: 'تمت الموافقة على {{number}}.',
    rejectedNotice: 'رُفض {{number}}.',
    withdrawnNotice: 'تم السحب.',
  },
  quotations: {
    number: 'رقم العرض',
    date: 'تاريخ العرض',
    expectedOn: 'صالح حتى',
    linesTitle: 'ما هو معروض',
    none: 'لا عروض أسعار.',
    new: 'عرض سعر جديد',
    newSales: 'عرض سعر بيع جديد',
    document: 'عرض سعر',
    confirm: 'تعليم كمقبول',
    confirmed: 'مقبول',
    cancelled: 'مرفوض',
    close: 'رفض العرض',
    closedFor: 'مرفوض: {{reason}}',
    searchHint: 'بحث…',
    savedNotice: 'تم حفظ {{number}}.',
    confirmedNotice: 'تم قبول {{number}}.',
    closedNotice: 'تم الرفض.',
  },
  prefs: {
    tabs: {
      general: 'عام',
      defaults: 'القيم الافتراضية',
      sales: 'المبيعات',
      purchase: 'المشتريات',
      inventory: 'المخزون',
      accounts: 'الحسابات',
      charges: 'الحسابات الإضافية',
      printing: 'الطباعة',
      numbering: 'الترقيم',
      data: 'البيانات',
    },
    decimalPlaces_zero: 'بدون منازل عشرية',
    decimalPlaces_one: 'منزلة عشرية واحدة',
    decimalPlaces_two: 'منزلتان عشريتان',
    decimalPlaces_few: '{{count}} منازل عشرية',
    decimalPlaces_many: '{{count}} منزلة عشرية',
    decimalPlaces_other: '{{count}} منزلة عشرية',
    regionTitle: 'المنطقة والمظهر',
    regionHint: 'أين تعمل المنشأة، وكيف تبدو الشاشات للجميع.',
    gulfMode: 'وضع الخليج',
    gulfModeHint:
      'أسماء عربية بجانب الإنجليزية في الأصناف والعملاء وغيرها. عند إيقافه تُخفى الحقول العربية ما لم تحتوِ اسماً.',
    theme: 'المظهر',
    themeHint: 'المظهر الذي يبدأ به كل مستخدم، ويمكن لكل شخص تغييره لنفسه.',
    light: 'فاتح',
    dark: 'داكن',
    partiesTitle: 'الأطراف والأماكن',
    partiesHint: 'الطرف والمكان الذي يبدأ به المستند الجديد، ويمكن تغييرهما في المستند.',
    accountsTitle: 'الحسابات الافتراضية',
    accountsHint:
      'حسابا النقدية والبنك اللذان يُقبض فيهما ويُدفع منهما ما لم يُختر غيرهما.',
    defaultCash: 'حساب النقدية',
    firstCash: 'أول حساب نقدي',
    defaultBank: 'الحساب البنكي',
    firstBank: 'أول حساب بنكي',
    approvalsTitle: 'الموافقات',
    billWiseReceiptsTitle: 'المقبوضات: حسب الفاتورة',
    billWisePaymentsTitle: 'المدفوعات: حسب الفاتورة',
    billWiseHint: 'للأطراف المدارة حسب الفاتورة: على أي فواتير تُسجَّل المبالغ.',
    billWise: 'الإدارة حسب الفاتورة',
    billWiseReceiptsHint: 'عرض الفواتير المعلقة للعميل الآجل في سند القبض.',
    billWisePaymentsHint: 'عرض الفواتير المعلقة للمورد في سند الصرف.',
    fillBills: 'تعبئة الفواتير المحددة',
    fillBillsHint:
      'حدد الفواتير التي تُسوّى وأدخل المبلغ لكل منها؛ يُملأ المبلغ الإجمالي منها.',
    salesRulesTitle: 'قواعد المبيعات',
    salesEntryTitle: 'إدخال المبيعات',
    salesEntryHint: 'كيف تُسعّر المبيعات الجديدة وما الأعمدة التي تعرضها أسطرها.',
    taxModeNone: 'غير خاضع للضريبة',
    taxModeVat: 'ضريبة (القيمة المضافة)',
    taxModeGst: 'ضريبة السلع والخدمات',
    rateRetail: 'سعر التجزئة',
    rateWholesale: 'سعر الجملة',
    rateMrp: 'السعر الأقصى',
    rateLastQuotation: 'آخر عرض سعر',
    rateCustomer: 'سعر العميل',
    rateBatch: 'سعر الدفعة',
    purchaseRulesTitle: 'قواعد المشتريات',
    stockRulesTitle: 'قواعد المخزون',
    trackingTitle: 'التتبع',
    trackingHint: 'ما تتبعه المنشأة في أصنافها.',
    savingTitle: 'حفظ المعاملات',
    salesScreenTitle: 'في شاشة المبيعات',
    showSalesScan: 'خانة مسح الباركود',
    showSalesReference: 'الرقم المرجعي',
    pickSalesmen: 'اختيار مندوب المبيعات ومسؤول الفوترة',
    showBillingName: 'اسم الفاتورة',
    showSalesNarration: 'البيان / الملاحظات',
    allowRateChange: 'تغيير الأسعار في الفاتورة',
    itemDiscountColumn: 'عمود خصم الصنف',
    freeQuantityColumn: 'عمود الكمية المجانية',
    inclusiveRates: 'الأسعار شاملة الضريبة (حساب عكسي)',
    allowLoadFrom: 'التحميل من عرض سعر أو أمر أو مشتريات',
    allowHold: 'تعليق الفواتير',
    namedCustomerForNonCash: 'مبيعات البطاقة والإلكتروني والآجل تحتاج عميلاً مسمّى',
    walkInCustomer: 'العميل النقدي',
    primaryCustomer: 'العميل الرئيسي',
    salesPaymentTitle: 'الدفع والتقريب',
    defaultSalesPaymentMode: 'طريقة الدفع للبيع الجديد',
    salesRoundTo: 'تقريب إجمالي الفاتورة إلى',
    noRounding: 'بدون تقريب',
    roundToNearest: 'أقرب {{unit}}',
    onlineTypesTitle: 'أنواع الدفع الإلكتروني',
    noOnlineTypes:
      'لا يوجد بعد. أضف Google Pay أو PhonePe أو Talabat أو أي طريقة دفع إلكترونية أخرى.',
    onlineTypeName: 'الاسم',
    onlineTypeBank: 'يُسوّى في',
    onlineTypeActive: 'نشط',
    defaultBankAccount: 'الحساب البنكي الافتراضي',
    onlineTypePlaceholder: 'Google Pay، PhonePe، Talabat…',
    addOnlineType: 'إضافة نوع',
    onlineTypeTaken: 'هذا النوع موجود في القائمة.',
    salesDefaultsTitle: 'يبدأ البيع الجديد بـ',
    purchaseDefaultsTitle: 'تبدأ المشتريات الجديدة بـ',
    voucherPaymentMode: 'طريقة الدفع في سندات القبض والصرف',
    paymentDebitAccount: 'الحساب المدين لسند الصرف',
    receiptCreditAccount: 'الحساب الدائن لسند القبض',
    showChargesOn: 'عرض الحسابات الإضافية في {{document}}',
    documents: {
      Sales: 'فاتورة مبيعات',
      SalesReturn: 'مرتجع مبيعات',
      SalesOrder: 'أمر بيع',
      Purchase: 'فاتورة مشتريات',
      PurchaseReturn: 'مرتجع مشتريات',
      PurchaseOrder: 'أمر شراء',
    },
    noCharges: 'لا رسوم.',
    chargeActive: 'تطبيق',
    dataTitle: 'الاستيراد والمسح',
    dataHint: 'الاستيراد من نظام آخر، أو مسح بيانات المنشأة للبدء من جديد.',
    importData: 'استيراد البيانات',
    clearData: 'مسح البيانات',
    sampleTitle: 'بيانات تجريبية',
    sampleHint: 'فواتير ومشتريات وأوامر وقيود تجريبية لتجربة النظام.',
    sampleRun: 'إنشاء بيانات تجريبية',
    sampleRunning: 'جارٍ الإنشاء…',
    sampleDone: 'تم إنشاء بيانات تجريبية.',
    sampleFailed: 'تعذر إنشاء بعض البيانات.',
  },
  years: {
    title: 'السنوات المالية',
    hint: 'الفترات التي تُرقّم فيها المستندات وتُرحّل. السنة المقفلة لا تقبل ترحيلاً حتى تُفتح مجدداً.',
    code: 'السنة',
    start: 'تبدأ',
    end: 'تنتهي',
    status: 'الحالة',
    open: 'مفتوحة',
    closed: 'مقفلة',
    locked: 'مقفلة نهائياً',
    edit: 'تعديل',
    close: 'إقفال',
    reopen: 'إعادة فتح',
    add: 'إضافة سنة',
    save: 'حفظ',
  },
  notes: {
    customer: 'العميل',
    supplier: 'المورد',
    invoice: 'الفاتورة',
    notBilled: 'لم تُفوتر بعد',
    billedBy: 'فوترت بالفاتورة {{number}}',
    raiseInvoice: 'إنشاء فاتورة مبيعات',
    raisePurchase: 'إنشاء فاتورة مشتريات',
    chooseCustomer: 'اختر العميل.',
    chooseSupplier: 'اختر المورد.',
    deliveryHint:
      'تخرج البضاعة من المخزون عند حفظ الإشعار. أنشئ الفاتورة من الإشعار لاحقاً؛ فهي تفوتر البضاعة دون إخراجها مرة أخرى.',
    receiptHint:
      'تدخل البضاعة المخزون بالسعر المُدخل عند حفظ الإشعار. أنشئ فاتورة الشراء من الإشعار عند وصول فاتورة المورد؛ فهي تدفع ثمن البضاعة دون استلامها مرة أخرى.',
  },
  history: {
    code: 'الرمز',
    previous: 'الرصيد السابق',
    actual: 'الرصيد الفعلي',
    adjusted: 'التسوية',
    reason: 'السبب',
    none: 'لا تصحيحات.',
  },
  damaged: {
    entriesTab: 'الإتلافات',
    historyTab: 'السجل',
    historyTitle: 'سجل المخزون التالف',
  },
  policies: {
    title: 'قواعد العمل',
    hint: 'قواعد يفرضها الخادم على كل مستند مهما كانت الشاشة أو الجهاز.',
    readOnly: 'للقراءة فقط.',
    negativeStock: 'المخزون السالب',
    negativeStockHint:
      'هل يجوز صرف بضاعة لا يظهرها النظام متوفرة. الدفعات والأرقام التسلسلية لا تنزل تحت الصفر أبداً.',
    creditLimit: 'حد الائتمان',
    creditLimitHint: 'ما يحدث عندما يتجاوز بيع آجل حد ائتمان العميل.',
    block: 'منع',
    warn: 'تنبيه',
    ignore: 'تجاهل',
    transfersRequireApproval: 'التحويلات تحتاج موافقة',
    transfersRequireApprovalHint:
      'مفعّل: ينتقل المخزون بين المستودعات بالطلب والاعتماد والإرسال والاستلام فقط. معطّل: يسمح بالتحويل المباشر أيضاً.',
    editsRequireApproval: 'التعديلات تحتاج موافقة',
    editsRequireApprovalHint:
      'يُحتجز التصحيح أو الإلغاء من شخص لا يملك صلاحية الموافقة حتى يوافق عليه معتمِد في شاشة الموافقات.',
    purchasesUpdateProductRates: 'الشراء يحدّث الأسعار',
    purchasesUpdateProductRatesHint:
      'ترحيل الشراء يجعل تكلفة كل صنف ما دُفع فيه، ويعيد حساب سعر التجزئة من نسبة الربح (دون تجاوز السعر الأقصى).',
  },
  transfers: {
    number: 'رقم الطلب',
    from: 'من',
    to: 'إلى',
    requested: 'المطلوب',
    approved: 'المعتمد',
    dispatched: 'المرسل',
    availableAtSource: 'المتوفر في المصدر',
    dispatchNumber: 'رقم الإرسال',
    receiptNumber: 'رقم الاستلام',
    allStatuses: 'كل الحالات',
    noneFound: 'لا طلبات.',
    notFound: 'الطلب غير موجود.',
    new: 'طلب جديد',
    direct: 'تحويل مباشر',
    directHint: 'ينقل البضاعة فوراً من مستودع إلى آخر دون طلب أو اعتماد.',
    requestsRequired: 'التحويل المباشر معطل.',
    edit: 'تعديل الطلب',
    approve: 'اعتماد',
    reject: 'رفض',
    cancelRequest: 'إلغاء الطلب',
    dispatch: 'إرسال',
    receive: 'استلام',
    confirm: 'تأكيد',
    reason: 'السبب',
    askFrom: 'من المستودع',
    chooseWarehouses: 'اختر المستودعين.',
    addAProduct: 'أضف منتجاً.',
    deliverTo: 'إلى المستودع',
    quantityWanted: 'الكمية المطلوبة',
    send: 'إرسال الطلب',
    saveChanges: 'حفظ التغييرات',
    approveHint: 'اعتمد من كل صنف ما يمكن للمصدر توفيره. الصفر يرفض السطر.',
    dispatchHint:
      'اختر الدفعات والأرقام التسلسلية المرسلة. تبقى البضاعة في «بضائع في الطريق» حتى يستلمها {{to}}.',
    receiveHint: 'يستلم كل ما أُرسل في المستودع المستلم بالتكلفة التي خرج بها.',
    receivedOn: 'تاريخ الاستلام',
    raisedNotice: 'تم إرسال الطلب {{number}}.',
    savedNotice: 'تم حفظ الطلب.',
    approvedNotice: 'تم اعتماد الطلب {{number}}.',
    rejectedNotice: 'تم رفض الطلب {{number}}.',
    cancelledNotice: 'تم إلغاء الطلب {{number}}.',
    dispatchedNotice: 'شُحن {{number}} ({{document}}).',
    receivedNotice: 'استُلم {{number}} ({{document}}).',
    statusRequested: 'مطلوب',
    statusApproved: 'معتمد',
    statusDispatched: 'مرسل',
    statusReceived: 'مستلم',
    statusRejected: 'مرفوض',
    statusCancelled: 'ملغى',
  },
  warehouses: {
    branch: 'الفرع',
    central: '— مركزي —',
    address: 'العنوان',
    default: 'افتراضي',
    makeDefault: 'جعله افتراضياً',
  },
  sales: {
    number: 'الرقم',
    billingName: 'اسم الفاتورة',
    billingNamePlaceholder: 'اسم العميل النقدي',
    onlineType: 'طريقة الدفع',
    chooseOnlineType: 'اختر…',
    onlineTypeRequired: 'اختر طريقة الدفع الإلكتروني.',
    namedCustomerRequired: 'اختر العميل: مبيعات {{mode}} تحتاج عميلاً مسمّى.',
    heldBills_zero: 'لا فواتير معلقة',
    heldBills_one: 'فاتورة معلقة واحدة',
    heldBills_two: 'فاتورتان معلقتان',
    heldBills_few: '{{count}} فواتير معلقة',
    heldBills_many: '{{count}} فاتورة معلقة',
    heldBills_other: '{{count}} فاتورة معلقة',
    heldTitle: 'الفواتير المعلقة',
    heldSubtitle: 'الفواتير المعلقة اليوم، الأحدث أولاً.',
    noHeldBills: 'لا توجد فواتير معلقة.',
    openHeld: 'فتح',
    discardHeld: 'حذف',
    heldNotice: 'عُلّقت الفاتورة. افتحها من الفواتير المعلقة.',
    resumedFrom: 'معلقة منذ {{time}}',
    dateRequired: 'التاريخ مطلوب.',
    customerRequired: 'العميل مطلوب.',
    warehouseRequired: 'المستودع مطلوب.',
    linesRequired: 'أضف منتجاً.',
    quantityInvalid: 'الكمية أكبر من صفر.',
    rateInvalid: 'سعر غير صالح.',
    taxInvalid: 'الضريبة 0–100٪.',
    kind: 'النوع',
    invoice: 'فاتورة',
    return: 'مرتجع',
    date: 'التاريخ',
    customer: 'العميل',
    warehouse: 'المستودع',
    reference: 'المرجع',
    lines: 'السطور',
    taxable: 'الخاضع للضريبة',
    tax: 'الضريبة',
    total: 'الإجمالي',
    status: 'الحالة',
    draft: 'مسودة',
    posted: 'مرحّلة',
    cancelled: 'ملغاة',
    allKinds: 'كل الأنواع',
    allStatuses: 'كل الحالات',
    from: 'من',
    to: 'إلى',
    search: 'بحث',
    searchHint: 'الرقم أو المرجع',
    none: 'لا مستندات.',
    new: 'فاتورة جديدة',
    newInvoice: 'فاتورة جديدة',
    newReturn: 'مرتجع جديد',
    open: 'فتح',
    document: 'المستند',
    close: 'إغلاق',
    saveDraft: 'حفظ كمسودة',
    post: 'ترحيل',
    cancel: 'إلغاء المستند',
    cancelReason: 'السبب',
    product: 'الصنف',
    quantity: 'الكمية',
    rate: 'السعر',
    discount: 'الخصم',
    taxPercent: 'نسبة الضريبة',
    net: 'الصافي',
    lineTotal: 'الإجمالي',
    addLine: 'إضافة سطر',
    removeLine: 'حذف',
    chooseCustomer: '— اختر عميلاً —',
    chooseWarehouse: '— اختر مستودعاً —',
    chooseProduct: '— اختر صنفاً —',
    againstInvoice: 'مقابل الفاتورة',
    noInvoice: '— بدون —',
    currency: 'العملة',
    batch: 'الدفعة',
    chooseBatch: '— اختر دفعة —',
    serialsChosen: 'اختير {{chosen}}/{{wanted}}',
    noSerials: 'لا أرقام في المخزون.',
    savedNotice: 'حُفظ {{number}} (مسودة).',
    postedNotice: 'رُحّل {{number}}: {{total}}.',
    cancelledNotice: 'تم الإلغاء.',
    edit: 'تعديل',
    editDocument: 'تعديل {{number}}',
    editPostedHint: 'مُرحّل: الحفظ يعيد ترحيله بالرقم نفسه.',
    editReason: 'السبب',
    updatedNotice: 'تم تحديث {{number}}.',
    postedProduced: 'أنتج الترحيل مستند مخزون وقيداً — وفاتورة مستحقة في حالة البيع.',
    returnHint:
      'تحديد الفاتورة يعيد البضاعة بالتكلفة التي خرجت بها، ويسدد الرصيد مقابل تلك الفاتورة. وبدونها تعود بمتوسط التكلفة الحالي ويبقى الرصيد على حساب العميل.',
    totalsHint: 'تقدير مبدئي. يقوم الخادم بتقريب الإجمالي حسب العملة عند الحفظ.',
    salesInvoices: 'فواتير المبيعات',
    salesReturns: 'مرتجعات المبيعات',
    newSalesReturn: 'سند إرجاع جديد',
    returnNo: 'رقم المرتجع',
    noReturns: 'لا مرتجعات.',
    invoicesTab: 'فواتير المبيعات',
    returnsTab: 'مردودات المبيعات',
    invoiceNo: 'رقم الفاتورة',
    referenceNo: 'رقم المرجع',
    salesman: 'مندوب المبيعات',
    billingman: 'مسؤول الفوترة',
    paymentMode: 'طريقة الدفع',
    taxMode: 'الوضع الضريبي',
    barcode: 'الباركود',
    barcodePlaceholder: 'امسح الباركود…',
    freeQuantity: 'كمية مجانية',
    itemDiscount: 'خصم الصنف',
    salesRateType: 'سعر البيع',
    reverseCalculation: 'حساب عكسي',
    autoBatch: 'دفعة تلقائية',
    additionalLedgers: 'حسابات إضافية',
    addLedger: 'إضافة حساب',
    addition: 'إضافة (+)',
    addCharge: '+ إضافة مصروف',
    deduction: 'خصم (-)',
    chooseLedger: 'اختر الحساب',
    additionalLedgersTotal: 'إجمالي الرسوم',
    batchExpiresOn: 'ينتهي في',
    batchExpired: 'منتهي الصلاحية',
    inclusiveRate: 'السعر شامل الضريبة',
    scanProductSuccess: 'أُضيف',
    scanProductNotFound: 'الباركود غير موجود',
    itemsTitle: 'الأصناف',
    itemCount_one: 'صنف واحد',
    itemCount_other: '{{count}} أصناف',
    generalTab: 'عام',
    shippingTab: 'الشحن والتوصيل',
    narration: 'البيان / ملاحظات',
    grandTotal: 'الإجمالي الكلي',
    cashGiven: 'النقد المستلم',
    changeBack: 'الباقي للإرجاع',
    billDue: 'المستحق على الفاتورة',
    itemsTotal: 'إجمالي الأصناف',
    prevBalance: 'الرصيد السابق',
    billSection: 'الفاتورة',
    billTotal: 'إجمالي الفاتورة',
    returnTotal: 'إجمالي المرتجع',
    paymentSection: 'الدفع',
    receivedNow: 'المبلغ المدفوع',
    receivedInto: 'استلام في',
    noReceivingAccount: 'لا حساب نقد/بنك',
    transactionId: 'رقم العملية',
    transactionIdPlaceholder: 'مثال: 4471 2290 1183',
    cardApproval: 'رمز الموافقة',
    cardApprovalPlaceholder: 'من إيصال البطاقة',
    accountSection: 'حساب العميل',
    thisBill: 'هذه الفاتورة',
    thisReturn: 'هذا المرتجع',
    balanceAfter: 'الرصيد الجديد',
    overCreditLimit: 'تجاوز الحد {{limit}} بـ {{by}}.',
    creditLimitBlocks: 'حصّل الزيادة أو ارفع الحد.',
    amountReceived: 'المستلم',
    paymentReference: 'رقم العملية',
    taxTreatment: 'الوضع الضريبي',
    print: 'طباعة',
    printInvoice: 'طباعة الفاتورة',
    printPreview: 'طباعة',
    documentDetails: 'تفاصيل المستند',
    time: 'الوقت',
    noEmployees: 'لا يوجد موظفون بعد',
    savedPostedNotice: 'رُحّل {{number}}.',
    save: 'حفظ',
    saveAndPrint: 'حفظ وطباعة',
    taxAmount: 'الضريبة',
    setUpCharge: 'حساب إضافي جديد',
    setUpChargeHint:
      'يمكن إضافة أي حساب مصروفات أو إيرادات إلى البيع. اختره وحدد إن كان يضاف إلى الفاتورة أو يخصم منها، وسيُعرض بعدها في كل عملية بيع.',
    setUp: 'إعداد',
    noAdditionalLedgers: 'لا رسوم.',
  },
  customers: {
    mobile: 'الجوال',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    address: 'العنوان',
    address2: 'العنوان - سطر 2',
    creditDays: 'أيام الائتمان',
    creditLimit: 'حد الائتمان',
    noLimit: 'بدون',
    stateCode: 'الولاية',
    state: 'الولاية',
    noState: 'غير محددة',
    stateHint: 'تحدد بنود الضريبة على الفواتير الصادرة له.',
    stateNoRegime: 'حدد النظام الضريبي في الإعدادات لاختيار الولاية.',
    stateRequired: 'الولاية مطلوبة لـ GST.',
    registrationNumber: 'الرقم الضريبي',
    openingBalance: 'الرصيد الافتتاحي',
    openingHint: 'ما كان مستحقاً عليه عند بدء استخدام النظام.',
    openingInvalid: 'الرصيد الافتتاحي موجب.',
    codeAuto: 'اتركه فارغاً ليصدر الرمز التالي في التسلسل.',
    codeFixed: 'لا يمكن تغيير الرمز بعد ارتباط الحركات به.',
    codeTooLong: 'بحد أقصى 30 حرفاً.',
    nameRequired: 'الاسم مطلوب.',
    nameTooLong: 'بحد أقصى 200 حرف.',
    emailInvalid: 'بريد غير صالح.',
    creditDaysInvalid: 'أيام ائتمان غير صالحة.',
    creditLimitInvalid: 'حد ائتمان غير صالح.',
    edit: 'تعديل العميل',
    contactPerson: 'الشخص المسؤول',
    website: 'الموقع الإلكتروني',
    fax: 'الفاكس',
    city: 'المدينة',
    pinCode: 'الرمز البريدي',
    area: 'المنطقة',
    route: 'خط السير',
    noAreas: 'لا توجد مناطق بعد',
    noRoutes: 'لا توجد خطوط سير بعد',
    chooseCountryFirst: 'اختر الدولة أولاً',
    gstNumber: 'رقم GST',
    tinNumber: 'الرقم الضريبي TIN',
    cstNumber: 'رقم تسجيل CST',
    sms: 'رسائل SMS',
    sendSms: 'إرسال SMS',
    openingAmount: 'الرصيد الافتتاحي',
    sectionGeneral: 'عام',
    sectionAddress: 'العنوان',
    sectionTax: 'التسجيل الضريبي',
    sectionTerms: 'الائتمان والافتتاحي',
    sectionPrint: 'إعدادات الطباعة',
    printCopies: 'عدد النسخ',
    printPreview: 'المعاينة',
    previewFirst: 'عرض قبل الطباعة',
    printStraightAway: 'طباعة مباشرة',
    copiesInvalid: 'النسخ: 1 فأكثر.',
    photo: 'الصورة',
    noPhoto: 'لا توجد صورة',
    addPhoto: 'إضافة صورة',
    changePhoto: 'تغيير الصورة',
    removePhoto: 'إزالة الصورة',
    photoInvalid: 'صورة غير صالحة.',
  },
  places: {
    countries: 'الدول',
    states: 'المناطق الإدارية',
    countriesTitle: 'الدول',
    statesTitle: 'المناطق الإدارية',
    country: 'الدولة',
    allCountries: 'كل الدول',
    currency: 'العملة',
    currencyName: 'اسم العملة',
    newCountry: 'دولة جديدة',
    editCountry: 'تعديل الدولة',
    newState: 'منطقة إدارية جديدة',
    editState: 'تعديل المنطقة الإدارية',
    areas: 'المناطق',
    routes: 'خطوط السير',
    newArea: 'منطقة جديدة',
    newRoute: 'خط سير جديد',
    editArea: 'تعديل المنطقة',
    editRoute: 'تعديل خط السير',
    codeRequired: 'الرمز مطلوب.',
    codeTooLong: 'بحد أقصى 3 أحرف.',
    stateCodeTooLong: 'بحد أقصى 10 أحرف.',
    nameRequired: 'الاسم مطلوب.',
    nameTooLong: 'بحد أقصى 100 حرف.',
    countryRequired: 'اختر الدولة.',
    currencyInvalid: '3 أحرف، مثل QAR.',
  },
  suppliers: {
    registration: 'الرقم الضريبي',
    registrationHint: 'ما تُطالب به ضريبة المدخلات.',
    stateHint: 'تحدد بنود الضريبة على المشتريات منه.',
    stateRequired: 'الولاية مطلوبة لـ GST.',
    openingHint: 'ما كان مستحقاً للمورد عند بدء استخدام النظام.',
    nameRequired: 'الاسم مطلوب.',
    edit: 'تعديل المورد',
  },
  orders: {
    number: 'رقم الأمر',
    numberAuto: 'يصدر من تسلسل ترقيم الفرع.',
    numberOnSave: 'تلقائي عند الحفظ',
    date: 'تاريخ الأمر',
    expectedOn: 'التاريخ المتوقع',
    expectedHint: 'موعد تسليم البضاعة. يُترك فارغاً إن لم يُحدد.',
    supplier: 'المورد',
    customer: 'العميل',
    warehouse: 'المستودع',
    reference: 'المرجع',
    referenceSupplier: 'رقم عرض السعر لديه، إن وُجد.',
    referenceCustomer: 'رقم أمر الشراء لديه، إن وُجد.',
    narration: 'البيان',
    lines: 'السطور',
    linesTitle: 'الأصناف المطلوبة',
    outstandingLines: 'المتبقي',
    outstandingOnly: 'المستحقة فقط',
    taxable: 'القيمة الخاضعة',
    tax: 'الضريبة',
    total: 'الإجمالي',
    currency: 'العملة',
    status: 'الحالة',
    draft: 'مسودة',
    confirmed: 'مؤكد',
    completed: 'مكتمل',
    cancelled: 'مغلق',
    allStatuses: 'كل الحالات',
    from: 'من',
    to: 'إلى',
    search: 'بحث',
    searchHint: 'بحث…',
    none: 'لا أوامر.',
    new: 'أمر جديد',
    newPurchase: 'أمر شراء جديد',
    newSales: 'أمر بيع جديد',
    open: 'فتح',
    document: 'الأمر',
    saveDraft: 'حفظ المسودة',
    confirm: 'تأكيد الأمر',
    convertPurchase: 'إنشاء شراء',
    convertSales: 'إنشاء فاتورة',
    close: 'إغلاق الأمر',
    closeReason: 'السبب',
    closeHint: 'يُحفظ مع الأمر ويظهر عند مراجعته.',
    closedFor: 'مغلق: {{reason}}',
    booksHint:
      'الأمر التزام وليس حركة محاسبية: لا يُنشئ قيداً ولا يحرّك أي حساب ولا يظهر في ميزان المراجعة. تتأثر الدفاتر عند ترحيل فاتورة المشتريات أو المبيعات الناتجة عنه.',
    product: 'الصنف',
    quantity: 'الكمية',
    ordered: 'المطلوب',
    invoiced: 'المفوتر',
    outstanding: 'المتبقي',
    rate: 'السعر',
    discount: 'الخصم',
    taxPercent: 'نسبة الضريبة',
    net: 'الصافي',
    savedNotice: 'تم حفظ {{number}}.',
    confirmedNotice: 'تم تأكيد {{number}}.',
    convertedPurchase: 'أُنشئت مسودة شراء {{number}}.',
    convertedSales: 'أُنشئت مسودة فاتورة {{number}}.',
    closedNotice: 'أُغلق الأمر.',
    dateRequired: 'التاريخ مطلوب.',
    supplierRequired: 'المورد مطلوب.',
    customerRequired: 'العميل مطلوب.',
    warehouseRequired: 'المستودع مطلوب.',
    expectedBeforeDate: 'قبل تاريخ الأمر.',
    linesRequired: 'أضف منتجاً.',
    quantityInvalid: 'الكمية أكبر من صفر.',
    rateInvalid: 'سعر غير صالح.',
    taxInvalid: 'الضريبة 0–100٪.',
  },
  documents: {
    columns: 'أعمدة السطور',
    charges: 'المصاريف',
    chargesShort: 'المصاريف',
    chargesHint:
      'الشحن، التغليف، التوصيل، أو خصم على المستند كاملاً — ما فوتره المورد بجانب البضاعة.',
    chargeAccount: 'حساب المصروف',
    chargeAmount: 'المبلغ',
    chooseCharge: '— اختر حساباً —',
    addCharge: 'إضافة حساب',
    removeCharge: 'حذف',
    chargeAdds: 'يُضاف',
    chargeDeducts: 'يُخصم',
    noChargeAccounts: 'لا حسابات رسوم.',
    noChargesSetUp: 'لا حسابات إضافية مُعدّة.',
    setUpCharges: 'إعداد',
    gross: 'الإجمالي قبل الخصم',
    discount: 'الخصم',
    net: 'الصافي',
    rounding: 'تقريب',
    taxable: 'القيمة الخاضعة',
    tax: 'الضريبة',
    total: 'الإجمالي',
  },
  purchase: {
    number: 'الرقم',
    documentNumber: 'رقم المستند',
    numberAuto: 'يصدر من تسلسل ترقيم الفرع.',
    numberOnSave: 'تلقائي عند الحفظ',
    openDraft: 'فتح المسودة',
    noReturns: 'لا مرتجعات.',
    linesTitle: 'الأصناف المشتراة',
    narration: 'البيان',
    taxAmount: 'الضريبة',
    chargeTotal: 'المصاريف',
    taxMode: 'المعالجة الضريبية',
    modeTax: 'خاضعة للضريبة',
    modeNonTax: 'بدون ضريبة',
    modeCst: 'ضريبة مبيعات مركزية',
    warehouseDefaulted:
      'افتراضي من بيانات المستودعات؛ غيّره إذا وصلت البضاعة إلى مكان آخر.',
    againstHint: 'يتيح للمدين تسوية فاتورة ذلك المستند.',
    cancelHint: 'يُحفظ مع المستند ويظهر عند مراجعته.',
    dateRequired: 'التاريخ مطلوب.',
    supplierRequired: 'المورد مطلوب.',
    warehouseRequired: 'المستودع مطلوب.',
    supplierInvoiceMissing: 'أضف رقم فاتورة المورد.',
    saveAndPost: 'حفظ وترحيل',
    savedNotPosted: 'حُفظت {{number}} كمسودة؛ لم تُرحّل: {{reason}}',
    invoiceDateNeedsNumber: 'أضف رقم الفاتورة.',
    linesRequired: 'أضف منتجاً.',
    quantityInvalid: 'الكمية أكبر من صفر.',
    rateInvalid: 'سعر غير صالح.',
    discountInvalid: 'خصم غير صالح.',
    taxInvalid: 'الضريبة 0–100٪.',
    kind: 'النوع',
    invoice: 'مشتريات',
    return: 'مرتجع',
    date: 'التاريخ',
    supplier: 'المورد',
    warehouse: 'المستودع',
    supplierInvoice: 'فاتورة المورد',
    supplierInvoiceDate: 'تاريخها',
    lines: 'السطور',
    taxable: 'الخاضع للضريبة',
    tax: 'ضريبة المدخلات',
    total: 'الإجمالي',
    status: 'الحالة',
    draft: 'مسودة',
    posted: 'مرحّلة',
    cancelled: 'ملغاة',
    allKinds: 'كل الأنواع',
    allStatuses: 'كل الحالات',
    from: 'من',
    to: 'إلى',
    search: 'بحث',
    searchHint: 'أي من الرقمين',
    none: 'لا مستندات.',
    new: 'مستند جديد',
    newInvoice: 'مشتريات جديدة',
    newReturn: 'مرتجع جديد',
    open: 'فتح',
    document: 'المستند',
    close: 'إغلاق',
    saveDraft: 'حفظ كمسودة',
    post: 'ترحيل',
    cancelDocument: 'إلغاء المستند',
    cancelReason: 'السبب',
    product: 'الصنف',
    quantity: 'الكمية',
    rate: 'التكلفة',
    discount: 'الخصم',
    taxPercent: 'نسبة الضريبة',
    net: 'الصافي',
    addLine: 'إضافة سطر',
    removeLine: 'حذف',
    chooseSupplier: '— اختر مورداً —',
    chooseWarehouse: '— اختر مستودعاً —',
    chooseProduct: '— اختر صنفاً —',
    againstInvoice: 'مقابل المشتريات',
    noInvoice: '— بدون —',
    currency: 'العملة',
    batch: 'الدفعة',
    batchHint: 'من العبوة',
    batchExisting: 'الدفعة',
    batchSellingRate: 'سعر بيع الدفعة',
    batchSellingRateHint: 'فارغ = سعر المنتج',
    expiresOn: 'تنتهي في',
    serialsNamed: 'سُمّي {{named}}/{{wanted}}',
    serialsHint: 'رقم واحد في كل سطر',
    savedNotice: 'حُفظ {{number}} (مسودة).',
    postedNotice: 'رُحّل {{number}}: {{total}}.',
    cancelledNotice: 'تم الإلغاء.',
    edit: 'تعديل',
    editDocument: 'تعديل {{number}}',
    editPostedHint: 'مُرحّل: الحفظ يعيد ترحيله بالرقم نفسه.',
    editReason: 'السبب',
    updatedNotice: 'تم تحديث {{number}}.',
    postedProduced:
      'أنتج الترحيل مستند استلام وقيداً — وفاتورة مستحقة للمورد في حالة الشراء.',
    supplierInvoiceHint:
      'ضريبة المدخلات تُسترد مقابل فاتورة المورد الضريبية، فرقمها وتاريخها هما ما يرد في الإقرار. ولا يُقبل الرقم نفسه مرتين لنفس المورد.',
    returnHint:
      'تحديد مستند الشراء يسدد الرصيد مقابل فاتورته. وبدونه يبقى الرصيد على حساب المورد. وتخرج البضاعة بمتوسط تكلفتها في المخزون، وهو ليس دائماً السعر المتفق عليه.',
    totalsHint: 'تقدير مبدئي. يقوم الخادم بتقريب الإجمالي حسب العملة عند الحفظ.',
  },
  tax: {
    byDate: 'حسب التاريخ',
    byRate: 'حسب النسبة',
    salesTaxable: 'مبيعات خاضعة',
    purchaseTaxable: 'مشتريات خاضعة',
    total: 'الإجمالي',
    summary: 'الملخص',
    outputTax: 'ضريبة المخرجات',
    inputTax: 'ضريبة المدخلات',
    head: 'البند',
    rate: 'النسبة',
    tax: 'الضريبة',
    taxableValue: 'القيمة الخاضعة',
    taxableSupplies: 'التوريدات الخاضعة',
    zeroRated: 'التوريدات صفرية النسبة',
    taxablePurchases: 'المشتريات الخاضعة',
    zeroRatedPurchases: 'المشتريات صفرية النسبة',
    netPayable: 'الصافي المستحق',
    onLedger: 'في دفتر الأستاذ',
    difference: 'الفرق',
    inputOnLedger: 'مدخلات الحساب',
    inputDifference: 'فرق المدخلات',
    byJournal: 'مسجّل بقيد',
    document: 'المستند',
    voucher: 'القيد',
    account: 'الحساب',
    narration: 'البيان',
    registration: 'الرقم الضريبي',
    regimeVat: 'ضريبة القيمة المضافة الخليجية',
    regimeGst: 'ضريبة السلع والخدمات الهندية',
    regimeNone: 'بدون نظام ضريبي',
    reconciled: 'متطابق',
    notReconciled: 'غير متطابق — راجع',
    nothingCharged: 'لا ضريبة في هذه الفترة.',
    noSupplies: 'لا توريدات.',
    noInput: 'لا ضريبة مدخلات.',
    differenceHint:
      'عمود دفتر الأستاذ هو ما تحرك به حساب الضريبة فعلياً. وجود فرق يعني أن ضريبة وصلت إلى الدفاتر بطريق غير مستند مبيعات — قيد كُتب يدوياً — ولا يستطيع تحديد الصحيح منهما إلا شخص.',
    inputHint: 'أسطر القيود بلا قيمة خاضعة.',
  },
  grid: {
    search: 'بحث…',
    columns: 'الأعمدة',
    freeze: 'تثبيت الأول',
    unfreeze: 'إلغاء التثبيت',
    exportCsv: 'تصدير CSV',
    saveLayout: 'حفظ التخطيط',
    editLayout: 'تعديل التخطيط',
    resetLayout: 'إعادة تعيين',
    layoutSaved: 'تم حفظ التخطيط',
    layoutReset: 'تمت إعادة التعيين',
    sortBy: 'ترتيب حسب',
    sortNone: 'بدون ترتيب',
    sortDirection: 'عكس',
    moveLeft: 'تحريك للأمام',
    moveRight: 'تحريك للخلف',
    position: 'الترتيب',
    width: 'العرض',
    auto: 'تلقائي',
    actions: 'الإجراءات',
    noRows: 'لا يوجد ما يُعرض.',
    rowCount: '{{shown}} من {{total}}',
    pageOf: 'صفحة {{page}}/{{pages}} · {{total}}',
    previousPage: 'السابق',
    nextPage: 'التالي',
    columnsHint: 'الأعمدة',
    freezeHint: 'تثبيت العمود الأول',
    exportHint: 'تصدير CSV',
    saveLayoutHint: 'حفظ التخطيط',
    columnsLockedHint: 'الترتيب محفوظ. اضغط «تعديل التخطيط» لتغييره.',
    editLayoutHint: 'تعديل التخطيط',
    options: 'خيارات القائمة',
    resetLayoutHint: 'إعادة التخطيط',
    showingRange: '{{first}}–{{last}} من {{total}}',
    totalRows_one: 'سجل واحد',
    totalRows_other: '{{count}} سجل',
    ofUnfiltered: '(من {{total}})',
    pagination: 'ترقيم الصفحات',
    goToPage: 'الانتقال إلى صفحة {{page}}',
  },
  settings: {
    title: 'الإعدادات',
    subtitle: 'كيفية عمل هذا التثبيت',
    taxTitle: 'الضريبة',
    taxEnabled: 'تفعيل الضريبة',
    allowSaveDraft: 'عرض حفظ كمسودة في المبيعات والمشتريات',
    taxHint: 'النظام الضريبي يحدد بنود الضريبة على المستند والولايات المتاحة.',
    regime: 'النظام الضريبي',
    regimeVat: 'ضريبة القيمة المضافة الخليجية',
    regimeGst: 'ضريبة السلع والخدمات الهندية',
    regimeNone: 'بدون ضريبة',
    firmState: 'ولاية المنشأة',
    firmStateHint: 'تُقارن بولاية العميل لتحديد IGST مقابل CGST و SGST.',
    noState: 'غير محددة',
    taxRates: 'نسب الضريبة',
    taxRatesHint: 'مفصولة بفواصل. هذه هي النسب التي تظهر في قوائم الاختيار.',
    defaultTaxRate: 'نسبة الضريبة الافتراضية',
    translationTitle: 'الترجمة العربية',
    translationHint: 'تعبئة الاسم العربي من الإنجليزي. النتيجة قابلة للتعديل دائماً.',
    autoTranslate: 'ترجمة عربية تلقائية',
    autoTranslateHint:
      'يُرسل النص الإنجليزي إلى ترجمة جوجل أثناء الكتابة. معطّل حتى تقوم بتفعيله.',
    apiKey: 'مفتاح API للترجمة',
    apiKeyHint: 'اختياري. بدونه تُستخدم الواجهة العامة المحدودة من جوجل.',
    apiKeyOptional: 'اختياري',
    translateNow: 'ترجمة',
    translating: 'جارٍ الترجمة…',
    translateFailed: 'تعذّر الوصول إلى خدمة الترجمة. اكتب الاسم يدوياً.',
    documentsTitle: 'المستندات',
    documentsHint: 'ما يبدأ به المستند الجديد.',
    preferredWarehouse: 'المستودع الافتراضي',
    preferredWarehouseHint:
      'اتركه فارغاً لاستخدام المستودع الافتراضي المحدد في البيانات الأساسية.',
    warehouseFromMaster: 'افتراضي البيانات',
    masterDefault: 'افتراضي',
    defaultPaymentMode: 'طريقة الدفع الافتراضية',
    defaultPaymentModeHint:
      'اتركه فارغاً ليُسأل في كل مرة. الصندوق الذي يتعامل نقداً فقط لا يحتاج إلى تحديدها في كل سند.',
    productsTitle: 'يبدأ الصنف الجديد بـ',
    productsHint: 'ما يبدأ به المنتج الجديد، حتى لا يُسأل عن حقل إلزامي مرتين.',
    defaultCategory: 'الفئة الافتراضية',
    defaultCategoryHint: 'تبقى إلزامية — هذا يملؤها فقط.',
    defaultStockUnit: 'الوحدة الافتراضية',
    defaultStockUnitHint: 'تبقى إلزامية — هذا يملؤها فقط.',
    defaultBrand: 'العلامة الافتراضية',
    chargesTitle: 'الحسابات الإضافية',
    chargeEffect: 'الأثر',
    chargeDefault: 'المبلغ الافتراضي',
    chargeIsDefault: 'افتراضي',
    chargeTabs: {
      sales: 'المبيعات والمرتجعات',
      salesOrder: 'عروض الأسعار والأوامر',
      purchase: 'المشتريات والمرتجعات',
      purchaseOrder: 'أوامر الشراء',
    },
    chargesHint:
      'الشحن، التغليف، التوصيل، خصم ثابت — تُحدَّد مرة واحدة لكل نوع مستند وتُطبَّق على كل مستند جديد، فلا يُضاف السطر يدوياً في كل مرة.',
    chargesDocument: 'المستند',
    chargeAmount: 'المبلغ',
    chargeAmountBlank: 'يُسأل',
    chargesNoneSet: 'لا يوجد نوع مستند يبدأ برسوم بعد.',
    chargesSetOn: 'محدّدة على: {{kinds}}.',
    chargeDocuments: {
      sales: 'فاتورة مبيعات',
      salesReturn: 'مرتجع مبيعات',
      salesOrder: 'أمر بيع',
      purchase: 'فاتورة مشتريات',
      purchaseReturn: 'مرتجع مشتريات',
      purchaseOrder: 'أمر شراء',
    },
    askEachTime: 'اسأل في كل مرة',
    decimalsTitle: 'المنازل العشرية',
    decimalsHintFirm:
      'عدد المنازل العشرية لعرض المبالغ. يُضبط للشركة لأن هذا الدخول غير مرتبط بفرع.',
    decimalsHintBranch:
      'عدد المنازل العشرية لعرض المبالغ في هذا الفرع. لكل فرع إعداده، والفرع الذي لم يختر يتبع الشركة.',
    decimals: 'المنازل',
    decimalsFieldHint: 'للعرض فقط: يحفظ الخادم ويرحّل الدقة الكاملة كما استلمها.',
    decimalsFollowFirm: 'افتراضي المنشأة ({{count}})',
    listsTitle: 'القوائم',
    listsHint: 'عدد السجلات المعروضة دفعة واحدة.',
    pageSize: 'عدد السجلات في الصفحة',
    masterDefaultsTitle: 'افتراضيات البيانات',
    masterDefaultsHint: 'تهيئة الخيارات الأولية لجميع القوائم الرئيسية وميزات المبيعات.',
    defaultCustomer: 'العميل',
    defaultSupplier: 'المورد',
    defaultSalesman: 'مندوب المبيعات',
    defaultBillingman: 'مسؤول الفوترة',
    defaultSalesRateType: 'سعر البيع',
    defaultTaxMode: 'الوضع الضريبي',
    quantityDecimals: 'كسور الكمية',
    quantityDecimalsHint: 'عدد المنازل العشرية لكميات الأصناف.',
    enableDeviceAttributes: 'خصائص الجهاز',
    enableDeviceAttributesHint:
      'عرض سمات الجهاز (الجهاز، اللون، البطارية، الرام، السعة) في تفاصيل المنتج.',
    enableReverseCalculation: 'الحساب العكسي',
    enableReverseCalculationHint:
      'السماح بإدخال الأسعار شاملة الضريبة مع الحساب العكسي التلقائي.',
    enableFreeQuantity: 'تفعيل الكمية المجانية',
    enableFreeQuantityHint: 'عرض عمود الكمية المجانية في سطور المبيعات.',
    enableItemDiscount: 'تفعيل خصم الصنف',
    enableItemDiscountHint: 'عرض عمود الخصم في سطور المبيعات.',
    enableAutoBatch: 'تفعيل التعيين التلقائي للدفعات',
    enableAutoBatchHint: 'اختيار دفعات الوارد أولاً صادر أولاً تلقائياً لسطور المبيعات.',
    enableGodowns: 'المستودعات',
    enableGodownsHint:
      'اختيار المستودع في المستندات والمخزون والتقارير. عند الإيقاف يُستخدم المستودع الافتراضي ويُخفى الحقل.',
    directSave: 'الحفظ المباشر',
    directSaveHint:
      'حفظ كل معاملة نهائية بخطوة واحدة: تُؤكَّد الطلبات وتُرحَّل مستندات المخزون عند حفظها، دون مسودة.',
    enableSerialNumbers: 'رقم تسلسلي / IMEI',
    enableSerialNumbersHint:
      'عرض الأرقام التسلسلية للمنتجات المتتبَّعة بها. اختيارية دائماً: يمكن أن يذكر السطر كل وحداته أو بعضها أو لا شيء منها.',
    additionalLedgerDefaults: 'الرسوم الافتراضية',
    additionalLedgerDefaultsHint: 'إعداد الحسابات الإضافية الافتراضية لكل نوع حركة',
    addDefaultLedger: 'إضافة حساب افتراضي',
    customerInvoiceTemplate: 'قالب الطباعة',
    formatA4: 'A4',
    formatA5: 'A5',
    formatThermal: 'حراري 80 مم',
    pageSizeHint: 'بين 5 و 500.',
    reset: 'إعادة تعيين جميع الإعدادات',
    storedLocally: 'تُستخدم نسخة هذا المتصفح.',
    storedForFirm:
      'مشتركة بين جميع أجهزة هذه المنشأة: أي تغيير هنا يصل إلى كل نقطة بيع. يبقى عدد الصفوف في الصفحة خاصاً بهذا المتصفح.',
    storedReadOnly: 'للمسؤول فقط. التغييرات في هذا المتصفح.',
  },
  masterPicker: {
    add: {
      category: 'فئة جديدة',
      brand: 'علامة تجارية جديدة',
      unit: 'وحدة جديدة',
      taxRate: 'نسبة ضريبة جديدة',
      customer: 'عميل جديد',
      supplier: 'مورد جديد',
      employee: 'موظف جديد',
      ledger: 'حساب جديد',
      warehouse: 'مستودع جديد',
    },
    code: 'الرمز',
    codeAutomatic: 'تلقائي',
    name: 'الاسم',
    nameArabic: 'الاسم (عربي)',
    mobile: 'الجوال',
    convertsTo: 'تتحول إلى {{unit}}',
    factor: 'كم {{unit}} في الواحدة',
  },
  taxRates: {
    title: 'نسب الضريبة',
    new: 'نسبة ضريبة جديدة',
    edit: 'تعديل نسبة الضريبة',
    percentage: 'النسبة',
    default: 'افتراضية',
    makeDefault: 'جعلها افتراضية',
    isDefault: 'النسبة الافتراضية',
    namePlaceholder: 'مثال: ضريبة القيمة المضافة 5%',
    nameRequired: 'أدخل اسماً للنسبة.',
    percentageRequired: 'أدخل النسبة.',
    percentageInvalid: '0–100 فقط.',
    duplicate: 'مستخدمة في {{name}}.',
    none: 'بدون ضريبة',
  },
  photos: {
    title: 'الصور',
    photo: 'صورة {{number}}',
    add: 'إضافة',
    change: 'تغيير',
    remove: 'إزالة',
    invalid: 'هذا الملف ليس صورة.',
  },
  employees: {
    title: 'الموظفون',
    new: 'موظف جديد',
    edit: 'تعديل موظف',
    none: 'لا موظفين.',
  },
  ledgerList: {
    title: 'قائمة الحسابات',
    none: 'لا توجد حسابات.',
    allKinds: 'كل الأنواع',
  },
  voucherList: {
    receipts: 'سندات القبض',
    payments: 'سندات الصرف',
    journals: 'قيود اليومية',
    newReceipt: 'سند قبض جديد',
    newPayment: 'سند صرف جديد',
    newJournal: 'قيد جديد',
    receivedFrom: 'مستلم من',
    receivedInto: 'مستلم في',
    paidTo: 'مدفوع إلى',
    paidFrom: 'مدفوع من',
    debited: 'مدين',
    credited: 'دائن',
    noReceipts: 'لا سندات قبض في هذه الفترة.',
    noPayments: 'لا سندات صرف في هذه الفترة.',
    noJournals: 'لا قيود في هذه الفترة.',
    savedNotice: 'تم حفظ {{number}}.',
  },
  ledgerMaster: {
    newLedger: 'حساب جديد',
    editLedger: 'تعديل الحساب',
    name: 'اسم الحساب',
    code: 'الرمز',
    codeHint: 'اتركه فارغاً ليُنشأ من الاسم.',
    nameArabic: 'الاسم بالعربية',
    kind: 'النوع',
    group: 'مجموعة الحساب',
    openingBalance: 'الرصيد الافتتاحي',
    openingHint: 'الرصيد عند بدء استخدام النظام.',
    openingSide: 'جانب الافتتاحي',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    address: 'العنوان',
    bankDetails: 'بيانات البنك',
    withdraw: 'تعطيل',
    restore: 'إعادة الاستخدام',
    editedElsewhere: 'عدّل «{{name}}» من شاشته.',
    renameGroup: 'إعادة تسمية المجموعة',
    newGroup: 'مجموعة جديدة',
    groupName: 'اسم المجموعة',
    parentGroup: 'تحت',
    parentHint: 'تأخذ المجموعة الجديدة طبيعة هذه المجموعة: أصول، خصوم، إيرادات…',
    statement: 'كشف الحساب',
    groups: 'المجموعات',
    view: 'العرض',
    ledgerCount: 'الحسابات',
    rename: 'إعادة تسمية',
  },
  statement: {
    title: 'كشف حساب',
    voucher: 'السند',
    type: 'النوع',
    particulars: 'البيان',
    reference: 'المرجع',
    balance: 'الرصيد',
    chooseLedger: 'اختر حساباً…',
    pickLedger: 'اختر حساباً.',
    ledger: 'الحساب',
    movement: 'الحركة',
    noPostings: 'لا قيود.',
  },
  outstanding: {
    debtorsTitle: 'أرصدة العملاء المستحقة',
    creditorsTitle: 'أرصدة الموردين المستحقة',
    overdueOnly: 'المتأخرة فقط',
    view: 'العرض',
    views: {
      bills: 'حسب الفاتورة',
      aging: 'حسب العمر',
    },
    nothing: {
      debtors: 'لا مستحقات.',
      creditors: 'لا مستحقات.',
    },
    nothingAged: 'لا شيء مستحق.',
    billWiseHint:
      'مبني على الفواتير المفتوحة، لذا يظهر الطرف هنا عندما يكون حسابه حسب الفاتورة. رصيد أي طرف آخر في كشف حسابه.',
    parties: 'الأطراف',
    totalOutstanding: 'المستحق',
    totalOverdue: 'المتأخر',
    mixedCurrencies: 'الإجماليات بعملات متعددة.',
    billCount_one: 'فاتورة واحدة',
    billCount_other: '{{count}} فواتير',
    overdueAmount: '{{amount}} متأخر',
    bill: 'الفاتورة',
    due: 'الاستحقاق',
    original: 'المبلغ',
    settled: 'المسدد',
    outstanding: 'المستحق',
    daysOverdue: 'أيام التأخير',
    range: '{{from}}–{{to}} يوم',
    over: 'أكثر من {{days}} يوم',
    party: 'الطرف',
    notDue: 'غير مستحق بعد',
    total: 'الإجمالي',
  },
  profile: {
    anonymous: 'مستخدم',
    user: 'مستخدم',
    title: 'ملفي الشخصي',
    subtitle: 'بياناتك وتسجيل الدخول والتفضيلات',
    active: 'جلسة نشطة',
    passwordManaged: 'كلمة المرور مُدارة',
    myProfile: 'ملفي الشخصي',
    tabs: {
      details: 'البيانات',
      security: 'الأمان',
      preferences: 'التفضيلات',
      access: 'الأدوار والصلاحيات',
    },
    noRoles: 'لا أدوار',
    lastSignIn: 'آخر تسجيل دخول',
    passwordChanged: 'تغيير كلمة المرور',
    never: 'أبداً',
    signedInOn: 'مسجّل الدخول على',
    deviceCount_one: 'جهاز واحد',
    deviceCount_other: '{{count}} أجهزة',
    personalDetails: 'البيانات الشخصية',
    personalHint: 'يظهر اسمك على الشاشات وعلى المستندات التي تدخلها.',
    detailsSaved: 'تم الحفظ.',
    displayName: 'الاسم',
    displayNameHint: 'يظهر في الشريط العلوي وعلى المستندات وفي التقارير.',
    email: 'البريد الإلكتروني',
    mobile: 'الجوال',
    signInDetails: 'تسجيل الدخول',
    userName: 'اسم المستخدم',
    company: 'الشركة',
    memberSince: 'عضو منذ',
    userNameFixed:
      'لا يمكن تغيير اسم المستخدم. يمكن للمسؤول إعادة تعيين كلمة المرور من الإعدادات › المستخدمون.',
    samePassword: 'يجب أن تختلف عن الحالية.',
    passwordHint: 'استخدم 12 حرفاً على الأقل. العبارة الطويلة أسهل للتذكر وأصعب للتخمين.',
    passwordChangedSignIn: 'تم تغيير كلمة المرور. سجّل الدخول مجدداً.',
    passwordSignsOut: 'يُخرجك من كل الأجهزة.',
    showPasswords: 'إظهار كلمات المرور',
    strength: {
      1: 'ضعيفة',
      2: 'مقبولة',
      3: 'جيدة',
      4: 'قوية',
    },
    whereSignedIn: 'الجلسات',
    whereSignedInHint: 'سجّل الخروج من جهاز لم تعد تستخدمه أو لا تعرفه.',
    on: ' على ',
    unknownDevice: 'جهاز غير معروف',
    thisDevice: 'هذا الجهاز',
    sessionTimes: 'دخول {{signedIn}} · نشط {{active}}',
    noSessions: 'لا توجد جلسات أخرى.',
    sessionEnded: 'تم تسجيل الخروج.',
    othersEnded_one: 'تم إخراج {{count}} جهاز.',
    othersEnded_other: 'تم إخراج {{count}} جهاز.',
    signOutOthers_one: 'إخراج الأجهزة الأخرى',
    signOutOthers_other: 'إخراج الأجهزة الأخرى',
    light: 'فاتح',
    dark: 'داكن',
    themeHint: 'مظهر التطبيق على هذا الجهاز.',
    languageHint: 'العربية تجعل الشاشة كلها من اليمين إلى اليسار.',
    yourRoles: 'أدوارك',
    rolesHint: 'يمنح المسؤول الأدوار من الإعدادات › المستخدمون.',
    fullAccess: 'وصول كامل',
    whatYouCanDo: 'ما يمكنك فعله',
    everything: 'صلاحية كاملة.',
    nothingGranted: 'لا صلاحيات.',
  },
  changePassword: {
    title: 'تغيير كلمة المرور',
    subtitle: 'اختر كلمة مرور قوية وفريدة',
    current: 'كلمة المرور الحالية',
    new: 'كلمة المرور الجديدة',
    confirm: 'تأكيد كلمة المرور الجديدة',
    policy: '12 حرفاً على الأقل',
    mismatch: 'كلمتا المرور مختلفتان.',
    unreachable: 'تعذر الوصول للخادم.',
    submit: 'تحديث كلمة المرور',
    working: 'جارٍ التحديث…',
    success: 'تم تحديث كلمة المرور.',
  },
  users: {
    title: 'المستخدمون',
    hint: 'من يمكنه تسجيل الدخول وبأي أدوار. يجب على المستخدم الجديد تغيير كلمة المرور الأولى عند أول دخول. صلاحيات الدور وقوائمه تُضبط في شاشة الأدوار والقوائم.',
    new: 'مستخدم جديد',
    editTitle: 'تعديل {{name}}',
    userName: 'اسم المستخدم',
    displayName: 'الاسم',
    email: 'البريد الإلكتروني',
    mobile: 'الجوال',
    password: 'كلمة المرور الأولى',
    newPassword: 'كلمة مرور أولى جديدة',
    passwordHint: '12 حرفاً على الأقل',
    roles: 'الأدوار',
    status: 'الحالة',
    active: 'مسموح بالدخول',
    lastLogin: 'آخر دخول',
    mustChange: 'يجب تغيير كلمة المرور',
    lockedOut: 'مقفل مؤقتاً',
    resetPassword: 'إعادة تعيين كلمة المرور',
    resetTitle: 'إعادة تعيين: {{name}}',
    createdNotice: 'تم إنشاء {{name}}.',
    updatedNotice: 'تم حفظ {{name}}.',
    resetNotice: 'أُعيد تعيين كلمة مرور {{name}}.',
  },
  roles: {
    title: 'الأدوار والقوائم',
    hint: 'يحدد الدور القوائم التي يراها مستخدموه وما يمكنهم فعله. المستخدم ذو الأدوار المتعددة يحصل على كل ما تمنحه.',
    new: 'دور جديد',
    choose: 'اختر دوراً أو أنشئ واحداً.',
    userCount_one: 'مستخدم واحد',
    userCount_other: '{{count}} مستخدمين',
    everything: 'كل الصلاحيات',
    everythingHint: 'صلاحية كاملة؛ يُعدّل الاسم فقط.',
    name: 'اسم الدور',
    description: 'الوصف',
    menus: 'القوائم',
    allMenus: 'كل القوائم المسموحة',
    menusHint:
      'اختر الشاشات التي يراها هذا الدور في القائمة. اختيار شاشة يسمح أيضاً بفتحها.',
    permissions: 'الصلاحيات',
    permissionsHint:
      'العرض للقراءة؛ الإنشاء والتعديل والحذف للإدخال والتصحيح والإلغاء؛ والاعتماد والطباعة والتصدير كما تسمى.',
    resource: 'المجال',
    delete: 'حذف الدور',
    savedNotice: 'تم حفظ الدور {{name}}.',
    deletedNotice: 'تم حذف الدور {{name}}.',
    verbs: {
      view: 'عرض',
      create: 'إنشاء',
      edit: 'تعديل',
      delete: 'حذف',
      approve: 'اعتماد',
      print: 'طباعة',
      export: 'تصدير',
    },
    modules: {
      platform: 'النظام',
      accounting: 'المحاسبة',
      inventory: 'المخزون',
      sales: 'المبيعات',
      purchase: 'المشتريات',
      manufacturing: 'التصنيع',
      service: 'الخدمة',
      reporting: 'التقارير ولوحات المعلومات',
      printing: 'الطباعة',
      workflow: 'سير العمل',
    },
  },
  dataReset: {
    title: 'مسح البيانات',
    warning: 'لا يمكن التراجع. انسخ احتياطياً أولاً.',
    transactionsTitle: 'مسح جميع المعاملات',
    transactionsBody: 'يحذف كل المعاملات ويعيد الترقيم.',
    everythingTitle: 'مسح كل شيء',
    everythingBody: 'يحذف أيضاً العملاء والموردين والمنتجات وباقي البيانات الأساسية.',
    clearTransactions: 'مسح المعاملات',
    clearEverything: 'مسح كل شيء',
    confirmHint: 'اكتب {{name}} للتأكيد.',
    confirmLabel: 'اسم المنشأة',
    confirm: 'امسح الآن',
    clearing: 'جارٍ المسح…',
    transactionsDone: 'تم مسح المعاملات.',
    everythingDone: 'تم مسح كل شيء.',
    nothingRemoved: 'لا شيء للحذف.',
    kinds: {
      salesInvoices: 'المبيعات والمرتجعات',
      purchaseInvoices: 'المشتريات ومرتجعاتها',
      orders: 'الأوامر',
      stockDocuments: 'مستندات المخزون',
      vouchers: 'السندات',
      cheques: 'الشيكات',
      products: 'الأصناف',
      parties: 'الأطراف والحسابات',
      warehouses: 'المستودعات',
      categories: 'الفئات',
      units: 'الوحدات',
    },
  },
  importData: {
    title: 'استيراد البيانات',
    subtitle: 'من EasyRetail',
    intro:
      'ينقل بيانات المنشأة من EasyRetail: مجموعات الحسابات والحسابات، والعملاء والموردين والموظفين، والفئات والعلامات التجارية والوحدات والمستودعات، والمنتجات مع باركوداتها، ورصيد كل حساب في تاريخ محدد، والمخزون المتوفر كمخزون افتتاحي. لا تُنقل الفواتير والقيود السابقة؛ فالأرصدة تحمل أثرها.',
    again:
      'إعادة الاستيراد آمنة. ما نُقل من قبل يُتعرَّف عليه ويُترك كما هو، فالاستيراد الذي توقف في منتصفه يكتمل بتشغيله مرة أخرى.',
    sourceLabel: 'المصدر',
    sourceFile: 'ملف التصدير',
    sourceDatabase: 'قاعدة البيانات',
    fileStep1: 'نزّل برنامج التصدير.',
    fileStep2: 'شغّله على جهاز EasyRetail.',
    exporterWarning: 'إذا ظهر تحذير Windows: مزيد من المعلومات › التشغيل على أي حال.',
    fileStep3: 'في نافذته، اتصل واختر قاعدة البيانات ثم اضغط Export.',
    fileStep4: 'اختر easyretail-export.json من سطح المكتب.',
    downloadExporter: 'تنزيل برنامج التصدير (.exe)',
    downloading: 'جارٍ التنزيل…',
    chooseFile: 'اختيار ملف التصدير',
    reading: 'جارٍ القراءة…',
    notAnExport: 'ليس ملف تصدير EasyRetail.',
    unreadableFile: 'ملف غير مقروء.',
    databaseHint:
      'عندما يستطيع هذا الخادم الوصول إلى SQL Server الخاص بالمحل، على الشبكة نفسها أو عبر VPN. وإلا فاستخدم ملف تصدير.',
    server: 'SQL Server',
    database: 'قاعدة البيانات',
    userName: 'المستخدم',
    password: 'كلمة المرور',
    asAt: 'الأرصدة في تاريخ',
    userHint: 'مستخدم فارغ = دخول Windows',
    readDatabase: 'قراءة قاعدة البيانات',
    asAtLine: 'الأرصدة في {{date}} · صُدّرت في {{exported}}',
    changeSource: 'استخدام مصدر آخر',
    mastersTitle: 'البيانات الأساسية',
    counts: {
      groups: 'مجموعات الحسابات',
      ledgers: 'الحسابات',
      customers: 'العملاء',
      suppliers: 'الموردون',
      employees: 'الموظفون',
      categories: 'الفئات',
      subClasses: 'الفئات الفرعية',
      brands: 'العلامات التجارية',
      products: 'المنتجات',
      barcodes: 'الباركودات',
    },
    balancesTitle: 'الأرصدة',
    debit: 'مدين',
    credit: 'دائن',
    difference: 'الفرق',
    differenceNote: 'الفرق {{amount}} في حساب فرق الافتتاح.',
    stockTitle: 'المخزون المتوفر',
    stockLines: 'السطور',
    stockValue: 'القيمة بالتكلفة',
    negativeStock: 'صفر أو أقل: مُتجاوز',
    optionsTitle: 'ما يُستورد',
    optionMasters: 'البيانات الأساسية (دائماً)',
    optionProducts: 'المنتجات والباركودات',
    optionBalances: 'الأرصدة في {{date}}',
    optionCarry: 'ترحيل الأرباح لحقوق الملكية',
    optionStock: 'المخزون في {{date}}',
    optionsHint:
      'تحل الأرصدة محل الرصيد الافتتاحي لكل حساب مستورد. السجلات الموجودة في هذه المنشأة بالرمز نفسه تُطابَق ولا تُكرَّر.',
    start: 'استيراد الآن',
    runAgain: 'تشغيل مرة أخرى',
    stop: 'إيقاف',
    stopping: 'جارٍ الإيقاف…',
    progressTitle: 'التقدم',
    finished: 'اكتمل الاستيراد.',
    failed: 'فشل الاستيراد. أصلح وأعد التشغيل.',
    stopped: 'توقف. أعد التشغيل للمتابعة.',
    openingSet: 'الأرصدة الافتتاحية المسجلة',
    profitCarried: 'الربح لحقوق الملكية',
    differenceCarried: 'فرق الأرصدة الافتتاحية',
    check: 'تحقق:',
    links: {
      trialBalance: 'ميزان المراجعة',
      ledgers: 'دليل الحسابات',
      customers: 'العملاء',
      suppliers: 'الموردون',
      products: 'المنتجات',
      valuation: 'تقييم المخزون',
    },
    steps: {
      masters: 'البيانات الأساسية',
      products: 'المنتجات',
      balances: 'الأرصدة',
      stock: 'المخزون الافتتاحي',
    },
    status: {
      waiting: 'في الانتظار',
      running: 'قيد التنفيذ',
      done: 'تم',
      failed: 'فشل',
      stopped: 'متوقف',
      off: 'غير محدد',
    },
    batch: '{{done}} من {{total}}',
    tally: {
      created: 'أُنشئ {{value}}',
      balancesSet: 'أرصدة {{value}}',
      matched: 'مطابق {{value}}',
      alreadyImported: 'موجود {{value}}',
      skipped: 'مستبعد {{value}}',
      nothing: 'لا شيء للتنفيذ',
    },
    notes_zero: '{{count}} ملاحظة',
    notes_one: 'ملاحظة واحدة',
    notes_two: 'ملاحظتان',
    notes_few: '{{count}} ملاحظات',
    notes_many: '{{count}} ملاحظة',
    notes_other: '{{count}} ملاحظة',
  },
  printing: {
    print: 'طباعة',
    template: 'القالب',
    copies: 'النسخ',
    alsoPrint: 'وأيضاً {{copies}} على {{template}}',
    choosePrinter: 'الطابعة: {{printer}}',
    settingsTitle: 'إعدادات الطباعة',
    settingsHint:
      'طريقة طباعة كل مستند، منفصلة للضريبي وغير الضريبي: معاينة أولاً أو طباعة مباشرة، والقالب، والطابعة المقصودة، وعدد النسخ، وطباعة ثانية اختيارية على طابعة وقالب آخرين.',
    printerHint:
      'يختار المتصفح الطابعة الفعلية في نافذة الطباعة ولا يمكنه اختيارها بنفسه. يظهر اسم الطابعة المحدد هنا عند الطباعة لاختيار الصحيحة؛ وجعلها الطابعة الافتراضية للجهاز يوفر هذه الخطوة.',
    savedNotice: 'تم الحفظ.',
    document: 'المستند',
    preview: 'معاينة',
    printer: 'الطابعة',
    printerPlaceholder: 'مثال: طابعة الكاشير',
    secondPrint: 'طباعة ثانية',
    copyLabels: 'وسم النسخ',
    bilingual: 'عربي + إنجليزي',
    bilingualHint:
      'يُطبع كل سطر من المستند مرتين: العربية في سطر والإنجليزية في السطر التالي - الفواتير وعروض الأسعار والمشتريات والسندات وكل مطبوع آخر. يمكن تغييره لأي طباعة.',
    bilingualShort: 'عربي + إنجليزي',
    movedHint: 'تُضبط الطباعة الآن لكل مستند ونوع ضريبة مع قوالب مصممة:',
    followSettings: 'حسب إعدادات الطباعة',
    templatesTitle: 'قوالب الطباعة',
    templatesHint:
      'صمم قوالب طباعة المستندات: بيانات الشركة والشعار، والأجزاء الظاهرة، والأعمدة، والشروط والتواقيع، والورق والنمط. المعاينة هي ما يُطبع بالضبط.',
    templatesSaved: 'تم حفظ القوالب.',
    newTemplate: 'قالب جديد',
    duplicate: 'نسخ',
    shareCompany: 'تطبيق على كل القوالب',
    companyShared: 'تم النسخ. احفظ للإبقاء.',
    deleteTemplate: 'حذف',
    copyOf: 'نسخة من {{name}}',
    previewAs: 'معاينة كـ',
    general: 'عام',
    templateName: 'اسم القالب',
    paper: 'الورق',
    titleOverride: 'العنوان',
    company: 'الشركة',
    companyName: 'اسم الشركة',
    companyNameArabic: 'اسم الشركة بالعربية',
    companyDetails: 'العنوان والتواصل',
    taxNumber: 'الرقم الضريبي',
    logo: 'الشعار',
    logoUnreadable: 'صورة غير صالحة.',
    removeLogo: 'إزالة',
    showSection: 'إظهار',
    columnsSection: 'أعمدة الأصناف',
    footerSection: 'التذييل',
    termsLabel: 'الشروط والأحكام',
    note: 'ملاحظة التذييل',
    signatureLeft: 'سطر التوقيع الأيسر',
    signatureRight: 'سطر التوقيع الأيمن',
    styleSection: 'النمط',
    fontSize: 'حجم الخط',
    accent: 'اللون',
    borders: 'الخطوط والحدود',
    number: 'رقم',
    date: 'التاريخ',
    reference: 'المرجع',
    warehouse: 'المستودع',
    item: 'الصنف',
    quantity: 'الكمية',
    unit: 'الوحدة',
    rate: 'السعر',
    discount: 'الخصم',
    taxPercent: 'الضريبة %',
    tax: 'الضريبة',
    amount: 'المبلغ',
    serials: 'الرقم التسلسلي / IMEI',
    account: 'الحساب',
    debit: 'مدين',
    credit: 'دائن',
    inWords: 'كتابةً',
    narration: 'ملاحظة',
    total: 'الإجمالي',
    terms: 'الشروط والأحكام',
    taxable: 'القيمة الخاضعة',
    taxTotal: 'الضريبة',
    charges: 'المصاريف',
    roundOff: 'التقريب',
    documents: {
      salesInvoice: 'فاتورة مبيعات',
      salesReturn: 'مرتجع مبيعات',
      salesQuotation: 'عرض سعر',
      salesOrder: 'أمر بيع',
      purchaseInvoice: 'مشتريات',
      purchaseReturn: 'مرتجع مشتريات',
      receipt: 'سند قبض',
      payment: 'سند صرف',
      journal: 'قيد يومية',
    },
    modes: { tax: 'ضريبي', nonTax: 'غير ضريبي' },
    papers: { A4: 'A4', A5: 'A5', '80mm': 'لفافة 80 مم', '58mm': 'لفافة 58 مم' },
    fontScales: { small: 'صغير', normal: 'عادي', large: 'كبير' },
    show: {
      arabicTitle: 'العنوان بالعربية',
      partyAddress: 'عنوان الطرف',
      partyTaxNumber: 'الرقم الضريبي للطرف',
      warehouse: 'المستودع',
      reference: 'المرجع',
      narration: 'البيان',
      productCode: 'رمز الصنف',
      serials: 'الأرقام التسلسلية / IMEI',
      amountInWords: 'المبلغ كتابةً',
      signatures: 'سطور التوقيع',
    },
    columns: {
      unit: 'الوحدة',
      rate: 'السعر',
      discount: 'الخصم',
      taxPercent: 'الضريبة %',
      tax: 'مبلغ الضريبة',
    },
    titles: {
      salesInvoice: { tax: 'فاتورة ضريبية', nonTax: 'فاتورة' },
      salesReturn: { tax: 'إشعار دائن ضريبي', nonTax: 'إشعار دائن' },
      salesQuotation: { tax: 'عرض سعر', nonTax: 'عرض سعر' },
      salesOrder: { tax: 'أمر بيع', nonTax: 'أمر بيع' },
      purchaseInvoice: { tax: 'فاتورة مشتريات', nonTax: 'فاتورة مشتريات' },
      purchaseReturn: { tax: 'إشعار مدين', nonTax: 'إشعار مدين' },
      receipt: { tax: 'سند قبض', nonTax: 'سند قبض' },
      payment: { tax: 'سند صرف', nonTax: 'سند صرف' },
      journal: { tax: 'سند قيد', nonTax: 'سند قيد' },
    },
    titlesArabic: {
      salesInvoice: { tax: '', nonTax: '' },
      salesReturn: { tax: '', nonTax: '' },
      salesQuotation: { tax: '', nonTax: '' },
      salesOrder: { tax: '', nonTax: '' },
      purchaseInvoice: { tax: '', nonTax: '' },
      purchaseReturn: { tax: '', nonTax: '' },
      receipt: { tax: '', nonTax: '' },
      payment: { tax: '', nonTax: '' },
      journal: { tax: '', nonTax: '' },
    },
    party: {
      billTo: 'العميل',
      supplier: 'المورد',
      receivedFrom: 'استلمنا من',
      paidTo: 'دفعنا إلى',
    },
    watermark: { draft: 'مسودة', cancelled: 'ملغى' },
    copy: { '1': 'الأصل', '2': 'نسخة ثانية', '3': 'نسخة ثالثة', '4': 'نسخة' },
    sample: { narration: 'سُلمت إلى محل العميل.' },
  },
  common: {
    retry: 'أعد المحاولة',
    dismiss: 'إخفاء',
    yes: 'نعم',
    no: 'لا',
    loading: 'جارٍ التحميل…',
    theme: 'المظهر',
    language: 'اللغة',
    close: 'إغلاق',
    cancel: 'إلغاء',
    save: 'حفظ',
    saving: 'جارٍ الحفظ…',
    edit: 'تعديل',
    choose: 'ابحث أو اختر…',
    none: '— بدون —',
    noMatches: 'لا توجد نتائج مطابقة.',
    moreMatches: 'و{{count}} أخرى',
    required: 'مطلوب.',
    notANumber: 'أدخل رقماً.',
    mustChangePassword: 'غيّر كلمة المرور للمتابعة.',
    remove: 'إزالة',
    expandAll: 'توسيع الكل',
    collapseAll: 'طي الكل',
  },
} as const;

/**
 * What is shown when a key cannot be resolved.
 *
 * The default is the key itself, and a dotted path is a worse thing to show
 * somebody than almost anything else: the chart of accounts printed
 * `ledgerKinds.undefined` in a column for every charge account a firm has, and the
 * day book printed `reports.voucherCount` whenever the count it interpolates was
 * absent — both read as breakage to the person in front of them and as nothing at
 * all to the person reading a screenshot of it.
 *
 * So an unresolved key degrades to its last segment, spaced out and capitalised.
 * `ledgerKinds.AdditionalCharge` becomes "Additional charge", which is very nearly
 * right; `reports.voucherCount` becomes "Voucher count", which is at least a
 * phrase. Neither is a substitute for the key existing — the console still says so
 * in development — but neither is a bug report from a user either.
 */
function readableFallback(key: string): string {
  const last = key.split('.').pop() ?? key;

  if (last === 'undefined' || last === 'null' || last === '') {
    return '—';
  }

  const spaced = last
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

await i18next.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ar: { translation: ar },
  },
  lng: localStorage.getItem('erp.language') === 'ar' ? 'ar' : 'en',
  fallbackLng: 'en',
  parseMissingKeyHandler: readableFallback,
  // Says so where a developer will see it, and nowhere the user will.
  saveMissing: import.meta.env.DEV,
  missingKeyHandler: (_languages, _namespace, key) => {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] no translation for "${key}"`);
    }
  },
  interpolation: {
    // React escapes for us; letting i18next escape as well double-encodes any
    // apostrophe in a ledger name.
    escapeValue: false,
  },
});

export default i18next;
